
import { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { BACKEND_URL } from '../api/axios';
import Navbar from './Navbar';
import Footer from './Footer';
import SupportTicketsPanel from './SupportTicketsPanel';
import BusinessesPanel from './BusinessesPanel';
import EnterprisePanel from './EnterprisePanel';
import BusinessImportPanel from './BusinessImportPanel';
import LocationCategoryPanel from './LocationCategoryPanel';
import VerificationPanelList from './verification/VerificationPanelList';
import VerificationPanelDetail from './verification/VerificationPanelDetail';
   import PlansAdminPanel from './PlansAdminPanel';
import EnterpriseCardPanel from './EnterpriseCardPanel';
   import BillingAnalyticsPanel from './BillingAnalyticsPanel';
import AdminBillingReports from './AdminBillingReports';
import AdminSubscriptionsPanel from './AdminSubscriptionsPanel';
import LocationMasterPanel from './LocationMasterPanel';
import NotificationCampaignsPanel from './NotificationCampaignsPanel';
import ActionMenu from './ActionMenu';
import { hasWrongLocation } from './locationFix';
import NotificationReportPanel from './NotificationReportPanel';
import DeletionRequestsPanel from './DeletionRequestsPanel';
import ContactEnquiriesPanel from './ContactEnquiriesPanel';
import AdminEnterpriseInquiries from './AdminEnterpriseInquiries';


const MASTER_TYPES = [
  { value: 'skill', label: 'Skills' },
  { value: 'education', label: 'Education / Degree' },
  { value: 'institution', label: 'Institution' },
  { value: 'board_university', label: 'Board / University' },
  { value: 'job_title', label: 'Job Title' },
  { value: 'company', label: 'Company Name' },
  { value: 'certification', label: 'Certification Name' },
  { value: 'issued_by', label: 'Issued By' },
  { value: 'language', label: 'Language' },
];

// Job Type / Status option lists for the admin Jobs tab filters & edit form —
// mirrors AgentJobsList.jsx's JOB_TYPE_OPTIONS / STATUS_OPTIONS.
const ADMIN_JOB_TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'full_time', label: 'Full Time' },
  { value: 'part_time', label: 'Part Time' },
  { value: 'contract', label: 'Contract' },
  { value: 'internship', label: 'Internship' },
];

const ADMIN_JOB_STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'closed', label: 'Closed' },
  { value: 'draft', label: 'Draft' },
];

const ADMIN_STATUS_STYLES = {
  active: 'bg-green-50 text-green-600',
  closed: 'bg-gray-100 text-gray-500',
  paused: 'bg-amber-50 text-amber-600',
  draft: 'bg-blue-50 text-blue-600',
};


const JOB_METRIC_COLUMNS = [
  { key: 'openings', label: 'Openings' },
  { key: 'impressions', label: 'Impressions' },
  { key: 'clicks', label: 'Clicks' },
  { key: 'applicants', label: 'Applicants' },
  { key: 'screening', label: 'Screening' },
  { key: 'shortlisted', label: 'Shortlisted' },
  { key: 'interview_round_1', label: 'Interview R1' },
  { key: 'interview_round_2', label: 'Interview R2' },
  { key: 'hr_round', label: 'HR Round' },
  { key: 'offer', label: 'Job Offered' },
  { key: 'joined', label: 'Joined' },
   { key: 'rejected', label: 'Rejected' },
];

const jobMetricValue = (j, key) => {
  if (key === 'applicants') return j.applications_count ?? j.job_applications_count ?? 0;
  if (key === 'openings' || key === 'impressions' || key === 'clicks') return j[key] ?? 0;
  return j.funnel?.[key] ?? 0;
};

const fmtINR = (n) => (n ? '₹' + Number(n).toLocaleString('en-IN') : '—');
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN') : '—');

const waLink = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return null;
  return `https://wa.me/${digits.length === 10 ? '91' + digits : digits}`;
};

// Shared pill-button styles (same look as the "Active" status badge)
const PILL = 'inline-flex items-center justify-center whitespace-nowrap px-3.5 py-1 rounded-full text-xs font-bold transition disabled:opacity-50 disabled:cursor-not-allowed';
const PILL_PURPLE = `${PILL} bg-purple-50 text-[#7C3AED] hover:bg-purple-100`;
const PILL_GREEN = `${PILL} bg-[#ECFDF5] text-[#10B981] hover:bg-emerald-100`;
const PILL_RED = `${PILL} bg-[#FEF2F2] text-[#EF4444] hover:bg-red-100`;
const PILL_AMBER = `${PILL} bg-amber-50 text-amber-600 hover:bg-amber-100`;
const PILL_BLUE = `${PILL} bg-blue-50 text-blue-600 hover:bg-blue-100`;
const PILL_GRAY = `${PILL} bg-gray-100 text-gray-600 hover:bg-gray-200`;

const ACT = 'inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-full text-xs font-semibold border shadow-sm transition whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed';
const ACT_CALL = `${ACT} bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-emerald-100`;
const ACT_WA = `${ACT} bg-green-50 text-green-600 border-green-100 hover:bg-green-100`;
const ACT_VIEW = `${ACT} bg-purple-50 text-[#7C3AED] border-purple-100 hover:bg-purple-100`;
const ACT_EDIT = `${ACT} bg-blue-50 text-blue-600 border-blue-100 hover:bg-blue-100`;
const ACT_DELETE = `${ACT} bg-red-50 text-red-500 border-red-100 hover:bg-red-100`;
const ACT_AMBER = `${ACT} bg-amber-50 text-amber-600 border-amber-100 hover:bg-amber-100`;
const ACT_GRAY = `${ACT} bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100`;
const ACT_OFF = `${ACT} bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed`;

// Same button order everywhere: Call, WhatsApp, View, Edit, [extra], Delete
function RowActions({ phone, hideContact, onView, onEdit, onDelete, deleting, extra }) {
  return (
    <ActionMenu
      phone={phone}
       hideContact={hideContact}
      extra={extra}
      items={[
        onView && { label: 'View', icon: '👁', tone: 'purple', onClick: onView },
        onEdit && { label: 'Edit', icon: '✏️', tone: 'blue', onClick: onEdit },
        onDelete && {
          label: deleting ? 'Deleting…' : 'Delete',
          icon: '🗑',
          tone: 'red',
          danger: true,
          disabled: deleting,
          onClick: onDelete,
        },
      ]}
    />
  );
}

// Generic sorting helpers (used by all admin tables)
const compareSort = (av, bv, dir) => {
  const aEmpty = av === null || av === undefined || av === '';
  const bEmpty = bv === null || bv === undefined || bv === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;   // empty values always stay at the bottom
  if (bEmpty) return -1;
  const r =
    typeof av === 'number' && typeof bv === 'number'
      ? av - bv
      : String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
  return dir === 'asc' ? r : -r;
};

function SortTh({ label, sortKey, sort, onSort, className = '' }) {
  return (
    <th
      onClick={() => onSort(sortKey)}
      className={`text-left px-4 py-3 font-semibold cursor-pointer select-none hover:text-[#7C3AED] whitespace-nowrap ${className}`}
    >
      {label}
      {sort.key === sortKey ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ' ↕'}
    </th>
  );
}

function PaginationBar({ total, page, pageSize, onPage, onPageSize, className = '' }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const cur = Math.min(page, pages);
  const from = total === 0 ? 0 : (cur - 1) * pageSize + 1;
  const to = Math.min(cur * pageSize, total);
  const nums = [];
  for (let p = Math.max(1, cur - 2); p <= Math.min(pages, cur + 2); p++) nums.push(p);
  const btn = 'px-3 py-1.5 rounded-lg text-xs font-semibold border disabled:opacity-40';
  return (
    <div className={`flex items-center justify-between flex-wrap gap-3 text-sm text-gray-600 ${className}`}>
      <div className="grid grid-rows-2 grid-flow-col auto-cols-fr gap-2 w-max">
        <span>Showing {from}–{to} of {total}</span>
        <select
          value={pageSize}
          onChange={(e) => onPageSize(Number(e.target.value))}
          className="border rounded-lg px-2 py-1 text-xs"
        >
          {[10, 25, 50, 100].map((n) => (
            <option key={n} value={n}>{n} / page</option>
          ))}
        </select>
      </div>
      <div className="flex items-center gap-1">
        <button className={btn} disabled={cur === 1} onClick={() => onPage(1)}>«</button>
        <button className={btn} disabled={cur === 1} onClick={() => onPage(cur - 1)}>Prev</button>
        {nums.map((p) => (
          <button
            key={p}
            onClick={() => onPage(p)}
            className={`${btn} ${p === cur ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white border-[#7C3AED]' : 'bg-white'}`}
          >
            {p}
          </button>
        ))}
        <button className={btn} disabled={cur === pages} onClick={() => onPage(cur + 1)}>Next</button>
        <button className={btn} disabled={cur === pages} onClick={() => onPage(pages)}>»</button>
      </div>
    </div>
  );
}

const userStatus = (u) => u.status || 'active';
const USER_STATUS_STYLES = {
  active: 'bg-[#ECFDF5] text-[#10B981]',
  suspended: 'bg-amber-50 text-amber-600',
  banned: 'bg-[#FEF2F2] text-[#EF4444]',
};


const employerViewedLabel = (app) => {
  if (!app.employer_viewed_at) return null;
  const hours = (new Date(app.employer_viewed_at) - new Date(app.created_at)) / 36e5;
  if (hours <= 2) return 'Yes (within 2 hours)';
  if (hours <= 24) return 'Yes (within 24 hours)';
  return `Yes (within ${Math.ceil(hours / 24)} days)`;
};

const inDateRange = (value, from, to) => {
  if (!from && !to) return true;
  if (!value) return false;
  const d = new Date(value).setHours(0, 0, 0, 0);
  if (from && d < new Date(from).setHours(0, 0, 0, 0)) return false;
  if (to && d > new Date(to).setHours(23, 59, 59, 999)) return false;
  return true;
};

const isAgentAdded = (u) =>
  u.role === 'employer'
    ? u.created_by_agent_id != null
    : (u.created_by_agent_id != null || u.agent_id != null);

const isAgentPosted = (j) => j.posted_by_agent_id != null;
const isMergedAway = (u) => u.merged_into_id != null;


const COMPLETION_RANGES = {
  '1-25':   { label: '1% – 25%',   min: 1,  max: 25 },
  '26-50':  { label: '26% – 50%',  min: 26, max: 50 },
  '51-75':  { label: '51% – 75%',  min: 51, max: 75 },
  '76-100': { label: '76% – 100%', min: 76, max: 100 },
};
const userStateOf = (u) => (u.state || ((u.city || '').split(',')[1] || '')).trim();

const FILTER_INPUT_CLS =
  'w-full border border-purple-200 rounded-full px-3 py-1.5 text-xs text-gray-700 placeholder:text-gray-400 bg-white ' +
  'hover:border-purple-400 transition focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent';
const FILTER_LABEL_CLS = 'block text-[10px] font-semibold uppercase tracking-wide text-purple-600 mb-1 ml-1';
const FILTER_SELECT_CLS =
  'w-full appearance-none cursor-pointer border border-purple-200 rounded-full pl-3 pr-8 py-1.5 text-xs font-medium text-purple-700 ' +
  'bg-gradient-to-r from-purple-50 to-pink-50 hover:border-purple-400 transition ' +
  'focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent';

// Labels for CompanyDocument.document_type ('gst' | 'pan' | 'cin' | 'license')
const DOCUMENT_TYPE_LABELS = {
  gst: 'GST Certificate',
  pan: 'PAN Card',
  cin: 'CIN Certificate',
  license: 'Company License',
};


// Some aggregator sources (e.g. Arbeitnow) return descriptions as
// double-encoded HTML — the string literally contains "&lt;div&gt;"
// instead of real tags. Decode entities, then strip tags for clean
// plain-text display, preserving paragraph/line breaks.
function cleanDescription(html) {
  if (!html) return '';

  const decodeEntities = (str) => {
    const el = document.createElement('textarea');
    el.innerHTML = str;
    return el.value;
  };

  // Decode twice — handles both single- and double-encoded content.
  let decoded = decodeEntities(html);
  decoded = decodeEntities(decoded);

  // Turn block-level closing tags into line breaks before stripping,
  // so paragraphs/list items/table rows don't all run together.
  decoded = decoded.replace(/<\/(p|div|li|tr|h[1-6])>/gi, '\n');
  decoded = decoded.replace(/<br\s*\/?>/gi, '\n');
  decoded = decoded.replace(/<[^>]+>/g, '');

  // Collapse excessive blank lines left behind by stripped block tags.
  return decoded.replace(/\n{3,}/g, '\n\n').trim();
}

function formatAggSalary(min, max, currency) {
  if (!min && !max) return '—';
  const symbol = currency === 'INR' ? '₹' : (currency ? `${currency} ` : '');
  if (min && max) return `${symbol}${Number(min).toLocaleString('en-IN')} – ${symbol}${Number(max).toLocaleString('en-IN')}`;
  return `${symbol}${Number(min || max).toLocaleString('en-IN')}`;
}

// Searchable dropdown with checkboxes (multi-select)
function MultiSelectDropdown({ values, onChange, options, allLabel }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? options.filter((o) => o.toLowerCase().includes(s)) : options;
  }, [options, q]);

  const toggle = (o) =>
    onChange(values.includes(o) ? values.filter((v) => v !== o) : [...values, o]);

  const allFilteredSelected = filtered.length > 0 && filtered.every((o) => values.includes(o));

  const toggleAllFiltered = () => {
    if (allFilteredSelected) onChange(values.filter((v) => !filtered.includes(v)));
    else onChange(Array.from(new Set([...values, ...filtered])));
  };

  const label =
    values.length === 0 ? allLabel : values.length === 1 ? values[0] : `${values.length} selected`;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white text-left focus:outline-none focus:ring-2 focus:ring-purple-400"
      >
        <span className="truncate">{label}</span>
        <span className="text-gray-400 text-xs shrink-0">▼</span>
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full min-w-[260px] bg-white border border-purple-100 rounded-xl shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search..."
              className="w-full border border-purple-200 rounded-full px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
            />
          </div>

          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 text-xs font-semibold">
            <label className="flex items-center gap-2 cursor-pointer text-purple-700">
              <input
                type="checkbox"
                checked={allFilteredSelected}
                onChange={toggleAllFiltered}
                className="accent-purple-600"
              />
              Select all{q.trim() ? ' (filtered)' : ''}
            </label>
            {values.length > 0 && (
              <button type="button" onClick={() => onChange([])} className="text-red-500 hover:underline">
                Clear ({values.length})
              </button>
            )}
          </div>

          <div className="max-h-56 overflow-y-auto py-1">
            {filtered.map((o) => (
              <label
                key={o}
                className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-purple-50 ${
                  values.includes(o) ? 'bg-purple-50 text-purple-700 font-semibold' : 'text-gray-700'
                }`}
              >
                <input
                  type="checkbox"
                  checked={values.includes(o)}
                  onChange={() => toggle(o)}
                  className="accent-purple-600 shrink-0"
                />
                <span className="truncate">{o}</span>
              </label>
            ))}
            {filtered.length === 0 && <p className="px-3 py-2 text-xs text-gray-400">No matches</p>}
          </div>
        </div>
      )}
    </div>
  );
}

function MergeEmployersModal({ employers, onClose, onConfirm, loading }) {
  const [primaryId, setPrimaryId] = useState(employers[0].id);
  const primary = employers.find((e) => e.id === primaryId);
  const secondary = employers.find((e) => e.id !== primaryId);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center z-50 p-4" onClick={loading ? undefined : onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-purple-700 to-pink-600 px-6 py-5 flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-lg font-bold text-white">Merge Employer Accounts</h3>
            <p className="text-purple-100 text-xs mt-0.5">Select the account you want to keep as the main account</p>
          </div>
          <button onClick={onClose} disabled={loading} className="text-purple-100 hover:text-white text-2xl leading-none">×</button>
        </div>

        <div className="px-6 py-5 overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {employers.map((e) => {
              const isMain = e.id === primaryId;
              return (
                <label key={e.id} className={`cursor-pointer rounded-2xl border-2 p-4 transition ${isMain ? 'border-purple-500 bg-purple-50/50' : 'border-gray-200 hover:border-purple-300'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${isMain ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
                      {isMain ? 'Main account' : 'Will be merged'}
                    </span>
                    <input type="radio" name="primary" checked={isMain} onChange={() => setPrimaryId(e.id)} className="accent-purple-600 h-4 w-4" />
                  </div>
                  <p className="font-semibold text-gray-800 truncate">{e.name}</p>
                  <div className="mt-2 text-xs text-gray-600 space-y-1">
                    <div><span className="text-gray-400">Email: </span>{e.email || '—'}</div>
                    <div><span className="text-gray-400">Phone: </span>{e.phone || '—'}</div>
                    <div><span className="text-gray-400">Location: </span>{e.city ? (e.state ? `${e.city}, ${e.state}` : e.city) : '—'}</div>
                    <div><span className="text-gray-400">Jobs: </span>{e.job_postings_count ?? 0}</div>
                    <div><span className="text-gray-400">Created: </span>{fmtDate(e.created_at)}</div>
                  </div>
                </label>
              );
            })}
          </div>

          <div className="mt-5 rounded-xl bg-amber-50 border border-amber-100 px-4 py-3 text-xs text-amber-800 space-y-1">
            <p className="font-semibold">After merge:</p>
            <p>• <b>{primary.name}</b> stays active with email <b>{primary.email || '—'}</b> and phone <b>{primary.phone || '—'}</b>.</p>
            <p>• Alternate email: <b>{secondary.email || '—'}</b> · Alternate phone: <b>{secondary.phone || '—'}</b></p>
            <p>• All jobs of <b>{secondary.name}</b> move to the main account, and that account is disabled.</p>
            <p>• You can undo this with the Unmerge button.</p>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 shrink-0">
          <button onClick={onClose} disabled={loading} className="text-sm font-medium text-gray-500 hover:text-gray-700 px-4 py-2">Cancel</button>
          <button onClick={() => onConfirm(primary.id, secondary.id)} disabled={loading}
            className="bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90 text-white px-6 py-2 rounded-full text-sm font-semibold disabled:opacity-50 shadow-sm">
            {loading ? 'Merging…' : 'Confirm merge'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminPanel({ initialTab }) {
  const navigate = useNavigate();
  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);

 const [jobsSubTab, setJobsSubTab] = useState('internal');
 const [subsSubTab, setSubsSubTab] = useState('all'); // 'all' | 'renewals'
const [aggJobs, setAggJobs] = useState([]);
const [aggJobsLoaded, setAggJobsLoaded] = useState(false);
const [aggJobsLoading, setAggJobsLoading] = useState(false);
const [aggJobsError, setAggJobsError] = useState('');
const [deletingJobId, setDeletingJobId] = useState(null);
const [deletingAggJobId, setDeletingAggJobId] = useState(null);
const [jobSearch, setJobSearch] = useState('');
const [aggJobSearch, setAggJobSearch] = useState('');
const [verificationSelectedId, setVerificationSelectedId] = useState(null);

  // ---- Jobs tab: filters, bulk status, view/edit modals ----
  const [jobCityFilter, setJobCityFilter] = useState([]);
  const [jobEmployerFilter, setJobEmployerFilter] = useState([]);
  const [jobTypeFilter, setJobTypeFilter] = useState('');
  const [jobStatusFilter, setJobStatusFilter] = useState('');

  const [aggCityFilter, setAggCityFilter] = useState([]);
  const [aggTypeFilter, setAggTypeFilter] = useState('');

    const [jobDateFrom, setJobDateFrom] = useState('');
  const [jobDateTo, setJobDateTo] = useState('');
  const [aggDateFrom, setAggDateFrom] = useState('');
  const [aggDateTo, setAggDateTo] = useState('');
  const [aggSort, setAggSort] = useState({ key: '', dir: 'desc' });

  const [selectedJobIds, setSelectedJobIds] = useState(new Set());
  const [jobFunnelFilter, setJobFunnelFilter] = useState('');
  const [jobPostedByFilter, setJobPostedByFilter] = useState('');
    const [jobAgentFilter, setJobAgentFilter] = useState('');
  const [jobPage, setJobPage] = useState(1);
  const [jobPageSize, setJobPageSize] = useState(25);

  const [jobSort, setJobSort] = useState({ key: '', dir: 'desc' });
const [metricModal, setMetricModal] = useState(null); // { job, key, label }
const [metricUsers, setMetricUsers] = useState([]);
const [metricLoading, setMetricLoading] = useState(false);
const [metricError, setMetricError] = useState('');
  useEffect(() => {
    setJobPage(1);
  }, [jobSearch, jobCityFilter, jobEmployerFilter, jobTypeFilter, jobStatusFilter, jobDateFrom, jobDateTo, jobFunnelFilter, jobPostedByFilter, jobAgentFilter, jobPageSize, jobSort]);
  const [bulkJobStatus, setBulkJobStatus] = useState('');

  const [bulkJobApplying, setBulkJobApplying] = useState(false);

  const [viewJobModal, setViewJobModal] = useState(null); // job object, or {..., _agg:true} for aggregated
  const [editJobModal, setEditJobModal] = useState(null); // internal jobs only
    const [closeReasonJob, setCloseReasonJob] = useState(null);
  const [savingJobEdit, setSavingJobEdit] = useState(false);
  // ---- end Jobs tab extras ----

  const [activeTab, setActiveTab] = useState(initialTab || 'stats');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [aadhaarRequests, setAadhaarRequests] = useState([]);
  const [roleFilter, setRoleFilter] = useState('');
const [userSearch, setUserSearch] = useState('');
const [userLocationFilter, setUserLocationFilter] = useState([]);
const [userDateFrom, setUserDateFrom] = useState('');
const [userDateTo, setUserDateTo] = useState('');

  // selected user detail state
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
const [togglingUserId, setTogglingUserId] = useState(null);

const [jobsModalUser, setJobsModalUser] = useState(null); // { id, name }
const [appsModalUser, setAppsModalUser] = useState(null); // { id, name }
const [userApps, setUserApps] = useState([]);
const [userAppsLoading, setUserAppsLoading] = useState(false);
const [userAppsError, setUserAppsError] = useState('');
const [userSort, setUserSort] = useState({ key: '', dir: 'desc' });
const [userPage, setUserPage] = useState(1);
const [userPageSize, setUserPageSize] = useState(25);
const [userJobs, setUserJobs] = useState([]);
const [userJobsLoading, setUserJobsLoading] = useState(false);
const [userJobsError, setUserJobsError] = useState('');

const [empAppsModalUser, setEmpAppsModalUser] = useState(null); // { id, name }
const [empApps, setEmpApps] = useState([]);
const [empAppsLoading, setEmpAppsLoading] = useState(false);
const [empAppsError, setEmpAppsError] = useState('');

const [userStatusFilter, setUserStatusFilter] = useState('');
const [userSourceFilter, setUserSourceFilter] = useState('');
const [userVerifiedFilter, setUserVerifiedFilter] = useState('');
const [userAgentFilter, setUserAgentFilter] = useState([]);

const [userStateFilter, setUserStateFilter] = useState([]);
const [userCompanyFilter, setUserCompanyFilter] = useState([]);
const [userProfileFilter, setUserProfileFilter] = useState('');
const [userCompletionFilter, setUserCompletionFilter] = useState('');
const [userNoEmailOnly, setUserNoEmailOnly] = useState(false);
const [userWrongLocOnly, setUserWrongLocOnly] = useState(false);
const [userNoLocOnly, setUserNoLocOnly] = useState(false);
const [showUserFilters, setShowUserFilters] = useState(false);

const [selectedUserIds, setSelectedUserIds] = useState(new Set());
const [bulkUserLoading, setBulkUserLoading] = useState(false);

const [mergeOpen, setMergeOpen] = useState(false);
const [mergeLoading, setMergeLoading] = useState(false);
const [userMergeFilter, setUserMergeFilter] = useState('');

useEffect(() => {
  setUserPage(1);
}, [userSearch, userLocationFilter, userStateFilter, userCompanyFilter, userProfileFilter, userCompletionFilter, userNoEmailOnly, userWrongLocOnly, userNoLocOnly, userStatusFilter, userSourceFilter, userVerifiedFilter, userMergeFilter, userAgentFilter, userDateFrom, userDateTo, roleFilter, userSort, userPageSize]);
  // Master data state
  const [masterType, setMasterType] = useState('skill');
  const [masterView, setMasterView] = useState(''); // '' | 'locations' | 'plans' | 'data'
  const [masterItems, setMasterItems] = useState([]);
  const [masterLoading, setMasterLoading] = useState(false);
  const [newMasterValue, setNewMasterValue] = useState('');
  const [masterSaving, setMasterSaving] = useState(false);
  const [masterError, setMasterError] = useState('');

  // Find Candidates price state
  const [crSettings, setCrSettings] = useState({ price_per_credit: '', unlock_bundle_credits: '', unlock_bundle_unlocks: '', job_post_bundle_credits: '', job_post_bundle_posts: '' });
  const [crMsg, setCrMsg] = useState('');
  const [crErr, setCrErr] = useState('');
  const [crSaving, setCrSaving] = useState(false);
  const [fcPrice, setFcPrice] = useState('');
  const [fcPriceSaving, setFcPriceSaving] = useState(false);
  const [fcPriceMsg, setFcPriceMsg] = useState('');
  const [fcPriceError, setFcPriceError] = useState('');

  // Plans state
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [planEdits, setPlanEdits] = useState({}); // { [planId]: { price_monthly, job_post_limit, is_active } }
  const emptyPack = { name: '', price_monthly: '', job_post_limit: '', candidate_credits: '', featured_jobs: '', contact_unlocks_per_job: '', per_job_duration_days: '', pack_validity_days: '', is_featured_pack: false };
  const [newPack, setNewPack] = useState(emptyPack);
  const [addingPack, setAddingPack] = useState(false);
  const emptyTopup = { credits: '', price: '', unlocks: '' };
  const [topupPacks, setTopupPacks] = useState([]);
  const [topupEdits, setTopupEdits] = useState({});
  const [newTopup, setNewTopup] = useState(emptyTopup);
  const [topupMsg, setTopupMsg] = useState('');
  const [topupErr, setTopupErr] = useState('');
  const [topupBusy, setTopupBusy] = useState(null);
  const emptyBoost = { duration_days: '', price: '' };
  const [boostTiers, setBoostTiers] = useState([]);
  const [boostEdits, setBoostEdits] = useState({});
  const [newBoost, setNewBoost] = useState(emptyBoost);
  const [boostMsg, setBoostMsg] = useState('');
  const [boostErr, setBoostErr] = useState('');
  const [boostBusy, setBoostBusy] = useState(null);
  const emptyFj = { name: 'Featured Job', price: '', duration_days: '' };
  const [fjOptions, setFjOptions] = useState([]);
  const [fjEdits, setFjEdits] = useState({});
  const [newFj, setNewFj] = useState(emptyFj);
  const [fjMsg, setFjMsg] = useState('');
  const [fjErr, setFjErr] = useState('');
  const [fjBusy, setFjBusy] = useState(null);
  const emptyProduct = { name: '', price: '', job_posts: '', validity_days: '' };
  const [jobProducts, setJobProducts] = useState([]);
  const [productEdits, setProductEdits] = useState({});
  const [newProduct, setNewProduct] = useState(emptyProduct);
  const [productMsg, setProductMsg] = useState('');
  const [productErr, setProductErr] = useState('');
  const [productBusy, setProductBusy] = useState(null);
  const [planSavingId, setPlanSavingId] = useState(null);
  const [planMsg, setPlanMsg] = useState('');
  const [planErr, setPlanErr] = useState('');
  const [newPlan, setNewPlan] = useState({ name: '', price_monthly: '', job_post_limit: '', contact_unlocks_per_job: '', per_job_duration_days: '', pack_validity_days: '', is_featured_pack: false });
  const [addingPlan, setAddingPlan] = useState(false);
  const [deletingPlanId, setDeletingPlanId] = useState(null);

  // Agents state (Add Agent feature)
  const [agents, setAgents] = useState([]);
  const [agentsLoaded, setAgentsLoaded] = useState(false);
  const [agentForm, setAgentForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [agentSaving, setAgentSaving] = useState(false);
  const [agentError, setAgentError] = useState('');
   const [agentSuccess, setAgentSuccess] = useState('');
  const [agentSearch, setAgentSearch] = useState('');
  const [agentSort, setAgentSort] = useState({ key: '', dir: 'desc' });
  const [editingAgentId, setEditingAgentId] = useState(null);
  const [editAgentForm, setEditAgentForm] = useState({ name: '', email: '', phone: '' });
  const [editAgentSaving, setEditAgentSaving] = useState(false);
  const [editAgentError, setEditAgentError] = useState('');
  const [deletingAgentId, setDeletingAgentId] = useState(null);
const [togglingAgentId, setTogglingAgentId] = useState(null);
const [impersonatingAgentId, setImpersonatingAgentId] = useState(null);
const [impersonatingUserId, setImpersonatingUserId] = useState(null);

const [passwordAgent, setPasswordAgent] = useState(null); // agent whose password is being changed
const [passwordForm, setPasswordForm] = useState({ password: '', password_confirmation: '' });
const [passwordSaving, setPasswordSaving] = useState(false);
const [passwordError, setPasswordError] = useState('');
const [showPassword, setShowPassword] = useState(false);
const [passwordSavedValue, setPasswordSavedValue] = useState('');
const [currentAgentPassword, setCurrentAgentPassword] = useState(null);

  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState('');
  const [analyticsSort, setAnalyticsSort] = useState({ key: 'impressions', dir: 'desc' });
  const [jobFilter, setJobFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState([]);

  // Company Verification state (GST/PAN/CIN/License documents)
  const [companyDocs, setCompanyDocs] = useState([]);
  const [companyDocsLoading, setCompanyDocsLoading] = useState(false);
  const [companyDocsError, setCompanyDocsError] = useState('');
  const [docActionLoadingId, setDocActionLoadingId] = useState(null);
  const [docStatusFilter, setDocStatusFilter] = useState('pending'); // 'pending' | 'approved' | 'rejected' | ''

    // Job Aggregator — Sources state
  const [jobSources, setJobSources] = useState([]);
  const [jobSourcesLoaded, setJobSourcesLoaded] = useState(false);
  const [jobSourcesLoading, setJobSourcesLoading] = useState(false);
  const [jobSourcesError, setJobSourcesError] = useState('');
  const [sourceForm, setSourceForm] = useState({
    name: '', source_type: 'company_career_page', website_url: '', feed_url: '',
    status: 'active', scrape_frequency: 'daily',
  });
  const [sourceSaving, setSourceSaving] = useState(false);
  const [sourceFormError, setSourceFormError] = useState('');
    const [editingSourceId, setEditingSourceId] = useState(null);
  const [sourceSearch, setSourceSearch] = useState('');
  const [sourceSort, setSourceSort] = useState({ key: '', dir: 'desc' });

    const [logsModalSource, setLogsModalSource] = useState(null);
  const [sourceLogs, setSourceLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState('');
   
  const [aggDashboard, setAggDashboard] = useState(null);
const [aggDashboardLoading, setAggDashboardLoading] = useState(false);
const [aggDashboardError, setAggDashboardError] = useState('');
const [dashboardSourceSearch, setDashboardSourceSearch] = useState('');
  
const [reviewJobs, setReviewJobs] = useState([]);
const [reviewJobsLoading, setReviewJobsLoading] = useState(false);
const [reviewJobsError, setReviewJobsError] = useState('');
const [reviewModalJob, setReviewModalJob] = useState(null); // full job object, or null when modal closed
const [reviewActionLoading, setReviewActionLoading] = useState(false);
const [reviewMobile, setReviewMobile] = useState('');
const [selectedReviewIds, setSelectedReviewIds] = useState(new Set());
const [bulkActionLoading, setBulkActionLoading] = useState(false);
const [reviewSearch, setReviewSearch] = useState('');
const [reviewSort, setReviewSort] = useState({ key: '', dir: 'desc' });

const [aadhaarSearch, setAadhaarSearch] = useState('');
const [selectedAadhaarIds, setSelectedAadhaarIds] = useState(new Set());
const [bulkAadhaarLoading, setBulkAadhaarLoading] = useState(false);

// ---- Applications state ----
const [applications, setApplications] = useState([]);
const [applicationsLoaded, setApplicationsLoaded] = useState(false);
const [applicationsLoading, setApplicationsLoading] = useState(false);
const [applicationsError, setApplicationsError] = useState('');
const [applicationSearch, setApplicationSearch] = useState('');
const [applicationStatusFilter, setApplicationStatusFilter] = useState('');
const [appPage, setAppPage] = useState(1);
const [appPageSize, setAppPageSize] = useState(25);
const [applicationResponseFilter, setApplicationResponseFilter] = useState('');
const RESPONSE_STATUS_GROUPS = { not_yet: ['applied'], screening: ['screening'], shortlisted: ['shortlisted'], interview: ['interview_round_1', 'interview_round_2', 'hr_round'], offer: ['offer'], joined: ['joined'], rejected: ['rejected'], withdrawn: ['withdrawn'] };

const [applicationDateFrom, setApplicationDateFrom] = useState('');
const [applicationDateTo, setApplicationDateTo] = useState('');
const [applicationViewedFilter, setApplicationViewedFilter] = useState('');
const [appCandidateFilter, setAppCandidateFilter] = useState([]);
const [appCompanyFilter, setAppCompanyFilter] = useState([]);
const [appSort, setAppSort] = useState({ key: '', dir: 'desc' });
const [reasonApplication, setReasonApplication] = useState(null);


// ---- Company Verification (GST/PAN/CIN/License) ----
  const loadCompanyDocs = async (status = docStatusFilter) => {
    setActiveTab('verification');
    setCompanyDocsLoading(true);
    setCompanyDocsError('');
    try {
      const response = await api.get('/admin/company-documents/pending', {
        params: status ? { status } : {},
      });
      setCompanyDocs(response.data);
    } catch (err) {
      setCompanyDocsError(err.response?.data?.message || 'Failed to load company documents.');
    } finally {
      setCompanyDocsLoading(false);
    }
  };



  const switchDocStatusFilter = (status) => {
    setDocStatusFilter(status);
    loadCompanyDocs(status);
  };

  const handleDocStatus = async (id, status) => {
    let remarks = null;
    if (status === 'rejected') {
      remarks = window.prompt('Reason for rejection (shown to the employer):', '');
      if (remarks === null) return; // cancelled
    }

    setDocActionLoadingId(id);
    try {
      const response = await api.put(`/admin/company-documents/${id}/status`, { status, remarks });
      const updatedDoc = response.data.document;

      setCompanyDocs((prev) => {
        // On the Pending tab, an actioned doc no longer belongs — drop it.
        if (docStatusFilter === 'pending') {
          return prev.filter((d) => d.id !== id);
        }
        // On Approved/Rejected/All tabs, just update it in place.
        return prev.map((d) => (d.id === id ? { ...d, ...updatedDoc } : d));
      });
    } catch (err) {
      alert(err.response?.data?.message || `Failed to mark document as ${status}.`);
    } finally {
      setDocActionLoadingId(null);
    }
  };
  // ---- end Company Verification ----

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const fetchStats = async () => {
      try {
        const response = await api.get('/admin/stats');
        setStats(response.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Access denied.');
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [navigate]);

  const loadUsers = async () => {
    setActiveTab('users');
    if (users.length === 0) {
      const response = await api.get('/admin/users');
      setUsers(response.data);
    }
    if (!agentsLoaded) loadAgents();
  };

const loadJobs = async () => {
  setActiveTab('jobs');
  if (jobs.length === 0) {
    const response = await api.get('/admin/jobs');
    setJobs(response.data);
  }
  if (!aggJobsLoaded) loadAggJobs();
    if (!agentsLoaded) loadAgents();
};

const loadAggJobs = async () => {
  setAggJobsLoading(true);
  setAggJobsError('');
  try {
    const response = await api.get('/aggregated-jobs');
    setAggJobs(Array.isArray(response.data) ? response.data : (response.data?.data ?? []));
    setAggJobsLoaded(true);
  } catch (err) {
    setAggJobsError(err.response?.data?.message || 'Failed to load aggregated jobs.');
  } finally {
    setAggJobsLoading(false);
  }
};

const deleteJob = async (job) => {
  if (!window.confirm(`Delete "${job.title}"? This cannot be undone.`)) return;
  setDeletingJobId(job.id);
  try {
    await api.delete(`/admin/jobs/${job.id}`);
    setJobs((prev) => prev.filter((j) => j.id !== job.id));
  } catch (err) {
    alert(err.response?.data?.message || 'Failed to delete job.');
  } finally {
    setDeletingJobId(null);
  }
};

const deleteAggJob = async (job) => {
  if (!window.confirm(`Delete "${job.title}"? This will also remove any applications tied to it. This cannot be undone.`)) return;
  setDeletingAggJobId(job.id);
  try {
    await api.delete(`/admin/aggregated-jobs/${job.id}`);
    setAggJobs((prev) => prev.filter((j) => j.id !== job.id));
  } catch (err) {
    alert(err.response?.data?.message || 'Failed to delete aggregated job.');
  } finally {
    setDeletingAggJobId(null);
  }
};

  // ---- Jobs tab: filter/select/edit helpers (mirrors AgentJobsList.jsx) ----
  const jobEmployerOptions = Array.from(
    new Set(jobs.map((j) => j.employer?.name).filter(Boolean))
  ).sort();

    const jobAgentName = (j) =>
    j.posted_by_agent_id
      ? (agents.find((a) => String(a.id) === String(j.posted_by_agent_id))?.name || `Agent #${j.posted_by_agent_id}`)
      : '';

  const jobAgentOptions = Array.from(new Set(jobs.map(jobAgentName).filter(Boolean))).sort();

  const jobCityOptions = [
    'Not Specified',
    ...Array.from(new Set([...jobs, ...aggJobs].map((j) => j.location).filter(Boolean))).sort(),
  ];

  const toggleJobSelect = (id) => {
    setSelectedJobIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allVisibleJobsSelected =
    pagedInternalJobsMemo().length > 0 &&
    pagedInternalJobsMemo().every((j) => selectedJobIds.has(j.id));

  const toggleSelectAllJobs = () => {
    setSelectedJobIds(
      allVisibleJobsSelected ? new Set() : new Set(pagedInternalJobsMemo().map((j) => j.id))
    );
  };

  // NOTE: assumes a `PUT /admin/jobs/{id}` endpoint exists on the backend,
  // mirroring AgentJobsList.jsx's `PUT /agent/jobs/{id}` — add it if missing.
  const handleJobStatusChange = async (jobId, newStatus) => {
    const prevJobs = jobs;
    setJobs((prev) => prev.map((j) => (j.id === jobId ? { ...j, status: newStatus } : j)));
    try {
      await api.put(`/admin/jobs/${jobId}`, { status: newStatus });
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update job status.');
      setJobs(prevJobs);
    }
  };

  const handleBulkJobStatusApply = async () => {
    if (!bulkJobStatus || selectedJobIds.size === 0) return;
    setBulkJobApplying(true);
    const ids = Array.from(selectedJobIds);
    try {
      await Promise.all(ids.map((id) => api.put(`/admin/jobs/${id}`, { status: bulkJobStatus })));
      setJobs((prev) => prev.map((j) => (ids.includes(j.id) ? { ...j, status: bulkJobStatus } : j)));
      setSelectedJobIds(new Set());
      setBulkJobStatus('');
    } catch (err) {
      alert('Some jobs failed to update. Please refresh and try again.');
    } finally {
      setBulkJobApplying(false);
    }
  };

  const handleSaveJobEdit = async (e) => {
    e.preventDefault();
    setSavingJobEdit(true);
    try {
      await api.put(`/admin/jobs/${editJobModal.id}`, {
        title: editJobModal.title,
        location: editJobModal.location,
        job_type: editJobModal.job_type,
        salary_min: editJobModal.salary_min,
        salary_max: editJobModal.salary_max,
        openings: editJobModal.openings,
        status: editJobModal.status,
      });
      setJobs((prev) => prev.map((j) => (j.id === editJobModal.id ? { ...j, ...editJobModal } : j)));
      setEditJobModal(null);
    } catch (err) {
      alert(err.response?.data?.message || 'Could not save changes. Please try again.');
    } finally {
      setSavingJobEdit(false);
    }
  };

 

  const toggleJobSort = (key) => {
    setJobSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }
    );
  };

  const openMetricModal = async (job, col) => {
    setMetricModal({ job, key: col.key, label: col.label });
    setMetricUsers([]);
    setMetricError('');
    setMetricLoading(true);
    try {
      const res = await api.get(`/admin/jobs/${job.id}/metric-users`, { params: { metric: col.key } });
      setMetricUsers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setMetricError(err.response?.data?.message || 'Failed to load users.');
    } finally {
      setMetricLoading(false);
    }
  };

  const clearJobFilters = () => {
    setJobFunnelFilter('');
    setJobPostedByFilter('');
    setJobAgentFilter('');
    setJobSearch('');
setJobCityFilter([]);
    setJobEmployerFilter([]);
    setJobTypeFilter('');
    setJobStatusFilter('');
    setJobDateFrom('');
    setJobDateTo('');
  };

  const clearAggJobFilters = () => {
    setAggJobSearch('');
    setAggCityFilter([]);
    setAggTypeFilter('');
    setAggDateFrom('');
    setAggDateTo('');
  };
  // ---- end Jobs tab helpers ----

  const loadAadhaar = async () => {
    setActiveTab('aadhaar');
    const response = await api.get('/admin/aadhaar/pending');
    setAadhaarRequests(response.data);
  };

  // ---- Master data ----
  const loadMasterItems = async (type) => {
    setMasterLoading(true);
    setMasterError('');
    try {
      const response = await api.get('/admin/master-items', { params: { type } });
      setMasterItems(response.data);
    } catch (err) {
      setMasterError(err.response?.data?.message || 'Failed to load items.');
    } finally {
      setMasterLoading(false);
    }
  };

  const loadFcPrice = async () => {
    setFcPriceError('');
    try {
      const response = await api.get('/find-candidates/price');
      setFcPrice(String(response.data.amount_inr));
    } catch (err) {
      setFcPriceError(err.response?.data?.message || 'Failed to load price.');
    }
  };

  const loadCreditSettings = async () => {
    try {
      const r = await api.get('/admin/credits/settings');
      setCrSettings({ price_per_credit: String(r.data.price_per_credit), unlock_bundle_credits: String(r.data.unlock_bundle_credits), unlock_bundle_unlocks: String(r.data.unlock_bundle_unlocks), job_post_bundle_credits: String(r.data.job_post_bundle_credits), job_post_bundle_posts: String(r.data.job_post_bundle_posts) });
    } catch (err) {
      setCrErr(err.response?.data?.message || 'Failed to load credit settings.');
    }
  };

  const saveCreditSettings = async (e) => {
    e.preventDefault();
    setCrSaving(true); setCrErr(''); setCrMsg('');
    try {
      await api.put('/admin/credits/settings', {
        price_per_credit: Number(crSettings.price_per_credit),
        unlock_bundle_credits: Number(crSettings.unlock_bundle_credits),
        unlock_bundle_unlocks: Number(crSettings.unlock_bundle_unlocks),
        job_post_bundle_credits: Number(crSettings.job_post_bundle_credits),
        job_post_bundle_posts: Number(crSettings.job_post_bundle_posts),
      });
      setCrMsg('Saved. Applies to new unlocks and new purchases.');
    } catch (err) {
      setCrErr(err.response?.data?.message || 'Failed to save.');
    } finally {
      setCrSaving(false);
    }
  };

  const saveFcPrice = async (e) => {
    e.preventDefault();
    const raw = String(fcPrice).trim();
    const amount = Number(raw);
    if (!/^\d+$/.test(raw) || amount < 1 || amount > 100000) {
      setFcPriceMsg('');
      setFcPriceError('Enter a whole number between 1 and 100000.');
      return;
    }
    setFcPriceSaving(true);
    setFcPriceError('');
    setFcPriceMsg('');
    try {
      const response = await api.put('/admin/find-candidates/price', { amount_inr: amount });
      setFcPrice(String(response.data.amount_inr));
      setFcPriceMsg('Price updated. New orders will be charged \u20B9' + response.data.amount_inr + '.');
    } catch (err) {
      setFcPriceError(
        err.response?.data?.errors?.amount_inr?.[0] ||
        err.response?.data?.message ||
        'Failed to update price.'
      );
    } finally {
      setFcPriceSaving(false);
    }
  };

  const loadPlans = async () => {
    setPlansLoading(true);
    setPlanErr('');
    try {
      const response = await api.get('/admin/plans');
      setPlans(response.data);
      const edits = {};
      response.data.forEach((p) => {
        edits[p.id] = {
          price_monthly: String(p.price_monthly),
          job_post_limit: p.job_post_limit === null ? '' : String(p.job_post_limit),
          contact_unlocks_per_job: p.contact_unlocks_per_job === null ? '' : String(p.contact_unlocks_per_job),
          candidate_credits: p.candidate_credits == null ? '' : String(p.candidate_credits),
          featured_jobs: p.featured_jobs == null ? '' : String(p.featured_jobs),
          per_job_duration_days: p.per_job_duration_days === null ? '' : String(p.per_job_duration_days),
          pack_validity_days: p.pack_validity_days === null ? '' : String(p.pack_validity_days),
          is_featured_pack: !!p.is_featured_pack,
          is_active: p.is_active,
        };
      });
      setPlanEdits(edits);
    } catch (err) {
      setPlanErr(err.response?.data?.message || 'Failed to load plans.');
    } finally {
      setPlansLoading(false);
    }
  };

  const updatePlanField = (planId, field, value) => {
    setPlanEdits((prev) => ({ ...prev, [planId]: { ...prev[planId], [field]: value } }));
  };

  const savePlan = async (planId) => {
    const edit = planEdits[planId];
    setPlanSavingId(planId);
    setPlanErr('');
    setPlanMsg('');
    try {
      const payload = {
        price_monthly: Number(edit.price_monthly),
        job_post_limit: edit.job_post_limit === '' ? null : Number(edit.job_post_limit),
        contact_unlocks_per_job: edit.contact_unlocks_per_job === '' ? null : Number(edit.contact_unlocks_per_job),
        candidate_credits: edit.candidate_credits == null || edit.candidate_credits === '' ? null : Number(edit.candidate_credits),
        featured_jobs: edit.featured_jobs == null || edit.featured_jobs === '' ? null : Number(edit.featured_jobs),
        per_job_duration_days: edit.per_job_duration_days === '' ? null : Number(edit.per_job_duration_days),
        pack_validity_days: edit.pack_validity_days === '' ? null : Number(edit.pack_validity_days),
        is_featured_pack: !!edit.is_featured_pack,
        is_active: edit.is_active,
      };
      const response = await api.put(`/admin/plans/${planId}`, payload);
      setPlans((prev) => prev.map((p) => (p.id === planId ? response.data : p)));
      setPlanMsg(`${response.data.name} updated.`);
    } catch (err) {
      setPlanErr(err.response?.data?.message || 'Failed to update plan.');
    } finally {
      setPlanSavingId(null);
    }
  };

  const addPlan = async () => {
    setAddingPlan(true);
    setPlanErr('');
    setPlanMsg('');
    try {
      const payload = {
        name: newPlan.name,
        price_monthly: Number(newPlan.price_monthly),
        job_post_limit: newPlan.job_post_limit === '' ? null : Number(newPlan.job_post_limit),
        contact_unlocks_per_job: newPlan.contact_unlocks_per_job === '' ? null : Number(newPlan.contact_unlocks_per_job),
        per_job_duration_days: newPlan.per_job_duration_days === '' ? null : Number(newPlan.per_job_duration_days),
        pack_validity_days: newPlan.pack_validity_days === '' ? null : Number(newPlan.pack_validity_days),
        is_featured_pack: !!newPlan.is_featured_pack,
      };
      const response = await api.post('/admin/plans', payload);
      setPlans((prev) => [...prev, response.data]);
      setPlanEdits((prev) => ({ ...prev, [response.data.id]: {
        price_monthly: String(response.data.price_monthly),
        job_post_limit: response.data.job_post_limit === null ? '' : String(response.data.job_post_limit),
        contact_unlocks_per_job: response.data.contact_unlocks_per_job === null ? '' : String(response.data.contact_unlocks_per_job),
        per_job_duration_days: response.data.per_job_duration_days === null ? '' : String(response.data.per_job_duration_days),
        pack_validity_days: response.data.pack_validity_days === null ? '' : String(response.data.pack_validity_days),
        is_featured_pack: !!response.data.is_featured_pack,
        is_active: response.data.is_active,
      } }));
      setNewPlan({ name: '', price_monthly: '', job_post_limit: '', contact_unlocks_per_job: '', per_job_duration_days: '', pack_validity_days: '', is_featured_pack: false });
      setPlanMsg(`${response.data.name} plan created.`);
    } catch (err) {
      setPlanErr(err.response?.data?.message || 'Failed to create plan.');
    } finally {
      setAddingPlan(false);
    }
  };

  const productToEdit = (d) => ({
    name: d.name,
    price: String(d.price),
    job_posts: String(d.job_posts),
    validity_days: String(d.validity_days),
    is_active: !!d.is_active,
  });

  const loadJobProducts = async () => {
    try {
      const { data } = await api.get('/admin/job-products');
      setJobProducts(data);
      const e = {};
      data.forEach((d) => { e[d.id] = productToEdit(d); });
      setProductEdits(e);
    } catch (err) {
      setProductErr(err.response?.data?.message || 'Failed to load pay-per-job options.');
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadJobProducts(); }, []);

  const updateProductField = (id, field, value) => {
    setProductEdits((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const saveProduct = async (id) => {
    const e = productEdits[id];
    setProductBusy(id);
    setProductErr('');
    setProductMsg('');
    try {
      const { data } = await api.put(`/admin/job-products/${id}`, {
        name: e.name,
        price: Number(e.price),
        job_posts: Number(e.job_posts),
        validity_days: Number(e.validity_days),
        is_active: !!e.is_active,
      });
      setJobProducts((prev) => prev.map((d) => (d.id === id ? data : d)));
      setProductEdits((prev) => ({ ...prev, [id]: productToEdit(data) }));
      setProductMsg(`${data.name} updated.`);
    } catch (err) {
      setProductErr(err.response?.data?.message || 'Failed to update.');
    } finally {
      setProductBusy(null);
    }
  };

  const addProduct = async () => {
    setProductBusy('add');
    setProductErr('');
    setProductMsg('');
    try {
      const { data } = await api.post('/admin/job-products', {
        name: newProduct.name,
        price: Number(newProduct.price),
        job_posts: Number(newProduct.job_posts),
        validity_days: Number(newProduct.validity_days),
      });
      setJobProducts((prev) => [...prev, data]);
      setProductEdits((prev) => ({ ...prev, [data.id]: productToEdit(data) }));
      setNewProduct(emptyProduct);
      setProductMsg(`${data.name} created.`);
    } catch (err) {
      setProductErr(err.response?.data?.message || 'Failed to create.');
    } finally {
      setProductBusy(null);
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm('Delete this pay-per-job option? This cannot be undone.')) return;
    setProductBusy(id);
    setProductErr('');
    setProductMsg('');
    try {
      await api.delete(`/admin/job-products/${id}`);
      setJobProducts((prev) => prev.filter((d) => d.id !== id));
      setProductMsg('Option deleted.');
    } catch (err) {
      setProductErr(err.response?.data?.message || 'Failed to delete.');
    } finally {
      setProductBusy(null);
    }
  };

  const topupToEdit = (d) => ({
    credits: String(d.credits),
    price: String(d.price),
    unlocks: String(d.unlocks),
    is_active: !!d.is_active,
  });

  const loadTopups = async () => {
    try {
      const { data } = await api.get('/admin/candidate-credit-packs');
      setTopupPacks(data);
      const e = {};
      data.forEach((d) => { e[d.id] = topupToEdit(d); });
      setTopupEdits(e);
    } catch (err) {
      setTopupErr(err.response?.data?.message || 'Failed to load top-up packs.');
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadTopups(); }, []);

  const updateTopupField = (id, field, value) => {
    setTopupEdits((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const saveTopup = async (id) => {
    const e = topupEdits[id];
    setTopupBusy(id);
    setTopupErr('');
    setTopupMsg('');
    try {
      const { data } = await api.put(`/admin/candidate-credit-packs/${id}`, {
        credits: Number(e.credits),
        price: Number(e.price),
        unlocks: Number(e.unlocks),
        is_active: !!e.is_active,
      });
      setTopupPacks((prev) => prev.map((d) => (d.id === id ? data : d)));
      setTopupEdits((prev) => ({ ...prev, [id]: topupToEdit(data) }));
      setTopupMsg(`${data.credits}-credit pack updated.`);
    } catch (err) {
      setTopupErr(err.response?.data?.message || 'Failed to update.');
    } finally {
      setTopupBusy(null);
    }
  };

  const addTopup = async () => {
    setTopupBusy('add');
    setTopupErr('');
    setTopupMsg('');
    try {
      const { data } = await api.post('/admin/candidate-credit-packs', {
        credits: Number(newTopup.credits),
        price: Number(newTopup.price),
        unlocks: Number(newTopup.unlocks),
      });
      setTopupPacks((prev) => [...prev, data]);
      setTopupEdits((prev) => ({ ...prev, [data.id]: topupToEdit(data) }));
      setNewTopup(emptyTopup);
      setTopupMsg(`${data.credits}-credit pack created.`);
    } catch (err) {
      setTopupErr(err.response?.data?.message || 'Failed to create.');
    } finally {
      setTopupBusy(null);
    }
  };

  const deleteTopup = async (id) => {
    if (!window.confirm('Delete this top-up pack? This cannot be undone.')) return;
    setTopupBusy(id);
    setTopupErr('');
    setTopupMsg('');
    try {
      await api.delete(`/admin/candidate-credit-packs/${id}`);
      setTopupPacks((prev) => prev.filter((d) => d.id !== id));
      setTopupMsg('Top-up pack deleted.');
    } catch (err) {
      setTopupErr(err.response?.data?.message || 'Failed to delete.');
    } finally {
      setTopupBusy(null);
    }
  };

  const boostToEdit = (d) => ({
    duration_days: String(d.duration_days),
    price: String(d.price),
    is_active: !!d.is_active,
  });

  const loadBoosts = async () => {
    try {
      const { data } = await api.get('/admin/job-boosts');
      setBoostTiers(data);
      const e = {};
      data.forEach((d) => { e[d.id] = boostToEdit(d); });
      setBoostEdits(e);
    } catch (err) {
      setBoostErr(err.response?.data?.message || 'Failed to load boost tiers.');
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadBoosts(); }, []);

  const updateBoostField = (id, field, value) => {
    setBoostEdits((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const saveBoost = async (id) => {
    const e = boostEdits[id];
    setBoostBusy(id);
    setBoostErr('');
    setBoostMsg('');
    try {
      const { data } = await api.put(`/admin/job-boosts/${id}`, {
        duration_days: Number(e.duration_days),
        price: Number(e.price),
        is_active: !!e.is_active,
      });
      setBoostTiers((prev) => prev.map((d) => (d.id === id ? data : d)));
      setBoostEdits((prev) => ({ ...prev, [id]: boostToEdit(data) }));
      setBoostMsg(`${data.duration_days}-day boost updated.`);
    } catch (err) {
      setBoostErr(err.response?.data?.message || 'Failed to update.');
    } finally {
      setBoostBusy(null);
    }
  };

  const addBoost = async () => {
    setBoostBusy('add');
    setBoostErr('');
    setBoostMsg('');
    try {
      const { data } = await api.post('/admin/job-boosts', {
        duration_days: Number(newBoost.duration_days),
        price: Number(newBoost.price),
      });
      setBoostTiers((prev) => [...prev, data]);
      setBoostEdits((prev) => ({ ...prev, [data.id]: boostToEdit(data) }));
      setNewBoost(emptyBoost);
      setBoostMsg(`${data.duration_days}-day boost created.`);
    } catch (err) {
      setBoostErr(err.response?.data?.message || 'Failed to create.');
    } finally {
      setBoostBusy(null);
    }
  };

  const deleteBoost = async (id) => {
    if (!window.confirm('Delete this boost tier? This cannot be undone.')) return;
    setBoostBusy(id);
    setBoostErr('');
    setBoostMsg('');
    try {
      await api.delete(`/admin/job-boosts/${id}`);
      setBoostTiers((prev) => prev.filter((d) => d.id !== id));
      setBoostMsg('Boost tier deleted.');
    } catch (err) {
      setBoostErr(err.response?.data?.message || 'Failed to delete.');
    } finally {
      setBoostBusy(null);
    }
  };

  const fjToEdit = (d) => ({
    name: d.name,
    price: String(d.price),
    duration_days: String(d.duration_days),
    is_active: !!d.is_active,
  });

  const loadFj = async () => {
    try {
      const { data } = await api.get('/admin/featured-job-options');
      setFjOptions(data);
      const e = {};
      data.forEach((d) => { e[d.id] = fjToEdit(d); });
      setFjEdits(e);
    } catch (err) {
      setFjErr(err.response?.data?.message || 'Failed to load Featured Job options.');
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadFj(); }, []);

  const updateFjField = (id, field, value) => {
    setFjEdits((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const saveFj = async (id) => {
    const e = fjEdits[id];
    setFjBusy(id);
    setFjErr('');
    setFjMsg('');
    try {
      const { data } = await api.put(`/admin/featured-job-options/${id}`, {
        name: e.name,
        price: Number(e.price),
        duration_days: Number(e.duration_days),
        is_active: !!e.is_active,
      });
      setFjOptions((prev) => prev.map((d) => (d.id === id ? data : d)));
      setFjEdits((prev) => ({ ...prev, [id]: fjToEdit(data) }));
      setFjMsg(`${data.name} updated.`);
    } catch (err) {
      setFjErr(err.response?.data?.message || 'Failed to update.');
    } finally {
      setFjBusy(null);
    }
  };

  const addFj = async () => {
    setFjBusy('add');
    setFjErr('');
    setFjMsg('');
    try {
      const { data } = await api.post('/admin/featured-job-options', {
        name: newFj.name || 'Featured Job',
        price: Number(newFj.price),
        duration_days: Number(newFj.duration_days),
      });
      setFjOptions((prev) => [...prev, data]);
      setFjEdits((prev) => ({ ...prev, [data.id]: fjToEdit(data) }));
      setNewFj(emptyFj);
      setFjMsg(`${data.name} created.`);
    } catch (err) {
      setFjErr(err.response?.data?.message || 'Failed to create.');
    } finally {
      setFjBusy(null);
    }
  };

  const deleteFj = async (id) => {
    if (!window.confirm('Delete this Featured Job option? Credits already bought keep working.')) return;
    setFjBusy(id);
    setFjErr('');
    setFjMsg('');
    try {
      await api.delete(`/admin/featured-job-options/${id}`);
      setFjOptions((prev) => prev.filter((d) => d.id !== id));
      setFjMsg('Option deleted.');
    } catch (err) {
      setFjErr(err.response?.data?.message || 'Failed to delete.');
    } finally {
      setFjBusy(null);
    }
  };

  const addPack = async () => {
    setAddingPack(true);
    setPlanErr('');
    setPlanMsg('');
    try {
      const num = (v) => (v === '' ? null : Number(v));
      const response = await api.post('/admin/plans', {
        type: 'hiring',
        name: newPack.name,
        price_monthly: Number(newPack.price_monthly),
        job_post_limit: num(newPack.job_post_limit),
        candidate_credits: num(newPack.candidate_credits),
        featured_jobs: num(newPack.featured_jobs),
        contact_unlocks_per_job: num(newPack.contact_unlocks_per_job),
        per_job_duration_days: num(newPack.per_job_duration_days),
        pack_validity_days: num(newPack.pack_validity_days),
        is_featured_pack: !!newPack.is_featured_pack,
      });
      const d = response.data;
      setPlans((prev) => [...prev, d]);
      setPlanEdits((prev) => ({ ...prev, [d.id]: {
        price_monthly: String(d.price_monthly),
        job_post_limit: d.job_post_limit == null ? '' : String(d.job_post_limit),
        candidate_credits: d.candidate_credits == null ? '' : String(d.candidate_credits),
        featured_jobs: d.featured_jobs == null ? '' : String(d.featured_jobs),
        contact_unlocks_per_job: d.contact_unlocks_per_job == null ? '' : String(d.contact_unlocks_per_job),
        per_job_duration_days: d.per_job_duration_days == null ? '' : String(d.per_job_duration_days),
        pack_validity_days: d.pack_validity_days == null ? '' : String(d.pack_validity_days),
        is_featured_pack: !!d.is_featured_pack,
        is_active: !!d.is_active,
      } }));
      setNewPack(emptyPack);
      setPlanMsg(`${d.name} hiring pack created.`);
    } catch (err) {
      setPlanErr(err.response?.data?.message || 'Failed to create hiring pack.');
    } finally {
      setAddingPack(false);
    }
  };

  const deletePlan = async (planId) => {
    if (!window.confirm('Delete this plan? This cannot be undone.')) return;
    setDeletingPlanId(planId);
    setPlanErr('');
    setPlanMsg('');
    try {
      await api.delete(`/admin/plans/${planId}`);
      setPlans((prev) => prev.filter((p) => p.id !== planId));
      setPlanMsg('Plan deleted.');
    } catch (err) {
      setPlanErr(err.response?.data?.message || 'Failed to delete plan.');
    } finally {
      setDeletingPlanId(null);
    }
  };

  const openMasterTab = () => {
    loadFcPrice();
    loadPlans();
    loadCreditSettings();
    setActiveTab('master');
    loadMasterItems(masterType);
        setMasterView('');
  };

  const switchMasterType = (type) => {
    setMasterType(type);
    loadMasterItems(type);
  };

  const addMasterItem = async (e) => {
    e.preventDefault();
    if (!newMasterValue.trim()) return;
    setMasterSaving(true);
    setMasterError('');
    try {
      const response = await api.post('/admin/master-items', {
        type: masterType,
        value: newMasterValue.trim(),
      });
      setMasterItems((prev) =>
        [...prev, response.data.item].sort((a, b) => a.value.localeCompare(b.value))
      );
      setNewMasterValue('');
    } catch (err) {
      setMasterError(err.response?.data?.message || 'Failed to add item.');
    } finally {
      setMasterSaving(false);
    }
  };

  const deleteMasterItem = async (id) => {
    try {
      await api.delete(`/admin/master-items/${id}`);
      setMasterItems((prev) => prev.filter((item) => item.id !== id));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete item.');
    }
  };
  // ---- end Master data ----

  // ---- Analytics ----
  const loadAnalytics = async () => {
    setActiveTab('analytics');
    setAnalyticsLoading(true);
    setAnalyticsError('');
    try {
      // NOTE: assumed route — expects the backend to return:
      // {
      //   totals: { impressions, views, saves, applications, skips, avg_time_spent_seconds },
      //   jobs: [{ id, title, company_name, impressions, views, saves, applications, skips, avg_time_spent_seconds }]
      // }
      const response = await api.get('/admin/analytics');
      setAnalytics(response.data);
    } catch (err) {
      setAnalyticsError(err.response?.data?.message || 'Failed to load analytics.');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const ctr = (views, impressions) =>
    impressions > 0 ? ((views / impressions) * 100).toFixed(1) : '0.0';

  const conversionRate = (applications, views) =>
    views > 0 ? ((applications / views) * 100).toFixed(1) : '0.0';

  const formatSeconds = (secs) => {
    if (!secs) return '—';
    const m = Math.floor(secs / 60);
    const s = Math.round(secs % 60);
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  const filteredJobs = (analytics?.jobs || []).filter((job) => {
    const matchesJob = job.title.toLowerCase().includes(jobFilter.toLowerCase());
    const matchesCompany = companyFilter.length === 0 || companyFilter.includes(job.company_name);
    return matchesJob && matchesCompany;
  });

  const sortedJobs = filteredJobs
    .slice()
    .sort((a, b) => {
      const { key, dir } = analyticsSort;
      const av = key === 'ctr' ? Number(ctr(a.views, a.impressions))
        : key === 'conversion' ? Number(conversionRate(a.applications, a.views))
        : a[key] ?? 0;
      const bv = key === 'ctr' ? Number(ctr(b.views, b.impressions))
        : key === 'conversion' ? Number(conversionRate(b.applications, b.views))
        : b[key] ?? 0;
      return dir === 'asc' ? av - bv : bv - av;
    });

  const toggleSort = (key) => {
    setAnalyticsSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }
    );
  };

  const companyOptions = Array.from(
    new Set((analytics?.jobs || []).map((j) => j.company_name))
  ).sort();
  // ---- end Analytics ----

    // ---- Job Aggregator — Sources ----
  const loadJobSources = async () => {
    setJobSourcesLoading(true);
    setJobSourcesError('');
    try {
      const response = await api.get('/admin/job-aggregator/sources');
      setJobSources(response.data);
      setJobSourcesLoaded(true);
    } catch (err) {
      setJobSourcesError(err.response?.data?.message || 'Failed to load job sources.');
    } finally {
      setJobSourcesLoading(false);
    }
  };

  const openJobAggregatorTab = () => {
    setActiveTab('job-aggregator');
    if (!jobSourcesLoaded) loadJobSources();
  };

  const handleSourceFormChange = (e) => {
    setSourceForm({ ...sourceForm, [e.target.name]: e.target.value });
  };

  const resetSourceForm = () => {
    setSourceForm({
      name: '', source_type: 'company_career_page', website_url: '', feed_url: '',
      status: 'active', scrape_frequency: 'daily',
    });
    setEditingSourceId(null);
  };

  const submitSourceForm = async (e) => {
    e.preventDefault();
    setSourceSaving(true);
    setSourceFormError('');
    try {
      if (editingSourceId) {
        const response = await api.put(`/admin/job-aggregator/sources/${editingSourceId}`, sourceForm);
        setJobSources((prev) =>
          prev.map((s) => (s.id === editingSourceId ? response.data.source : s))
        );
      } else {
        const response = await api.post('/admin/job-aggregator/sources', sourceForm);
        setJobSources((prev) => [response.data.source, ...prev]);
      }
      resetSourceForm();
    } catch (err) {
      const errors = err.response?.data?.errors;
      setSourceFormError(
        errors ? Object.values(errors).flat().join(' ') : (err.response?.data?.message || 'Failed to save source.')
      );
    } finally {
      setSourceSaving(false);
    }
  };

  const editSource = (source) => {
    setEditingSourceId(source.id);
    setSourceForm({
      name: source.name,
      source_type: source.source_type,
      website_url: source.website_url || '',
      feed_url: source.feed_url || '',
      status: source.status,
      scrape_frequency: source.scrape_frequency,
    });
  };

  const deleteSource = async (id) => {
    if (!window.confirm('Delete this job source? Its aggregated jobs will also be removed.')) return;
    try {
      await api.delete(`/admin/job-aggregator/sources/${id}`);
      setJobSources((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete source.');
    }
  };

  
  const viewSourceLogs = async (source) => {
    setLogsModalSource(source);
    setLogsLoading(true);
    setLogsError('');
    try {
      const response = await api.get(`/admin/job-aggregator/sources/${source.id}/logs`);
      setSourceLogs(response.data);
    } catch (err) {
      setLogsError(err.response?.data?.message || 'Failed to load run history.');
    } finally {
      setLogsLoading(false);
    }
  };

  const closeLogsModal = () => {
    setLogsModalSource(null);
    setSourceLogs([]);
    setLogsError('');
  };

  const loadAggregatorDashboard = async () => {
  setActiveTab('aggregator-dashboard');
  setAggDashboardLoading(true);
  setAggDashboardError('');
  try {
    const response = await api.get('/admin/job-aggregator/dashboard');
    setAggDashboard(response.data);
  } catch (err) {
    setAggDashboardError(err.response?.data?.message || 'Failed to load dashboard.');
  } finally {
    setAggDashboardLoading(false);
  }
};

const loadReviewJobs = async () => {
  setActiveTab('job-review');
  setReviewJobsLoading(true);
  setReviewJobsError('');
  setSelectedReviewIds(new Set());
  try {
    const response = await api.get('/admin/job-aggregator/jobs', { params: { status: 'pending_review' } });
    setReviewJobs(response.data);
  } catch (err) {
    setReviewJobsError(err.response?.data?.message || 'Failed to load jobs for review.');
  } finally {
    setReviewJobsLoading(false);
  }
};
 
const openReviewModal = (job) => {
  setReviewModalJob(job);
  setReviewMobile(job.company_mobile || '');
};
const closeReviewModal = () => {
  setReviewModalJob(null);
  setReviewMobile('');
};


const toggleReviewSelect = (id) => {
  setSelectedReviewIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    return next;
  });
};

const toggleReviewSelectAll = () => {
  setSelectedReviewIds((prev) =>
    prev.size === filteredReviewJobs.length ? new Set() : new Set(filteredReviewJobs.map((j) => j.id))
  );
};

const handleBulkReviewAction = async (action) => {
  if (selectedReviewIds.size === 0) return;
  const label = action === 'publish' ? 'publish' : action === 'reject' ? 'reject' : 'mark as duplicate';
  if (!window.confirm(`${label.charAt(0).toUpperCase() + label.slice(1)} ${selectedReviewIds.size} selected job(s)?`)) return;

  setBulkActionLoading(true);
  try {
    const ids = Array.from(selectedReviewIds);
    await api.put('/admin/job-aggregator/jobs/bulk-status', { ids, action });
    setReviewJobs((prev) => prev.filter((j) => !selectedReviewIds.has(j.id)));
    setSelectedReviewIds(new Set());
  } catch (err) {
    alert(err.response?.data?.message || `Failed to ${label} selected jobs.`);
  } finally {
    setBulkActionLoading(false);
  }
};
 
const handleReviewAction = async (action) => {
  if (!reviewModalJob) return;
  setReviewActionLoading(true);
  try {
    await api.put(`/admin/job-aggregator/jobs/${reviewModalJob.id}/status`, {
      action,
      company_mobile: reviewMobile || null,
    });
    setReviewJobs((prev) => prev.filter((j) => j.id !== reviewModalJob.id));
    closeReviewModal();
  } catch (err) {
    alert(err.response?.data?.message || 'Failed to update job.');
  } finally {
    setReviewActionLoading(false);
  }
};

const fetchApplications = async () => {
  setApplicationsLoading(true);
  setApplicationsError('');
  try {
    const response = await api.get('/admin/applications', {
      params: {
        q: applicationSearch || undefined,
        status: applicationStatusFilter || undefined,
        date_from: applicationDateFrom || undefined,
        date_to: applicationDateTo || undefined,
        viewed: applicationViewedFilter || undefined,
        per_page: 1000,
      },
    });
    setApplications(Array.isArray(response.data) ? response.data : (response.data?.data ?? []));
    setApplicationsLoaded(true);
    setAppPage(1);
  } catch (err) {
    setApplicationsError(err.response?.data?.message || 'Failed to load applications.');
  } finally {
    setApplicationsLoading(false);
  }
};

const handleUpdateApplicationStatus = async (appId, newStatus) => {
  const prevApplications = applications;
  setApplications((prev) => prev.map((a) => (a.id === appId ? { ...a, status: newStatus } : a)));
  try {
    await api.put(`/admin/applications/${appId}/status`, { status: newStatus });
  } catch (err) {
    setApplications(prevApplications);
    alert(err.response?.data?.message || 'Failed to update application status.');
  }
};

const loadApplications = () => setActiveTab('applications');

useEffect(() => {
  if (activeTab !== 'applications') return;
  const t = setTimeout(fetchApplications, 300);
  return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [activeTab, applicationSearch, applicationStatusFilter, applicationDateFrom, applicationDateTo, applicationViewedFilter]);

  const SOURCE_TYPE_LABELS = {
    company_career_page: 'Company Career Page',
    api: 'API',
    rss_xml: 'RSS/XML',
    json_feed: 'JSON Feed',
    partner_feed: 'Partner Feed',
    government_feed: 'Government Feed',
    other: 'Other Permitted Source',
  };
  // ---- end Job Aggregator ----


  // ---- Agents ----
  const loadAgents = async () => {
    try {
      const response = await api.get('/admin/agents');
      setAgents(response.data);
      setAgentsLoaded(true);
    } catch (err) {
      console.error('Failed to load agents', err);
    }
  };

  const openAgentsTab = () => {
    setActiveTab('agents');
    if (!agentsLoaded) loadAgents();
  };

  const handleAgentFormChange = (e) => {
    setAgentForm({ ...agentForm, [e.target.name]: e.target.value });
  };

    const filteredAgents = agents.filter((a) => {
    if (!agentSearch.trim()) return true;
    const q = agentSearch.toLowerCase();
    return (
      a.name?.toLowerCase().includes(q) ||
      a.email?.toLowerCase().includes(q) ||
      a.phone?.includes(agentSearch)
    );
  });
    const toggleAgentSort = (key) =>
    setAgentSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }
    );

  const agentSortValue = (a, key) => {
    switch (key) {
      case 'name': return a.name || '';
      case 'email': return a.email || '';
      case 'phone': return a.phone || '';
      case 'status': return a.status || 'active';
      default: return null;
    }
  };

  const sortedAgents = agentSort.key
    ? filteredAgents.slice().sort((a, b) =>
        compareSort(agentSortValue(a, agentSort.key), agentSortValue(b, agentSort.key), agentSort.dir)
      )
    : filteredAgents;



  const startEditAgent = (agent) => {
    setEditingAgentId(agent.id);
    setEditAgentForm({ name: agent.name, email: agent.email, phone: agent.phone || '' });
    setEditAgentError('');
  };

  const cancelEditAgent = () => {
    setEditingAgentId(null);
    setEditAgentError('');
  };

  const handleEditAgentFormChange = (e) => {
    setEditAgentForm({ ...editAgentForm, [e.target.name]: e.target.value });
  };

  const saveEditAgent = async (id) => {
    setEditAgentSaving(true);
    setEditAgentError('');
    try {
      const response = await api.put(`/admin/agents/${id}`, editAgentForm);
      setAgents((prev) => prev.map((a) => (a.id === id ? response.data.agent : a)));
      setEditingAgentId(null);
    } catch (err) {
      const errors = err.response?.data?.errors;
      setEditAgentError(
        errors ? Object.values(errors).flat().join(' ') : (err.response?.data?.message || 'Failed to update agent.')
      );
    } finally {
      setEditAgentSaving(false);
    }
  };

  const deleteAgent = async (agent) => {
    if (!window.confirm(`Delete agent "${agent.name}"? This cannot be undone.`)) return;
    setDeletingAgentId(agent.id);
    try {
      await api.delete(`/admin/agents/${agent.id}`);
      setAgents((prev) => prev.filter((a) => a.id !== agent.id));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete agent.');
    } finally {
      setDeletingAgentId(null);
    }
  };


  const agentStatus = (a) => a.status || 'active';

const handleToggleAgentStatus = async (agent) => {
  const enabling = agentStatus(agent) !== 'active';
  if (!window.confirm(`Are you sure you want to ${enabling ? 'enable' : 'disable'} agent "${agent.name}"?`)) return;

  setTogglingAgentId(agent.id);
  try {
    const response = await api.put(`/admin/agents/${agent.id}/status`, {
      status: enabling ? 'active' : 'disabled',
    });
    setAgents((prev) => prev.map((a) => (a.id === agent.id ? response.data.agent : a)));
  } catch (err) {
    alert(err.response?.data?.message || 'Failed to update agent status.');
  } finally {
    setTogglingAgentId(null);
  }
};


const handleLoginAsAgent = async (agent) => {
  if (agentStatus(agent) !== 'active') {
    alert('This agent is disabled. Enable the agent first.');
    return;
  }
  if (!window.confirm(`Login as agent "${agent.name}"?`)) return;

  setImpersonatingAgentId(agent.id);
  try {
    const response = await api.post(`/admin/agents/${agent.id}/impersonate`);

    // Keep the admin session so we can return to it
    localStorage.setItem('admin_token', localStorage.getItem('token'));
    localStorage.setItem('admin_user', localStorage.getItem('user'));

    // Switch to the agent session
    localStorage.setItem('token', response.data.token);
    localStorage.setItem('user', JSON.stringify(response.data.user));

    window.location.href = '/agent/dashboard';
  } catch (err) {
    alert(err.response?.data?.message || 'Failed to login as agent.');
    setImpersonatingAgentId(null);
  }
};

const handleLoginAsUser = async (u) => {
  if ((u.status || 'active') !== 'active') {
    alert('This account is disabled. Enable it first.');
    return;
  }
  if (!window.confirm(`Login as ${u.role === 'employer' ? 'employer' : 'candidate'} "${u.name}"?`)) return;

  setImpersonatingUserId(u.id);
  try {
    const response = await api.post(`/admin/users/${u.id}/impersonate`);

    // keep the admin session so "Return to Admin" works
    localStorage.setItem('admin_token', localStorage.getItem('token'));
    localStorage.setItem('admin_user', localStorage.getItem('user'));

    localStorage.setItem('token', response.data.token);
    localStorage.setItem('user', JSON.stringify(response.data.user));

    window.location.href = response.data.user.role === 'employer' ? '/employer/jobs' : '/dashboard';
  } catch (err) {
    alert(err.response?.data?.message || 'Failed to login as this user.');
    setImpersonatingUserId(null);
  }
};

const openPasswordModal = async (agent) => {
  setPasswordAgent(agent);
  setPasswordForm({ password: '', password_confirmation: '' });
  setPasswordError('');
  setShowPassword(false);
  setPasswordSavedValue('');
  setCurrentAgentPassword(null);
  try {
    const res = await api.get(`/admin/agents/${agent.id}/password`);
    setCurrentAgentPassword(res.data.password || '');
  } catch {
    setCurrentAgentPassword('');
  }
};

const closePasswordModal = () => {
  setPasswordSavedValue('');
  setCurrentAgentPassword(null);
  setPasswordAgent(null);
  setPasswordForm({ password: '', password_confirmation: '' });
  setPasswordError('');
};

const generatePassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  const bytes = crypto.getRandomValues(new Uint32Array(12));
  const pwd = Array.from(bytes, (b) => chars[b % chars.length]).join('');
  setPasswordForm({ password: pwd, password_confirmation: pwd });
  setShowPassword(true);
};

const copyPassword = async () => {
  try {
    await navigator.clipboard.writeText(passwordForm.password);
  } catch {
    alert('Could not copy. Please select and copy the password manually.');
  }
};

const submitAgentPassword = async (e) => {
  e.preventDefault();
  if (passwordForm.password.length < 8) {
    setPasswordError('Password must be at least 8 characters.');
    return;
  }
  if (passwordForm.password !== passwordForm.password_confirmation) {
    setPasswordError('Passwords do not match.');
    return;
  }
  setPasswordSaving(true);
  setPasswordError('');
  try {
    await api.put(`/admin/agents/${passwordAgent.id}/password`, passwordForm);
    setPasswordSavedValue(passwordForm.password);
    setCurrentAgentPassword(passwordForm.password);
  } catch (err) {
    const errors = err.response?.data?.errors;
    setPasswordError(
      errors ? Object.values(errors).flat().join(' ') : (err.response?.data?.message || 'Failed to update password.')
    );
  } finally {
    setPasswordSaving(false);
  }
};
  


  const createAgent = async (e) => {
    e.preventDefault();
    setAgentSaving(true);
    setAgentError('');
    setAgentSuccess('');
    try {
      const response = await api.post('/admin/agents', agentForm);
      setAgents((prev) => [...prev, response.data.agent].sort((a, b) => a.name.localeCompare(b.name)));
      setAgentSuccess(`Agent "${response.data.agent.name}" created successfully.`);
      setAgentForm({ name: '', email: '', phone: '', password: '' });
    } catch (err) {
      const errors = err.response?.data?.errors;
      if (errors) {
        setAgentError(Object.values(errors).flat().join(' '));
      } else {
        setAgentError(err.response?.data?.message || 'Failed to create agent.');
      }
    } finally {
      setAgentSaving(false);
    }
  };
  // ---- end Agents ----
  

  const handleAssignAgent = async (user, agentId) => {
    const endpoint = user.role === 'employer'
      ? `/admin/employers/${user.id}/assign-agent`
      : `/admin/candidates/${user.id}/assign-agent`;

    try {
      const response = await api.put(endpoint, { agent_id: agentId || null });
      const updated = user.role === 'employer' ? response.data.employer : response.data.candidate;
      setUsers((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, agent_id: updated.agent_id } : u))
      );
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to assign agent.');
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await api.delete(`/admin/users/${id}`);
      setUsers(users.filter((u) => u.id !== id));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete user.');
    }
  };

  const handleToggleUserStatus = async (u) => {
  const enabling = userStatus(u) !== 'active';
  const newStatus = enabling ? 'active' : 'suspended';
  if (!window.confirm(`Are you sure you want to ${enabling ? 'enable' : 'disable'} ${u.name}?`)) return;

  setTogglingUserId(u.id);
  try {
    const response = await api.put(`/admin/users/${u.id}/status`, { status: newStatus });
    const saved = response.data?.user?.status ?? newStatus;
    setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, status: saved } : x)));
  } catch (err) {
    alert(err.response?.data?.message || 'Failed to update user status.');
  } finally {
    setTogglingUserId(null);
  }
};


const toggleUserSelect = (id) => {
  setSelectedUserIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
};

const selectableUserIds = () =>
  filteredUsers.filter((u) => u.role !== 'admin' && !isMergedAway(u)).map((u) => u.id);

const allUsersSelected = () => {
  const ids = selectableUserIds();
  return ids.length > 0 && ids.every((id) => selectedUserIds.has(id));
};

const toggleSelectAllUsers = () => {
  setSelectedUserIds(allUsersSelected() ? new Set() : new Set(selectableUserIds()));
};

const handleBulkUserStatus = async (status) => {
  if (selectedUserIds.size === 0) return;
  const verb = status === 'active' ? 'Enable' : 'Disable';
  if (!window.confirm(`${verb} ${selectedUserIds.size} selected user(s)?`)) return;

  setBulkUserLoading(true);
  try {
    const res = await api.post('/admin/users/bulk-status', {
      ids: Array.from(selectedUserIds),
      status,
    });
    const done = new Set(res.data.updated_ids);
    setUsers((prev) => prev.map((x) => (done.has(x.id) ? { ...x, status } : x)));
    setSelectedUserIds(new Set());
  } catch (err) {
    alert(err.response?.data?.message || `Failed to ${verb.toLowerCase()} selected users.`);
  } finally {
    setBulkUserLoading(false);
  }
};

  const handleAadhaarStatus = async (id, status) => {
    try {
      await api.put(`/admin/aadhaar/${id}`, { aadhaar_status: status });
      setAadhaarRequests(aadhaarRequests.filter((u) => u.id !== id));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update status.');
    }
  };


  const handleBulkAadhaarStatus = async (status) => {
  if (selectedAadhaarIds.size === 0) return;
  const label = status === 'verified' ? 'approve' : 'reject';
  if (!window.confirm(`${label.charAt(0).toUpperCase() + label.slice(1)} ${selectedAadhaarIds.size} selected request(s)?`)) return;

  setBulkAadhaarLoading(true);
  const ids = Array.from(selectedAadhaarIds);
  try {
    await Promise.all(ids.map((id) => api.put(`/admin/aadhaar/${id}`, { aadhaar_status: status })));
    setAadhaarRequests((prev) => prev.filter((u) => !selectedAadhaarIds.has(u.id)));
    setSelectedAadhaarIds(new Set());
  } catch (err) {
    alert('Some requests failed to update. Please refresh and try again.');
  } finally {
    setBulkAadhaarLoading(false);
  }
};

const reloadUsers = async () => {
  const res = await api.get('/admin/users');
  setUsers(res.data);
};

const handleMerge = async (primaryId, secondaryId) => {
  setMergeLoading(true);
  try {
    await api.post('/admin/employers/merge', { primary_id: primaryId, secondary_id: secondaryId });
    setMergeOpen(false);
    setSelectedUserIds(new Set());
    await reloadUsers();
  } catch (err) {
    alert(err.response?.data?.message || 'Failed to merge employers.');
  } finally {
    setMergeLoading(false);
  }
};

const handleUnmerge = async (u) => {
  if (!window.confirm(`Unmerge "${u.name}"? Its email, phone and jobs will be restored and the account re-enabled.`)) return;
  setTogglingUserId(u.id);
  try {
    await api.post(`/admin/employers/${u.id}/unmerge`);
    await reloadUsers();
  } catch (err) {
    alert(err.response?.data?.message || 'Failed to unmerge account.');
  } finally {
    setTogglingUserId(null);
  }
};

const toggleUserSort = (key) => {
  setUserSort((prev) =>
    prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }
  );
};

const openUserJobsModal = async (u) => {
  setJobsModalUser({ id: u.id, name: u.name });
  setUserJobs([]);
  setUserJobsError('');
  setUserJobsLoading(true);
  try {
    const res = await api.get(`/admin/users/${u.id}`);
    setUserJobs(res.data?.job_postings || []);
  } catch (err) {
    setUserJobsError(err.response?.data?.message || 'Failed to load jobs.');
  } finally {
    setUserJobsLoading(false);
  }
};

const closeUserJobsModal = () => {
  setJobsModalUser(null);
  setUserJobs([]);
  setUserJobsError('');
};

const openUserAppsModal = async (u) => {
  setAppsModalUser({ id: u.id, name: u.name });
  setUserApps([]);
  setUserAppsError('');
  setUserAppsLoading(true);
  try {
    const res = await api.get(`/admin/users/${u.id}`);
    setUserApps(res.data?.job_applications || []);
  } catch (err) {
    setUserAppsError(err.response?.data?.message || 'Failed to load applications.');
  } finally {
    setUserAppsLoading(false);
  }
};

const closeUserAppsModal = () => {
  setAppsModalUser(null);
  setUserApps([]);
  setUserAppsError('');
};

const openEmployerAppsModal = async (u) => {
  setEmpAppsModalUser({ id: u.id, name: u.name });
  setEmpApps([]);
  setEmpAppsError('');
  setEmpAppsLoading(true);
  try {
    const res = await api.get(`/admin/users/${u.id}`);
    const rows = (res.data?.job_postings || []).flatMap((jp) =>
      (jp.applications || []).map((a) => ({ ...a, job_title: jp.title }))
    );
    rows.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    setEmpApps(rows);
  } catch (err) {
    setEmpAppsError(err.response?.data?.message || 'Failed to load applications.');
  } finally {
    setEmpAppsLoading(false);
  }
};

const closeEmployerAppsModal = () => {
  setEmpAppsModalUser(null);
  setEmpApps([]);
  setEmpAppsError('');
};

  const viewUserDetails = async (id) => {
    setDetailLoading(true);
    try {
      const response = await api.get(`/admin/users/${id}`);
      setSelectedUser(response.data);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to load user details.');
    } finally {
      setDetailLoading(false);
    }
  };
  const userLocationOptions = ['Not Specified', ...Array.from(new Set(users.flatMap((u) => [u.city, u.state]).filter(Boolean))).sort()];
    const userAgentOptions = ['Unassigned', ...agents.map((a) => a.name).filter(Boolean)];

    const userStateOptions = Array.from(new Set(users.map(userStateOf).filter(Boolean))).sort();
const userCompanyOptions = Array.from(new Set(users.filter((u) => u.role === 'employer').map((u) => u.name).filter(Boolean))).sort();
const userWrongLocCount = users.filter((u) => hasWrongLocation(u.city)).length;
const userNoLocCount = users.filter((u) => !String(u.city || '').trim()).length;
const userActiveFilterCount = [
  userLocationFilter.length > 0, userStateFilter.length > 0, userAgentFilter.length > 0, userCompanyFilter.length > 0,
  !!userStatusFilter, !!userSourceFilter, !!userVerifiedFilter, !!userMergeFilter, !!userProfileFilter, !!userCompletionFilter,
  !!userDateFrom, !!userDateTo, userNoEmailOnly,
].filter(Boolean).length;
const userHasActiveFilters = userSearch || userActiveFilterCount > 0 || userWrongLocOnly || userNoLocOnly;
const clearUserFilters = () => {
  setUserSearch(''); setUserLocationFilter([]); setUserStateFilter([]); setUserAgentFilter([]);
  setUserCompanyFilter([]); setUserMergeFilter(''); setUserStatusFilter(''); setUserSourceFilter(''); setUserVerifiedFilter('');
  setUserProfileFilter(''); setUserCompletionFilter(''); setUserDateFrom(''); setUserDateTo('');
  setUserNoEmailOnly(false); setUserWrongLocOnly(false); setUserNoLocOnly(false);
};

  const mergedCountById = users.reduce((m, x) => {
    if (x.merged_into_id) m[x.merged_into_id] = (m[x.merged_into_id] || 0) + 1;
    return m;
  }, {});
  const hasMerged = (x) => (mergedCountById[x.id] || 0) > 0;
  const mergedIntoName = (x) => users.find((y) => y.id === x.merged_into_id)?.name;

  const selectedUsersArr = users.filter((x) => selectedUserIds.has(x.id));
  const canMerge =
    selectedUsersArr.length === 2 &&
    selectedUsersArr.every((x) => x.role === 'employer' && !x.employer_id && !isMergedAway(x) && userStatus(x) === 'active');

  const filteredUsers = users
    .filter((u) => {
      switch (userMergeFilter) {
        case 'merged':     return isMergedAway(u);
        case 'has_merged': return hasMerged(u);
        case 'not_merged': return !isMergedAway(u) && !hasMerged(u);
        default:           return true;
      }
    })
    .filter((u) => (roleFilter ? u.role === roleFilter : true))

        .filter((u) => !userNoEmailOnly || !String(u.email || '').trim())
    .filter((u) => !userWrongLocOnly || hasWrongLocation(u.city))
    .filter((u) => !userNoLocOnly || !String(u.city || '').trim())
    .filter((u) => userStateFilter.length === 0 || userStateFilter.includes(userStateOf(u)))
    .filter((u) => userCompanyFilter.length === 0 || (u.role === 'employer' && userCompanyFilter.includes(u.name)))
    .filter((u) => {
      if (!userProfileFilter) return true;
      if (u.role !== 'job_seeker') return false;
      const done = Number(u.profile_completion ?? 0) >= 100;
      return userProfileFilter === 'complete' ? done : !done;
    })
    .filter((u) => {
      if (!userCompletionFilter) return true;
      if (u.role !== 'job_seeker') return false;
      const { min, max } = COMPLETION_RANGES[userCompletionFilter];
      const pct = Math.round(Number(u.profile_completion ?? 0));
      return pct >= min && pct <= max;
    })

        .filter((u) => (userStatusFilter ? userStatus(u) === userStatusFilter : true))

        .filter((u) =>
  userSourceFilter
    ? (userSourceFilter === 'agent' ? isAgentAdded(u) : !isAgentAdded(u))
    : true
)
    .filter((u) => {
      if (!userVerifiedFilter) return true;
      if (u.role !== 'employer') return false; // verification only applies to employers
      const verified = u.company_verified_status === 'verified';
      const hasJobs = Number(u.job_postings_count) > 0;
      switch (userVerifiedFilter) {
        case 'verified': return verified;
        case 'verified_posted': return verified && hasJobs;
        case 'verified_not_posted': return verified && !hasJobs;
        case 'not_verified': return !verified;
        default: return true;
      }
    })
    .filter((u) => {
      if (!userSearch.trim()) return true;
      const q = userSearch.toLowerCase();
      return (
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.phone?.includes(userSearch)
      );
    })
        .filter((u) => {
      if (userAgentFilter.length === 0) return true;
      const agentName = agents.find((a) => String(a.id) === String(u.agent_id))?.name;
      return userAgentFilter.some((sel) =>
        sel === 'Unassigned' ? !u.agent_id : agentName === sel
      );
    })
    .filter((u) => {
      if (userLocationFilter.length === 0) return true;
      return userLocationFilter.some((loc) =>
        loc === 'Not Specified' ? (!u.city && !u.state) : (u.city === loc || u.state === loc)
      );
    })
    .filter((u) => {
      if (!userDateFrom && !userDateTo) return true;
      if (!u.created_at) return false;
      const created = new Date(u.created_at).setHours(0, 0, 0, 0);
      if (userDateFrom && created < new Date(userDateFrom).setHours(0, 0, 0, 0)) return false;
      if (userDateTo && created > new Date(userDateTo).setHours(23, 59, 59, 999)) return false;
      return true;
    });

  const userSortValue = (u, key) => {
  switch (key) {
    case 'name': return u.name || '';
    case 'email': return u.email || '';
    case 'phone': return u.phone || '';
    case 'role': return u.role || '';
    case 'location': return u.city ? (u.state ? `${u.city}, ${u.state}` : u.city) : '';
    case 'created': return u.created_at ? new Date(u.created_at).getTime() : null;
    case 'applications':
      return u.role === 'job_seeker'
        ? Number(u.job_applications_count ?? 0)
        : u.role === 'employer'
          ? Number(u.received_applications_count ?? 0)
          : null;
    case 'job_posts': return u.role === 'employer' ? Number(u.job_postings_count ?? 0) : null;
    case 'agent':
      return u.role === 'employer' && !u.employer_id
        ? (agents.find((a) => String(a.id) === String(u.agent_id))?.name || '')
        : '';
    case 'status': return userStatus(u);
    default: return null;
  }
};

const sortedUsers = userSort.key
  ? filteredUsers.slice().sort((a, b) =>
      compareSort(userSortValue(a, userSort.key), userSortValue(b, userSort.key), userSort.dir)
    )
  : filteredUsers;

    const userTotalPages = Math.max(1, Math.ceil(sortedUsers.length / userPageSize));
  const userCurPage = Math.min(userPage, userTotalPages);
  const pagedUsers = sortedUsers.slice((userCurPage - 1) * userPageSize, userCurPage * userPageSize);

  function jobSortValue(j, key) {
  switch (key) {
    case 'title': return j.title || '';
    case 'company': return j.company_name || '';
    case 'employer': return j.employer?.name || '';
    case 'location': return j.location || '';
    case 'type': return j.job_type || '';
    case 'salary': return j.salary_min ? Number(j.salary_min) : null;
    case 'status': return j.status || '';
    case 'posted': return j.created_at ? new Date(j.created_at).getTime() : null;
        case 'postedBy': return jobAgentName(j);
    default: return Number(jobMetricValue(j, key)) || 0; // metric columns
  }
}

  function filteredInternalJobsMemo() {
    const list = filteredInternalJobsBase();
    if (!jobSort.key) return list;
    return list.slice().sort((a, b) =>
  compareSort(jobSortValue(a, jobSort.key), jobSortValue(b, jobSort.key), jobSort.dir)
);
  }
  function filteredInternalJobsBase() {
    return jobs.filter((j) => {
      if (jobSearch.trim()) {
        const q = jobSearch.toLowerCase();
        const matches =
          j.title?.toLowerCase().includes(q) ||
          j.company_name?.toLowerCase().includes(q) ||
          j.employer?.name?.toLowerCase().includes(q);
        if (!matches) return false;
      }
      if (jobCityFilter.length > 0) {
        const ok = jobCityFilter.some((c) => (c === 'Not Specified' ? !j.location : j.location === c));
        if (!ok) return false;
      }
      if (jobEmployerFilter.length > 0 && !jobEmployerFilter.includes(j.employer?.name)) return false;
      if (jobTypeFilter && j.job_type !== jobTypeFilter) return false;
      if (jobStatusFilter && j.status !== jobStatusFilter) return false;
      
            if (jobAgentFilter && jobAgentName(j) !== jobAgentFilter) return false;

            if (jobPostedByFilter) {
        const byAgent = isAgentPosted(j);
        if (jobPostedByFilter === 'agent' ? !byAgent : byAgent) return false;
      }

      if (jobFunnelFilter) {
        const f = j.funnel || {};
        const counts = {
          impressions: j.impressions,
          clicks: j.clicks,
          applicants: j.applications_count ?? j.job_applications_count,
          screening: f.screening,
          shortlisted: f.shortlisted,
          interview_round_1: f.interview_round_1,
          interview_round_2: f.interview_round_2,
          hr_round: f.hr_round,
          offer: f.offer,
          joined: f.joined,
          rejected: f.rejected,
        };
        if (!(Number(counts[jobFunnelFilter]) > 0)) return false;
      }
      if (!inDateRange(j.created_at, jobDateFrom, jobDateTo)) return false;
      return true;
    });
  }
  function pagedInternalJobsMemo() {
    const all = filteredInternalJobsMemo();
    const pages = Math.max(1, Math.ceil(all.length / jobPageSize));
    const p = Math.min(jobPage, pages);
    return all.slice((p - 1) * jobPageSize, p * jobPageSize);
  }
  const filteredInternalJobs = filteredInternalJobsMemo();
  const pagedInternalJobs = pagedInternalJobsMemo();

  const filteredAggJobs = aggJobs.filter((j) => {
    if (aggJobSearch.trim()) {
      const q = aggJobSearch.toLowerCase();
      const matches =
        j.title?.toLowerCase().includes(q) ||
        j.company_name?.toLowerCase().includes(q) ||
        j.location?.toLowerCase().includes(q);
      if (!matches) return false;
    }
      if (aggCityFilter.length > 0) {
      const ok = aggCityFilter.some((c) => (c === 'Not Specified' ? !j.location : j.location === c));
      if (!ok) return false;
    }
    if (aggTypeFilter && j.job_type !== aggTypeFilter) return false;
    if (!inDateRange(j.posted_at || j.created_at, aggDateFrom, aggDateTo)) return false;
    return true;
  });

  const toggleAggSort = (key) =>
  setAggSort((prev) =>
    prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }
  );

const aggSortValue = (j, key) => {
  switch (key) {
    case 'title': return j.title || '';
    case 'company': return j.company_name || '';
    case 'location': return j.location || '';
    case 'type': return j.job_type || '';
    case 'salary': return j.salary_min || j.salary_max ? Number(j.salary_min || j.salary_max) : null;
    case 'source': return j.source_name || '';
    default: return null;
  }
};

const sortedAggJobs = aggSort.key
  ? filteredAggJobs.slice().sort((a, b) =>
      compareSort(aggSortValue(a, aggSort.key), aggSortValue(b, aggSort.key), aggSort.dir)
    )
  : filteredAggJobs;


  const filteredJobSources = jobSources.filter((s) =>
    sourceSearch.trim() ? s.name?.toLowerCase().includes(sourceSearch.toLowerCase()) : true
  );

    const toggleSourceSort = (key) =>
    setSourceSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }
    );

  const sourceSortValue = (s, key) => {
    switch (key) {
      case 'name': return s.name || '';
      case 'type': return SOURCE_TYPE_LABELS[s.source_type] || s.source_type || '';
      case 'status': return s.status || '';
      case 'frequency': return s.scrape_frequency || '';
      case 'jobs': return Number(s.total_jobs ?? 0);
      case 'last_run': return s.last_run_at ? new Date(s.last_run_at).getTime() : null;
      default: return null;
    }
  };

  const sortedJobSources = sourceSort.key
    ? filteredJobSources.slice().sort((a, b) =>
        compareSort(sourceSortValue(a, sourceSort.key), sourceSortValue(b, sourceSort.key), sourceSort.dir)
      )
    : filteredJobSources;

  const filteredSourcePerformance = (aggDashboard?.source_performance || []).filter((s) =>
    dashboardSourceSearch.trim() ? s.name?.toLowerCase().includes(dashboardSourceSearch.toLowerCase()) : true
  );

const appCandidateOptions = Array.from(new Set(applications.map((a) => a.user?.name).filter(Boolean))).sort();
const appCompanyOptions = Array.from(new Set(applications.map((a) => a.job_posting?.company_name).filter(Boolean))).sort();

const filteredApplications = applications.filter((app) => {
  if (applicationResponseFilter && !(RESPONSE_STATUS_GROUPS[applicationResponseFilter] || []).includes(app.status)) return false;
  if (appCandidateFilter.length > 0 && !appCandidateFilter.includes(app.user?.name)) return false;
  if (appCompanyFilter.length > 0 && !appCompanyFilter.includes(app.job_posting?.company_name)) return false;
  return true;
});

const toggleAppSort = (key) => {
  setAppPage(1);
  setAppSort((prev) =>
    prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }
  );
};

const appSortValue = (app, key) => {
  switch (key) {
    case 'candidate': return app.user?.name || '';
    case 'job': return app.job_posting?.title || '';
    case 'company': return app.job_posting?.company_name || '';
    case 'applied': return app.created_at ? new Date(app.created_at).getTime() : null;
    case 'viewed':
      return app.employer_viewed_at
        ? new Date(app.employer_viewed_at).getTime() - new Date(app.created_at).getTime()
        : null;
    case 'status': return app.status || '';
    default: return null;
  }
};

const sortedApplications = appSort.key
  ? filteredApplications.slice().sort((a, b) =>
      compareSort(appSortValue(a, appSort.key), appSortValue(b, appSort.key), appSort.dir)
    )
  : filteredApplications;


  const filteredReviewJobs = reviewJobs.filter((j) => {
    if (!reviewSearch.trim()) return true;
    const q = reviewSearch.toLowerCase();
    return (
      j.title?.toLowerCase().includes(q) ||
      j.company_name?.toLowerCase().includes(q) ||
      j.location?.toLowerCase().includes(q)
    );
  });

    const toggleReviewSort = (key) =>
    setReviewSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }
    );

  const reviewSortValue = (j, key) => {
    switch (key) {
      case 'title': return j.title || '';
      case 'company': return j.company_name || '';
      case 'location': return j.location || '';
      case 'salary': return j.salary_min || j.salary_max ? Number(j.salary_min || j.salary_max) : null;
      case 'source': return j.source_name || '';
      case 'mobile': return j.company_mobile || '';
      case 'score': return j.quality_score != null ? Number(j.quality_score) : null;
      default: return null;
    }
  };

  const sortedReviewJobs = reviewSort.key
    ? filteredReviewJobs.slice().sort((a, b) =>
        compareSort(reviewSortValue(a, reviewSort.key), reviewSortValue(b, reviewSort.key), reviewSort.dir)
      )
    : filteredReviewJobs;

  const filteredAadhaarRequests = aadhaarRequests.filter((u) => {
  if (!aadhaarSearch.trim()) return true;
  const q = aadhaarSearch.toLowerCase();
  return (
    u.name?.toLowerCase().includes(q) ||
    u.email?.toLowerCase().includes(q) ||
    u.aadhaar_number?.includes(aadhaarSearch)
  );
});

const toggleAadhaarSelect = (id) => {
  setSelectedAadhaarIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
};

const toggleAadhaarSelectAll = () => {
  setSelectedAadhaarIds((prev) =>
    prev.size === filteredAadhaarRequests.length
      ? new Set()
      : new Set(filteredAadhaarRequests.map((u) => u.id))
  );
};
  const closeUserDetails = () => setSelectedUser(null);

  if (loading) return <div className="p-10 text-center text-gray-500">Loading...</div>;

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FAF5FC] to-[#FDF1F9] flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow text-center">
          <p className="text-red-600 font-medium mb-4">{error}</p>
          <Link to="/dashboard" className="text-[#7C3AED] hover:underline font-medium">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF5FC] to-[#FDF1F9]">
      <Navbar user={user} />

     {/* Sidebar - same look as the agent sidebar */}
<aside className="hidden lg:block fixed left-0 top-[69px] w-56 h-[calc(100vh-69px)] bg-white border-r border-gray-100 overflow-y-auto pb-6 z-10">
  <div className="p-3">
    <div className="bg-gradient-to-r from-[#7C3AED] to-[#DB2777] rounded-xl px-3 py-3 flex items-center gap-3 mb-3">
      <div className="w-9 h-9 rounded-full bg-white/20 text-white flex items-center justify-center font-bold text-sm shrink-0">
        {user?.name ? user.name.charAt(0).toUpperCase() : 'A'}
      </div>
      <div className="min-w-0">
        <p className="text-white text-sm font-semibold truncate">{user?.name || 'Admin'}</p>
        <p className="text-pink-100 text-xs uppercase tracking-wide">Admin</p>
      </div>
    </div>
  </div>
  <nav className="px-3 pb-3 space-y-1">
    {[
      { key: 'stats', label: 'Overview', icon: '📊', action: () => setActiveTab('stats') },
      { key: 'users', label: 'Users', icon: '👥', action: loadUsers },
      { key: 'agents', label: 'Agents', icon: '🧑\u200d💼', action: openAgentsTab },
      { key: 'jobs', label: 'Jobs', icon: '💼', action: loadJobs },
       { key: 'applications', label: 'Applications', icon: '📄', action: loadApplications },
     { key: 'verification', label: 'Verification', icon: '🛡️', action: () => { setActiveTab('verification'); setVerificationSelectedId(null); } },
       { key: 'aadhaar', label: 'Aadhaar Requests', icon: '🪪', action: loadAadhaar },
      { key: 'support-tickets', label: 'Support Tickets', icon: '🎫', action: () => setActiveTab('support-tickets') },
      { key: 'businesses', label: 'Businesses', icon: '🏢', action: () => setActiveTab('businesses') },
      { key: 'business-import', label: 'Import Businesses', icon: '📥', action: () => setActiveTab('business-import') },
      { key: 'locations', label: 'Locations & Categories', icon: '📍', action: () => setActiveTab('locations') },
      { key: 'enterprise', label: 'Enterprise Alerts', icon: '🔔', action: () => setActiveTab('enterprise') },
            { key: 'enterprise-enquiries', label: 'Enterprise Enquiries', icon: '📨', action: () => setActiveTab('enterprise-enquiries') },
      { key: 'subscriptions', label: 'Subscriptions', icon: '💳', action: () => setActiveTab('subscriptions') },
      { key: 'master', label: 'Master', icon: '🗂️', action: openMasterTab },
      // { key: 'analytics', label: 'Analytics', icon: '📈', action: loadAnalytics },
      { key: 'billing-analytics', label: 'Billing Analytics', icon: '💳', action: () => setActiveTab('billing-analytics') },
      { key: 'billing-reports', label: 'Billing Reports', icon: '💰', action: () => setActiveTab('billing-reports') },
        { key: 'job-aggregator', label: 'Job Aggregator', icon: '🔎', action: openJobAggregatorTab },
      { key: 'aggregator-dashboard', label: 'Aggregator Dashboard', icon: '📊', action: loadAggregatorDashboard },
           { key: 'job-review', label: 'Job Review', icon: '📝', action: loadReviewJobs },
      { key: 'notifications', label: 'Send Notification', icon: '📣', action: () => setActiveTab('notifications') },
           { key: 'notification-report', label: 'Notification Report', icon: '📋', action: () => setActiveTab('notification-report') },
{ key: 'deletion-requests', label: 'Deletion Requests', icon: '🗑️', action: () => setActiveTab('deletion-requests') },
{ key: 'contact-enquiries', label: 'Contact Enquiries', icon: '✉️', action: () => setActiveTab('contact-enquiries') },

    ].map((tab) => (
      <button
        key={tab.key}
        onClick={tab.action}
        className={`w-full flex items-center gap-3 text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
          activeTab === tab.key
            ? 'bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white shadow-sm'
            : 'text-gray-600 hover:bg-purple-50 hover:text-[#7C3AED]'
        }`}
      >
        <span>{tab.icon}</span>
        {tab.label}
      </button>
    ))}
  </nav>
</aside>

      {/* Main content */}
      <div className="w-full py-8 px-4 lg:pl-60 lg:pr-6">
           {/* <div className="bg-gradient-to-r from-[#0D1B3D] to-[#1E3A8A] rounded-xl shadow-lg p-8 mb-6 text-white">
          <h2 className="text-3xl font-bold">Admin Overview</h2>
          <p className="text-blue-100/80 mt-2">Manage users, jobs, and platform stats</p>
        </div> */}

        <div className="mb-6">
  <h2 className="text-2xl font-extrabold bg-gradient-to-r from-[#7C3AED] to-[#DB2777] bg-clip-text text-transparent capitalize">
    {activeTab.replace(/-/g, ' ')}
  </h2>
  <p className="text-sm text-gray-400">Manage and monitor your platform</p>
</div>

 {activeTab === 'stats' && stats && (
  <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
    {[
      {
        label: 'Total Users', value: stats.total_users, icon: '👥', grad: 'from-[#7C3AED] to-[#A78BFA]',
        onClick: () => { setRoleFilter(''); loadUsers(); },
      },
      {
        label: 'Job Seekers', value: stats.total_job_seekers, icon: '🧑‍🎓', grad: 'from-[#10B981] to-[#34D399]',
        onClick: () => { setRoleFilter('job_seeker'); loadUsers(); },
      },
      {
        label: 'Employers', value: stats.total_employers, icon: '🏢', grad: 'from-[#F59E0B] to-[#FBBF24]',
        onClick: () => { setRoleFilter('employer'); loadUsers(); },
      },
      {
        label: 'Total Jobs', value: stats.total_jobs, icon: '💼', grad: 'from-[#DB2777] to-[#F472B6]',
        onClick: () => { clearJobFilters(); setJobsSubTab('internal'); loadJobs(); },
      },
      {
        label: 'Active Jobs', value: stats.active_jobs, icon: '✅', grad: 'from-[#0EA5E9] to-[#38BDF8]',
        onClick: () => { clearJobFilters(); setJobStatusFilter('active'); setJobsSubTab('internal'); loadJobs(); },
      },
      {
        label: 'Total Applications', value: stats.total_applications, icon: '📄', grad: 'from-[#6366F1] to-[#818CF8]',
        onClick: () => {
          setApplicationSearch('');
          setApplicationStatusFilter('');
          setApplicationViewedFilter('');
          setApplicationResponseFilter('');
          setApplicationDateFrom('');
          setApplicationDateTo('');
          setAppCandidateFilter([]);
          setAppCompanyFilter([]);
          loadApplications();
        },
      },
    ].map((card) => (
      <button
        key={card.label}
        type="button"
        onClick={card.onClick}
        title={`View ${card.label}`}
        className={`text-left w-full rounded-2xl p-6 text-white shadow-lg bg-gradient-to-br ${card.grad} hover:-translate-y-1 hover:shadow-xl active:scale-[0.98] transition-all duration-200 relative overflow-hidden cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2`}
      >
        <span className="absolute -right-4 -top-4 text-7xl opacity-20">{card.icon}</span>
        <p className="text-4xl font-extrabold tracking-tight">{card.value}</p>
        <p className="text-sm font-semibold mt-1 uppercase tracking-wide text-white/90">{card.label}</p>
      </button>
    ))}
  </div>
)}

                {activeTab === 'users' && (
          <div className="space-y-4">
    <div className="bg-white rounded-2xl shadow-sm border border-purple-100 p-3">
  <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
    <div className="relative flex-1 min-w-[220px]">
      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm pointer-events-none">🔍</span>
      <input
        type="text"
        value={userSearch}
        onChange={(e) => setUserSearch(e.target.value)}
        placeholder="Search by name, email, or phone..."
        className={FILTER_INPUT_CLS + ' pl-10 pr-10 py-2'}
      />
      {userSearch && (
        <button type="button" onClick={() => setUserSearch('')}
          className="absolute right-3 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full text-gray-400 hover:bg-purple-50 hover:text-purple-600"
          aria-label="Clear search">×</button>
      )}
    </div>

    <button
      type="button"
      onClick={() => setShowUserFilters((s) => !s)}
      className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition ${
        showUserFilters || userActiveFilterCount > 0
          ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white border-transparent shadow-sm'
          : 'bg-white text-purple-700 border-purple-200 hover:border-purple-400'
      }`}
    >
      ⚙ Filters
      {userActiveFilterCount > 0 && (
        <span className="bg-white text-purple-600 text-[11px] font-bold h-5 min-w-[1.25rem] px-1 rounded-full flex items-center justify-center">
          {userActiveFilterCount}
        </span>
      )}
      <span className="text-[10px]">{showUserFilters ? '▲' : '▼'}</span>
    </button>

    {userWrongLocCount > 0 && (
      <button
        type="button"
        onClick={() => { setUserWrongLocOnly((v) => !v); setUserNoLocOnly(false); }}
        className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition ${
          userWrongLocOnly
            ? 'bg-amber-500 text-white border-transparent shadow-sm'
            : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
        }`}
      >
        ⚠ Wrong location
        <span className="bg-white text-amber-700 text-[11px] font-bold h-5 min-w-[1.25rem] px-1 rounded-full flex items-center justify-center">
          {userWrongLocCount}
        </span>
      </button>
    )}

    {userNoLocCount > 0 && (
      <button
        type="button"
        onClick={() => { setUserNoLocOnly((v) => !v); setUserWrongLocOnly(false); }}
        className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition ${
          userNoLocOnly
            ? 'bg-rose-500 text-white border-transparent shadow-sm'
            : 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100'
        }`}
      >
        📍 No location
        <span className="bg-white text-rose-700 text-[11px] font-bold h-5 min-w-[1.25rem] px-1 rounded-full flex items-center justify-center">
          {userNoLocCount}
        </span>
      </button>
    )}

    {userHasActiveFilters && (
      <button
        type="button"
        onClick={clearUserFilters}
        className="text-sm font-semibold px-4 py-2 rounded-full bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-100 transition"
      >
        ✕ Clear all
      </button>
    )}
  </div>

  {showUserFilters && (
    <div className="mt-3 pt-3 border-t border-purple-50">
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
        <div>
          <label className={FILTER_LABEL_CLS}>Location</label>
          <MultiSelectDropdown values={userLocationFilter} onChange={setUserLocationFilter} options={userLocationOptions} allLabel="All Locations" />
        </div>
        <div>
          <label className={FILTER_LABEL_CLS}>State</label>
          <MultiSelectDropdown values={userStateFilter} onChange={setUserStateFilter} options={userStateOptions} allLabel="All States" />
        </div>
        <div>
          <label className={FILTER_LABEL_CLS}>Company (employers)</label>
          <MultiSelectDropdown values={userCompanyFilter} onChange={setUserCompanyFilter} options={userCompanyOptions} allLabel="All Companies" />
        </div>
        <div>
          <label className={FILTER_LABEL_CLS}>Assigned Agent</label>
          <MultiSelectDropdown values={userAgentFilter} onChange={setUserAgentFilter} options={userAgentOptions} allLabel="All Agents" />
        </div>
        <div>
          <label className={FILTER_LABEL_CLS}>Status</label>
          <div className="relative">
            <select value={userStatusFilter} onChange={(e) => setUserStatusFilter(e.target.value)} className={FILTER_SELECT_CLS}>
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="banned">Banned</option>
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-purple-500 text-xs">▼</span>
          </div>
        </div>
        <div>
          <label className={FILTER_LABEL_CLS}>Source</label>
          <div className="relative">
            <select value={userSourceFilter} onChange={(e) => setUserSourceFilter(e.target.value)} className={FILTER_SELECT_CLS}>
              <option value="">All</option>
              <option value="agent">Added by agent</option>
              <option value="direct">Direct register</option>
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-purple-500 text-xs">▼</span>
          </div>
        </div>
        <div>
          <label className={FILTER_LABEL_CLS}>Verification</label>
          <div className="relative">
            <select value={userVerifiedFilter} onChange={(e) => setUserVerifiedFilter(e.target.value)} className={FILTER_SELECT_CLS}>
              <option value="">All</option>
              <option value="verified">Verified</option>
              <option value="verified_posted">Verified, job posted</option>
              <option value="verified_not_posted">Verified, no job posted</option>
              <option value="not_verified">Not verified</option>
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-purple-500 text-xs">▼</span>
          </div>
        </div>

                <div>
          <label className={FILTER_LABEL_CLS}>Merge status (employers)</label>
          <div className="relative">
            <select value={userMergeFilter} onChange={(e) => setUserMergeFilter(e.target.value)} className={FILTER_SELECT_CLS}>
              <option value="">All</option>
              <option value="merged">Merged (second accounts)</option>
              <option value="has_merged">Has merged accounts (main)</option>
              <option value="not_merged">Not merged</option>
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-purple-500 text-xs">▼</span>
          </div>
        </div>

        <div>
          <label className={FILTER_LABEL_CLS}>Profile (job seekers)</label>
          <div className="relative">
            <select value={userProfileFilter} onChange={(e) => setUserProfileFilter(e.target.value)} className={FILTER_SELECT_CLS}>
              <option value="">All</option>
              <option value="complete">Completed (100%)</option>
              <option value="incomplete">Not completed</option>
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-purple-500 text-xs">▼</span>
          </div>
        </div>
        <div>
          <label className={FILTER_LABEL_CLS}>Completion %</label>
          <div className="relative">
            <select value={userCompletionFilter} onChange={(e) => setUserCompletionFilter(e.target.value)} className={FILTER_SELECT_CLS}>
              <option value="">All</option>
              {Object.entries(COMPLETION_RANGES).map(([key, r]) => (
                <option key={key} value={key}>{r.label}</option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-purple-500 text-xs">▼</span>
          </div>
        </div>
        <div>
          <label className={FILTER_LABEL_CLS}>Created From</label>
          <input type="date" value={userDateFrom} onChange={(e) => setUserDateFrom(e.target.value)} className={FILTER_INPUT_CLS} />
        </div>
        <div>
          <label className={FILTER_LABEL_CLS}>Created To</label>
          <input type="date" value={userDateTo} onChange={(e) => setUserDateTo(e.target.value)} className={FILTER_INPUT_CLS} />
        </div>
        <div className="flex items-end">
          <label
            className={`flex items-center gap-2 text-xs cursor-pointer select-none px-3 py-1.5 rounded-full border transition w-full ${
              userNoEmailOnly
                ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white border-transparent shadow-sm'
                : 'bg-white text-gray-600 border-purple-200 hover:border-purple-400'
            }`}
          >
            <input type="checkbox" checked={userNoEmailOnly} onChange={(e) => setUserNoEmailOnly(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 accent-purple-600" />
            No email only
          </label>
        </div>
      </div>
    </div>
  )}

  <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
    <div className="flex flex-wrap gap-2">
      {[
        ...userLocationFilter.map((v) => ({ k: 'l' + v, label: '📍 ' + v, remove: () => setUserLocationFilter(userLocationFilter.filter((x) => x !== v)) })),
        ...userStateFilter.map((v) => ({ k: 's' + v, label: v, remove: () => setUserStateFilter(userStateFilter.filter((x) => x !== v)) })),
        ...userAgentFilter.map((v) => ({ k: 'a' + v, label: 'Agent: ' + v, remove: () => setUserAgentFilter(userAgentFilter.filter((x) => x !== v)) })),
        ...userCompanyFilter.map((v) => ({ k: 'c' + v, label: v, remove: () => setUserCompanyFilter(userCompanyFilter.filter((x) => x !== v)) })),
        ...(userStatusFilter ? [{ k: 'st', label: 'Status: ' + userStatusFilter, remove: () => setUserStatusFilter('') }] : []),
        ...(userSourceFilter ? [{ k: 'src', label: userSourceFilter === 'agent' ? 'Added by agent' : 'Direct register', remove: () => setUserSourceFilter('') }] : []),
        ...(userVerifiedFilter ? [{ k: 'vf', label: {
          verified: 'Verified',
          verified_posted: 'Verified, job posted',
          verified_not_posted: 'Verified, no job posted',
          not_verified: 'Not verified',
        }[userVerifiedFilter], remove: () => setUserVerifiedFilter('') }] : []),
                ...(userMergeFilter ? [{ k: 'mg', label: { merged: 'Merged accounts', has_merged: 'Has merged accounts', not_merged: 'Not merged' }[userMergeFilter], remove: () => setUserMergeFilter('') }] : []),
        ...(userProfileFilter ? [{ k: 'pf', label: userProfileFilter === 'complete' ? 'Profile completed' : 'Profile not completed', remove: () => setUserProfileFilter('') }] : []),
        ...(userCompletionFilter ? [{ k: 'pc', label: 'Completion ' + COMPLETION_RANGES[userCompletionFilter].label, remove: () => setUserCompletionFilter('') }] : []),
        ...(userDateFrom ? [{ k: 'df', label: 'From ' + fmtDate(userDateFrom), remove: () => setUserDateFrom('') }] : []),
        ...(userDateTo ? [{ k: 'dt', label: 'To ' + fmtDate(userDateTo), remove: () => setUserDateTo('') }] : []),
        ...(userNoEmailOnly ? [{ k: 'ne', label: 'No email', remove: () => setUserNoEmailOnly(false) }] : []),
        ...(userWrongLocOnly ? [{ k: 'wl', label: 'Wrong location', remove: () => setUserWrongLocOnly(false) }] : []),
        ...(userNoLocOnly ? [{ k: 'nl', label: 'No location', remove: () => setUserNoLocOnly(false) }] : []),
      ].map((c) => (
        <span key={c.k} className="inline-flex items-center gap-1.5 text-xs font-medium pl-3 pr-1.5 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-100">
          {c.label}
          <button type="button" onClick={c.remove} className="h-4 w-4 rounded-full hover:bg-purple-200 leading-none" aria-label="Remove filter">×</button>
        </span>
      ))}
    </div>
    <p className="text-xs text-gray-400">
      Showing <span className="font-semibold text-purple-600">{filteredUsers.length}</span> of {users.length} users
    </p>
  </div>
</div>

<div className="flex gap-1 border-b border-gray-100">
  {[
    { value: '', label: 'All', count: users.length },
    { value: 'job_seeker', label: 'Candidates', count: users.filter((u) => u.role === 'job_seeker').length },
    { value: 'employer', label: 'Employers', count: users.filter((u) => u.role === 'employer').length },
    { value: 'admin', label: 'Admins', count: users.filter((u) => u.role === 'admin').length }
  ].map((t) => (
    <button
      key={t.value}
      type="button"
      onClick={() => {
  setRoleFilter(t.value);
  setUserProfileFilter('');
  setUserCompletionFilter('');
  setUserCompanyFilter([]);
}}
      className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition inline-flex items-center gap-2 ${
        roleFilter === t.value
          ? 'border-purple-600 text-purple-600'
          : 'border-transparent text-gray-400 hover:text-gray-600'
      }`}
    >
      {t.label}
      <span
        className={`text-[11px] font-bold h-5 min-w-[1.25rem] px-1.5 rounded-full flex items-center justify-center ${
          roleFilter === t.value ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-500'
        }`}
      >
        {t.count}
      </span>
    </button>
  ))}
</div>

              {selectedUserIds.size > 0 && (
  <div className="flex items-center gap-3 bg-purple-50 border border-purple-100 rounded-xl px-4 py-3 flex-wrap">
    {canMerge && (
      <button
        onClick={() => setMergeOpen(true)}
        disabled={bulkUserLoading}
        className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-gradient-to-r from-purple-600 to-pink-500 text-white hover:opacity-90 disabled:opacity-50"
      >
        🔗 Merge accounts
      </button>
    )}
    <button
      onClick={() => handleBulkUserStatus('active')}
      disabled={bulkUserLoading}
      className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#10B981] text-white hover:opacity-90 disabled:opacity-50"
    >
      {bulkUserLoading ? 'Working…' : 'Enable selected'}
    </button>
    <button
      onClick={() => handleBulkUserStatus('suspended')}
      disabled={bulkUserLoading}
      className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-amber-600 text-white hover:opacity-90 disabled:opacity-50"
    >
      Disable selected
    </button>
    <button
      onClick={() => setSelectedUserIds(new Set())}
      disabled={bulkUserLoading}
      className="text-xs font-semibold text-gray-500 hover:text-gray-700"
    >
      Clear
    </button>
  
  </div>
)}

            <PaginationBar
              total={sortedUsers.length}
              page={userPage}
              pageSize={userPageSize}
              onPage={setUserPage}
              onPageSize={setUserPageSize}
            />

            <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 overflow-x-auto">
              <table className="w-full text-sm min-w-[1150px]">



          <thead className="bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 text-[11px] tracking-wider uppercase border-b border-gray-100">
  <tr>
    <th className="text-left px-4 py-3 font-semibold w-8">
      <input type="checkbox" checked={allUsersSelected()} onChange={toggleSelectAllUsers} />
    </th>

    {[
      { key: 'name', label: 'Name' },
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' },
      { key: 'role', label: 'Role' },
      { key: 'location', label: 'Location' },
      { key: 'created', label: 'Created' },
      { key: 'applications', label: 'Applications' },
      { key: 'job_posts', label: 'Job Posts' },
      { key: 'agent', label: 'Assigned Agent' },
      { key: 'status', label: 'Status' },
    ].map((col) => (
      <SortTh key={col.key} label={col.label} sortKey={col.key} sort={userSort} onSort={toggleUserSort} />
    ))}

    <th className="text-left px-4 py-3 font-semibold sticky right-0 z-20 bg-gray-50 border-l border-gray-100 shadow-[-6px_0_8px_-4px_rgba(0,0,0,0.08)]">Actions</th>
  </tr>
</thead>
                <tbody>
                  {pagedUsers.map((u) => (
                    <tr key={u.id} className="border-t border-gray-50 hover:bg-purple-50/70 transition-colors">
                      <td className="px-4 py-3">
  <input
    type="checkbox"
    checked={selectedUserIds.has(u.id)}
    disabled={u.role === 'admin' || isMergedAway(u)}
    onChange={() => toggleUserSelect(u.id)}
  />
</td>
<td className="px-4 py-3">
  <button
    type="button"
    onClick={() => viewUserDetails(u.id)}
    className="text-left font-medium text-[#7C3AED] hover:underline"
  >
    {isAgentAdded(u) && (
      <span title="Added by agent" className="mr-1 font-bold text-pink-600">(A)</span>
    )}
    {u.name || '—'}
    {isMergedAway(u) && (
      <span title={`Merged into ${mergedIntoName(u) || 'main account'}`} className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">MERGED</span>
    )}
    {hasMerged(u) && (
      <span title={`${mergedCountById[u.id]} account(s) merged into this one`} className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-600">+{mergedCountById[u.id]}</span>
    )}
  </button>
</td>
<td className="px-4 py-3 max-w-[200px] truncate" title={u.email}>{u.email}</td>
<td className="px-4 py-3">{u.phone || '—'}</td>
<td className="px-4 py-3 capitalize">{u.role.replace('_', ' ')}</td><td className="px-4 py-3">
  {u.city ? (u.state ? `${u.city}, ${u.state}` : u.city) : '—'}
</td>
<td className="px-4 py-3 text-xs text-gray-500">{fmtDate(u.created_at)}</td>
<td className="px-4 py-3">
  {u.role === 'job_seeker' ? (
    <button
      type="button"
      onClick={() => openUserAppsModal(u)}
      disabled={!Number(u.job_applications_count)}
      className="min-w-[2rem] text-xs font-semibold px-2 py-1 rounded-full bg-purple-50 text-[#7C3AED] hover:bg-purple-100 disabled:bg-transparent disabled:text-gray-400 disabled:cursor-default"
    >
      {u.job_applications_count ?? 0}
    </button>
  ) : u.role === 'employer' ? (
    <button
      type="button"
      title="Applications received on this employer's jobs"
      onClick={() => openEmployerAppsModal(u)}
      disabled={!Number(u.received_applications_count)}
      className="min-w-[2rem] text-xs font-semibold px-2 py-1 rounded-full bg-pink-50 text-pink-600 hover:bg-pink-100 disabled:bg-transparent disabled:text-gray-400 disabled:cursor-default"
    >
      {u.received_applications_count ?? 0}
    </button>
  ) : '—'}
</td>
<td className="px-4 py-3">
  {u.role === 'employer' ? (
    <button
      type="button"
      onClick={() => openUserJobsModal(u)}
      disabled={!Number(u.job_postings_count)}
      className="min-w-[2rem] text-xs font-semibold px-2 py-1 rounded-full bg-purple-50 text-[#7C3AED] hover:bg-purple-100 disabled:bg-transparent disabled:text-gray-400 disabled:cursor-default"
    >
      {u.job_postings_count ?? 0}
    </button>
  ) : '—'}
</td>
                                      <td className="px-4 py-3">
  {u.role === 'employer' && !u.employer_id ? (
                          <select
                            value={u.agent_id || ''}
                            onChange={(e) => handleAssignAgent(u, e.target.value)}
                            className="border rounded-lg px-2 py-1 text-xs"
                          >
                            <option value="">Unassigned</option>
                            {agents.map((a) => (
                              <option key={a.id} value={a.id}>{a.name}</option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </td>
                     <td className="px-4 py-3">
  <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${USER_STATUS_STYLES[userStatus(u)] || 'bg-gray-100 text-gray-500'}`}>
    {userStatus(u)}
  </span>
</td>
<td className="px-4 py-3 sticky right-0 z-10 bg-white border-l border-gray-100 shadow-[-6px_0_8px_-4px_rgba(0,0,0,0.08)]">
  <RowActions
    phone={u.phone}
    onView={() => viewUserDetails(u.id)}
    extra={u.role !== 'admin' && (
      <>

      {(u.role === 'job_seeker' || u.role === 'employer') && !isMergedAway(u) && (
  <button
    onClick={() => handleLoginAsUser(u)}
    disabled={impersonatingUserId === u.id || userStatus(u) !== 'active'}
    className={ACT_EDIT}
  >
    {impersonatingUserId === u.id ? 'Opening…' : '🔓 Login as'}
  </button>
)}

        {isMergedAway(u) && (
          <button onClick={() => handleUnmerge(u)} disabled={togglingUserId === u.id} className={ACT_EDIT}>
            {togglingUserId === u.id ? 'Working…' : '↩ Unmerge'}
          </button>
        )}
        {!isMergedAway(u) && (
          <button
            onClick={() => handleToggleUserStatus(u)}
            disabled={togglingUserId === u.id}
            className={userStatus(u) === 'active' ? ACT_AMBER : ACT_CALL}
          >
            {togglingUserId === u.id ? 'Updating…' : userStatus(u) === 'active' ? '⏸ Disable' : '▶ Enable'}
          </button>
        )}
      </>
    )}
    onDelete={u.role !== 'admin' ? () => handleDeleteUser(u.id) : undefined}
  />
</td>
                    </tr>
                  ))}
                  {filteredUsers.length === 0 && (
                    <tr>
                      <td colSpan={12} className="px-4 py-6 text-center text-gray-400">
                        No users found for this filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
                       </div>

              <PaginationBar
     className="mt-3"
     total={sortedUsers.length}
     page={userPage}
     pageSize={userPageSize}
     onPage={setUserPage}
     onPageSize={setUserPageSize}
   />
          </div>
        )}

        {activeTab === 'agents' && (
          <div className="space-y-4">
            {/* Create Agent form */}
            <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 p-6">
              <h3 className="font-bold text-[#0D1B3D] text-lg mb-3">Add New Agent</h3>
              {agentError && <p className="text-sm text-red-600 mb-2">{agentError}</p>}
              {agentSuccess && <p className="text-sm text-green-600 mb-2">{agentSuccess}</p>}
              <form onSubmit={createAgent} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  type="text"
                  name="name"
                  placeholder="Full name"
                  value={agentForm.name}
                  onChange={handleAgentFormChange}
                  required
                  className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={agentForm.email}
                  onChange={handleAgentFormChange}
                  required
                  className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                />
                <input
                  type="text"
                  name="phone"
                  placeholder="10-digit mobile number"
                  value={agentForm.phone}
                  onChange={handleAgentFormChange}
                  required
                  className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="Temporary password (min 8 chars)"
                  value={agentForm.password}
                  onChange={handleAgentFormChange}
                  required
                  minLength={8}
                  className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                />
                <button
                  type="submit"
                  disabled={agentSaving}
                  className="sm:col-span-2 bg-gradient-to-r from-purple-600 to-pink-500 text-white py-2 rounded-lg font-semibold text-sm hover:opacity-90 disabled:opacity-50"
                >
                  {agentSaving ? 'Creating...' : 'Create Agent'}
                </button>
              </form>
            </div>

                      {/* Agents search */}
            <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 p-4">
              <input
                type="text"
                value={agentSearch}
                onChange={(e) => setAgentSearch(e.target.value)}
                placeholder="Search by name, email, or phone..."
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-purple-400"
              />
            </div>

            {/* Agents list */}
            <p className="text-xs text-gray-400 px-1">Showing {filteredAgents.length} of {agents.length} agents</p>

            <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 overflow-x-auto">
              <table className="w-full text-sm">
               <thead className="bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 text-[11px] tracking-wider uppercase border-b border-gray-100">
  <tr>
    {[
      { key: 'name', label: 'Name' },
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' },
      { key: 'status', label: 'Status' },
    ].map((col) => (
      <SortTh key={col.key} label={col.label} sortKey={col.key} sort={agentSort} onSort={toggleAgentSort} />
    ))}
    <th className="text-left px-4 py-3 font-semibold sticky right-0 bg-purple-50">Actions</th>
  </tr>
</thead>
                <tbody>
                  {sortedAgents.map((a) => (
                    <tr key={a.id} className="border-t border-gray-50 hover:bg-purple-50/70 transition-colors">
                      {editingAgentId === a.id ? (
                        <>
                          <td className="px-4 py-2">
                            <input
                              type="text"
                              name="name"
                              value={editAgentForm.name}
                              onChange={handleEditAgentFormChange}
                              className="border rounded-lg px-2 py-1 text-xs w-full"
                            />
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="email"
                              name="email"
                              value={editAgentForm.email}
                              onChange={handleEditAgentFormChange}
                              className="border rounded-lg px-2 py-1 text-xs w-full"
                            />
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="text"
                              name="phone"
                              value={editAgentForm.phone}
                              onChange={handleEditAgentFormChange}
                              className="border rounded-lg px-2 py-1 text-xs w-full"
                            />
                          </td>

                          <td className="px-4 py-2">
  <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${
    agentStatus(a) === 'active' ? 'bg-[#ECFDF5] text-[#10B981]' : 'bg-amber-50 text-amber-600'
  }`}>
    {agentStatus(a)}
  </span>
</td>

                          <td className="px-4 py-2">
                            <div className="flex flex-col gap-1">
                              <div className="flex gap-3">
                                <button
                                  onClick={() => saveEditAgent(a.id)}
                                  disabled={editAgentSaving}
                                  className="text-[#10B981] hover:underline text-xs font-semibold disabled:opacity-50"
                                >
                                  {editAgentSaving ? 'Saving…' : 'Save'}
                                </button>
                                <button
                                  onClick={cancelEditAgent}
                                  className="text-gray-500 hover:underline text-xs font-semibold"
                                >
                                  Cancel
                                </button>
                              </div>
                              {editAgentError && (
                                <p className="text-[10px] text-red-600">{editAgentError}</p>
                              )}
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-3">{a.name}</td>
                          <td className="px-4 py-3">{a.email}</td>
                        <td className="px-4 py-3">{a.phone || '—'}</td>

<td className="px-4 py-3">
  <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${
    agentStatus(a) === 'active' ? 'bg-[#ECFDF5] text-[#10B981]' : 'bg-amber-50 text-amber-600'
  }`}>
    {agentStatus(a)}
  </span>
</td>

<td className="px-4 py-3">
  <RowActions
    phone={a.phone}
    onEdit={() => startEditAgent(a)}
    deleting={deletingAgentId === a.id}
    onDelete={() => deleteAgent(a)}
    extra={
      <>
        <button onClick={() => handleToggleAgentStatus(a)} disabled={togglingAgentId === a.id}
          className={agentStatus(a) === 'active' ? ACT_AMBER : ACT_CALL}>
          {togglingAgentId === a.id ? 'Updating…' : agentStatus(a) === 'active' ? '⏸ Disable' : '▶ Enable'}
        </button>
        <button onClick={() => handleLoginAsAgent(a)} disabled={impersonatingAgentId === a.id || agentStatus(a) !== 'active'} className={ACT_EDIT}>
          {impersonatingAgentId === a.id ? 'Opening…' : '🔓 Login as'}
        </button>
        <button onClick={() => openPasswordModal(a)} className={ACT_GRAY}>🔑 Password</button>
      </>
    }
  />
</td>
                        </>
                      )}
                    </tr>
                  ))}
                  {filteredAgents.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-6 text-center text-gray-400">
                        {agents.length === 0 ? 'No agents created yet.' : 'No agents match your search.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

   {activeTab === 'verification' && (
  verificationSelectedId ? (
    <VerificationPanelDetail
      apiPrefix="/admin/verification"
      employerId={verificationSelectedId}
      onBack={() => setVerificationSelectedId(null)}
      allowRedecide
    />
  ) : (
    <VerificationPanelList
      apiPrefix="/admin/verification"
      onSelectEmployer={(id) => setVerificationSelectedId(id)}
      allowRedecide
    />
  )
)}

        {activeTab === 'analytics' && (
          <div className="space-y-4">
            {analyticsLoading && (
              <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 p-10 text-center text-gray-400">
                Loading analytics...
              </div>
            )}

            {analyticsError && (
              <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 p-6 text-center text-red-600 text-sm">
                {analyticsError}
              </div>
            )}

            {!analyticsLoading && !analyticsError && analytics && (
              <>
                {/* Overview cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {[
                    { label: 'Impressions', value: analytics.totals.impressions, color: 'text-[#7C3AED]' },
                    { label: 'Views', value: analytics.totals.views, color: 'text-[#10B981]' },
                    { label: 'Saves', value: analytics.totals.saves, color: 'text-[#F59E0B]' },
                    { label: 'Applications', value: analytics.totals.applications, color: 'text-[#7C3AED]' },
                    { label: 'Skips', value: analytics.totals.skips, color: 'text-red-500' },
                    {
                      label: 'Avg. Time Spent',
                      value: formatSeconds(analytics.totals.avg_time_spent_seconds),
                      color: 'text-[#0D1B3D]',
                    },
                    {
                      label: 'Click-Through Rate',
                      value: `${ctr(analytics.totals.views, analytics.totals.impressions)}%`,
                      color: 'text-[#10B981]',
                    },
                    {
                      label: 'Application Conversion',
                      value: `${conversionRate(analytics.totals.applications, analytics.totals.views)}%`,
                      color: 'text-[#F59E0B]',
                    },
                  ].map((card) => (
                    <div
                      key={card.label}
                      className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 hover:shadow-md transition-shadow duration-200 p-6 relative overflow-hidden"
                    >
                      <span className={`absolute top-0 left-0 w-full h-1 ${card.color.replace('text-', 'bg-')}`}></span>
                      <p className={`text-2xl font-bold ${card.color}`}>{card.value}</p>
                      <p className="text-sm font-medium text-gray-500 mt-1">{card.label}</p>
                    </div>
                  ))}
                </div>

                {/* Filters */}
                <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 p-4 flex flex-col sm:flex-row gap-3">
                  <input
                    type="text"
                    value={jobFilter}
                    onChange={(e) => setJobFilter(e.target.value)}
                    placeholder="Filter by job title..."
                    className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-purple-400"
                  />
                  <div className="flex-1">
                    <MultiSelectDropdown
                      values={companyFilter}
                      onChange={setCompanyFilter}
                      options={companyOptions}
                      allLabel="All Companies"
                    />
                  </div>
                  {(jobFilter || companyFilter.length > 0) && (
                    <button
                      type="button"
                      onClick={() => { setJobFilter(''); setCompanyFilter([]); }}
                      className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-500 hover:bg-gray-100"
                    >
                      Clear
                    </button>
                  )}
                </div>

                {/* Per-job breakdown */}
                <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 overflow-hidden">
                  <div className="p-4 border-b flex items-center justify-between">
                    <h3 className="font-bold text-[#0D1B3D]">Per-Job Breakdown</h3>
                    <span className="text-xs text-gray-500">
                      Showing {sortedJobs.length} of {analytics.jobs.length} jobs
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 text-[11px] tracking-wider uppercase border-b border-gray-100">
                        <tr>
                          <th className="text-left px-4 py-3 font-semibold">Job</th>
                          {[
                            { key: 'impressions', label: 'Impressions' },
                            { key: 'views', label: 'Views' },
                            { key: 'saves', label: 'Saves' },
                            { key: 'applications', label: 'Applications' },
                            { key: 'skips', label: 'Skips' },
                            { key: 'avg_time_spent_seconds', label: 'Avg. Time' },
                            { key: 'ctr', label: 'CTR' },
                            { key: 'conversion', label: 'Conversion' },
                          ].map((col) => (
                            <th
                              key={col.key}
                              onClick={() => toggleSort(col.key)}
                              className="text-left px-4 py-3 font-semibold cursor-pointer select-none hover:text-[#7C3AED]"
                            >
                              {col.label}
                              {analyticsSort.key === col.key && (analyticsSort.dir === 'asc' ? ' ▲' : ' ▼')}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sortedJobs.map((job) => (
                          <tr key={job.id} className="border-t border-gray-50 hover:bg-purple-50/70 transition-colors">
                            <td className="px-4 py-3">
                              <p className="font-medium">{job.title}</p>
                              <p className="text-xs text-gray-500">{job.company_name}</p>
                            </td>
                            <td className="px-4 py-3">{job.impressions}</td>
                            <td className="px-4 py-3">{job.views}</td>
                            <td className="px-4 py-3">{job.saves}</td>
                            <td className="px-4 py-3">{job.applications}</td>
                            <td className="px-4 py-3">{job.skips}</td>
                            <td className="px-4 py-3">{formatSeconds(job.avg_time_spent_seconds)}</td>
                            <td className="px-4 py-3">{ctr(job.views, job.impressions)}%</td>
                            <td className="px-4 py-3">{conversionRate(job.applications, job.views)}%</td>
                          </tr>
                        ))}
                        {sortedJobs.length === 0 && (
                          <tr>
                            <td colSpan={9} className="px-4 py-6 text-center text-gray-400">
                              {analytics.jobs.length === 0
                                ? 'No job activity recorded yet.'
                                : 'No jobs match the current filters.'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

             {activeTab === 'jobs' && (
  <div className="space-y-4">
    {/* Search + advanced filters */}
    <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 p-4 space-y-3">
      <input
        type="text"
        value={jobsSubTab === 'internal' ? jobSearch : aggJobSearch}
        onChange={(e) =>
          jobsSubTab === 'internal' ? setJobSearch(e.target.value) : setAggJobSearch(e.target.value)
        }
        placeholder={jobsSubTab === 'internal' ? 'Search by title, company, or employer...' : 'Search by title, company, or location...'}
        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-purple-400"
      />

      {jobsSubTab === 'internal' ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MultiSelectDropdown
            values={jobCityFilter}
            onChange={setJobCityFilter}
            options={jobCityOptions}
            allLabel="All Cities"
          />
          <MultiSelectDropdown
  values={jobEmployerFilter}
  onChange={setJobEmployerFilter}
  options={jobEmployerOptions}
  allLabel="All Employers"
/>
          <select
            value={jobTypeFilter}
            onChange={(e) => setJobTypeFilter(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
          >
            {ADMIN_JOB_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            value={jobStatusFilter}
            onChange={(e) => setJobStatusFilter(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
          >
            {ADMIN_JOB_STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
                    <select
            value={jobPostedByFilter}
            onChange={(e) => setJobPostedByFilter(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
          >
            <option value="">Posted by: All</option>
            <option value="agent">Posted by agent</option>
            <option value="employer">Posted by employer</option>
          </select>
                    <select
            value={jobAgentFilter}
            onChange={(e) => setJobAgentFilter(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
          >
            <option value="">Agent: All</option>
            {jobAgentOptions.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
          <select
            title="Show only jobs that have at least one candidate at this stage" value={jobFunnelFilter}
            onChange={(e) => setJobFunnelFilter(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
          >
            <option value="">Funnel stage: All</option>
            <option value="impressions">Impressions</option>
            <option value="clicks">Clicks</option>
            <option value="applicants">Applicants</option>
            <option value="screening">Screening</option>
            <option value="shortlisted">Shortlisted</option>
            <option value="interview_round_1">Interview Round 1</option>
            <option value="interview_round_2">Interview Round 2</option>
            <option value="hr_round">HR Round</option>
            <option value="offer">Job Offered</option>
            <option value="joined">Joined</option>
            <option value="rejected">Rejected</option>
          </select>
          <div className="col-span-2 sm:col-span-3 flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-gray-400 uppercase">Posted</span>
            <input
              type="date"
              value={jobDateFrom}
              onChange={(e) => setJobDateFrom(e.target.value)}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent min-w-0 flex-1 sm:flex-none sm:w-44"
            />
            <span className="text-gray-400 text-sm">to</span>
            <input
              type="date"
              value={jobDateTo}
              onChange={(e) => setJobDateTo(e.target.value)}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent min-w-0 flex-1 sm:flex-none sm:w-44"
            />
          </div>
        </div>

      ) : (
                <div className="grid grid-cols-2 gap-3">
          <MultiSelectDropdown
            values={aggCityFilter}
            onChange={setAggCityFilter}
            options={jobCityOptions}
            allLabel="All Locations"
          />
          <select
            value={aggTypeFilter}
            onChange={(e) => setAggTypeFilter(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
          >
            {ADMIN_JOB_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
                   <div className="col-span-2 flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-gray-400 uppercase">Posted</span>
            <input
              type="date"
              value={aggDateFrom}
              onChange={(e) => setAggDateFrom(e.target.value)}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent min-w-0 flex-1 sm:flex-none sm:w-44"
            />
            <span className="text-gray-400 text-sm">to</span>
            <input
              type="date"
              value={aggDateTo}
              onChange={(e) => setAggDateTo(e.target.value)}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent min-w-0 flex-1 sm:flex-none sm:w-44"
            />
          </div>
        </div>
      )}

     
      {(jobsSubTab === 'internal'
        ? (jobSearch || jobCityFilter.length > 0 || jobEmployerFilter.length > 0 || jobTypeFilter || jobStatusFilter || jobDateFrom || jobDateTo || jobFunnelFilter || jobPostedByFilter || jobAgentFilter)
        : (aggJobSearch || aggCityFilter.length > 0 || aggTypeFilter || aggDateFrom || aggDateTo)) && (
        <button
          type="button"
          onClick={jobsSubTab === 'internal' ? clearJobFilters : clearAggJobFilters}
          className="text-xs font-semibold text-[#7C3AED] hover:underline"
        >
          Clear filters
        </button>
      )}
    </div>

    <div className="flex gap-1 border-b border-gray-100">
      <button
        onClick={() => setJobsSubTab('internal')}
        className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
          jobsSubTab === 'internal'
            ? 'border-purple-600 text-purple-600'
            : 'border-transparent text-gray-400 hover:text-gray-600'
        }`}
      >
        Employer Posted ({filteredInternalJobs.length})
      </button>
      <button
        onClick={() => setJobsSubTab('aggregated')}
        className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
          jobsSubTab === 'aggregated'
            ? 'border-purple-600 text-purple-600'
            : 'border-transparent text-gray-400 hover:text-gray-600'
        }`}
      >
        Aggregated ({filteredAggJobs.length})
      </button>
    </div>

    {jobsSubTab === 'internal' && selectedJobIds.size > 0 && (
      <div className="flex items-center gap-3 bg-purple-50 border border-purple-100 rounded-xl px-4 py-3 flex-wrap">
        <span className="text-sm text-[#7C3AED] font-medium">{selectedJobIds.size} selected</span>
        <select
          value={bulkJobStatus}
          onChange={(e) => setBulkJobStatus(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white"
        >
          <option value="">Set status…</option>
          {ADMIN_JOB_STATUS_OPTIONS.filter((o) => o.value).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <button
          onClick={handleBulkJobStatusApply}
          disabled={!bulkJobStatus || bulkJobApplying}
          className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:opacity-90 disabled:opacity-50"
        >
          {bulkJobApplying ? 'Applying…' : 'Apply'}
        </button>
        <button
          onClick={() => setSelectedJobIds(new Set())}
          className="text-sm text-gray-500 hover:text-gray-700"
        >
          Cancel
        </button>
      </div>
    )}

    {jobsSubTab === 'internal' ? (
  <>
  <PaginationBar
    className="mb-3"
    total={filteredInternalJobs.length}
    page={jobPage}
    pageSize={jobPageSize}
    onPage={setJobPage}
    onPageSize={setJobPageSize}
  />
  <div
    id="jobs-top-scroll"
    
        className="overflow-x-auto"
        style={{ height: 14 }}
        onScroll={(e) => {
          const m = document.getElementById('jobs-main-scroll');
          if (m && m.scrollLeft !== e.currentTarget.scrollLeft) m.scrollLeft = e.currentTarget.scrollLeft;
        }}
      >
        <div style={{ width: 2250, height: 1 }} />
      </div>
      <div
        id="jobs-main-scroll"
        className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 overflow-x-auto"
        onScroll={(e) => {
          const t = document.getElementById('jobs-top-scroll');
          if (t && t.scrollLeft !== e.currentTarget.scrollLeft) t.scrollLeft = e.currentTarget.scrollLeft;
        }}
      >
        <table className="w-full text-sm min-w-[2250px]">  <thead className="bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 text-[11px] tracking-wider uppercase border-b border-gray-100">
  <tr>
    <th className="text-left px-4 py-3 font-semibold w-8">
      {filteredInternalJobs.length > 0 && (
        <input
          type="checkbox"
          checked={allVisibleJobsSelected}
          onChange={toggleSelectAllJobs}
        />
      )}
    </th>

    {[
      { key: 'title', label: 'Title' },
      { key: 'company', label: 'Company' },
      { key: 'employer', label: 'Employer' },
      { key: 'location', label: 'Location' },
      { key: 'type', label: 'Type' },
      { key: 'salary', label: 'Salary' },
    ].map((col) => (
      <SortTh key={col.key} label={col.label} sortKey={col.key} sort={jobSort} onSort={toggleJobSort} />
    ))}

    {JOB_METRIC_COLUMNS.map((col) => (
      <SortTh key={col.key} label={col.label} sortKey={col.key} sort={jobSort} onSort={toggleJobSort} />
    ))}

    <SortTh label="Status" sortKey="status" sort={jobSort} onSort={toggleJobSort} />
    <SortTh label="Posted" sortKey="posted" sort={jobSort} onSort={toggleJobSort} />

    <SortTh label="Posted By" sortKey="postedBy" sort={jobSort} onSort={toggleJobSort} />

    <th className="text-left px-4 py-3 font-semibold sticky right-0 z-20 bg-gray-50 border-l border-gray-100 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.08)]">Actions</th>
  </tr>
</thead>
                   <tbody>
            {pagedInternalJobs.map((j) => (
              <tr key={j.id} className="border-t border-gray-50 hover:bg-purple-50/70 transition-colors">
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedJobIds.has(j.id)}
                    onChange={() => toggleJobSelect(j.id)}
                  />
                </td>
                <td className="px-4 py-3">{j.title}</td>
                <td className="px-4 py-3">
  {(j.employer?.id ?? j.employer_id) ? (
    <button
      type="button"
      onClick={() => viewUserDetails(j.employer?.id ?? j.employer_id)}
      className="text-left text-[#7C3AED] hover:underline"
    >
      {j.company_name}
    </button>
  ) : (
    j.company_name
  )}
</td>
                <td className="px-4 py-3">{j.employer?.name || '—'}</td>
                <td className="px-4 py-3">{j.location || '—'}</td>
                <td className="px-4 py-3 capitalize">{(j.job_type || '').replace(/_/g, ' ') || '—'}</td>
                <td className="px-4 py-3">{fmtINR(j.salary_min)} – {fmtINR(j.salary_max)}</td>
                {JOB_METRIC_COLUMNS.map((col) => {
                  const val = jobMetricValue(j, col.key);
                  if (col.key === 'openings') {
                    return (
                      <td key={col.key} className="px-4 py-3 text-center text-xs font-semibold text-gray-700">
                        {j.openings ?? '—'}
                      </td>
                    );
                  }
                  return (
                    <td key={col.key} className="px-4 py-3 text-center">
                      <button
                        type="button"
                        onClick={() => openMetricModal(j, col)}
                        disabled={!Number(val)}
                        className="min-w-[2rem] text-xs font-semibold px-2 py-1 rounded-full bg-purple-50 text-[#7C3AED] hover:bg-purple-100 disabled:bg-transparent disabled:text-gray-400 disabled:cursor-default"
                      >
                        {val}
                      </button>
                    </td>
                  );
                })}
                <td className="px-4 py-3">
                  <select
                    value={j.status || ''}
                    onChange={(e) => handleJobStatusChange(j.id, e.target.value)}
                    className={`text-xs font-medium rounded-full px-2 py-1 border-none capitalize cursor-pointer ${ADMIN_STATUS_STYLES[j.status] || 'bg-gray-50 text-gray-500'}`}
                  >
                    {ADMIN_JOB_STATUS_OPTIONS.filter((o) => o.value).map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                                    {j.status === 'closed' && j.close_reason && (
                    <button
                      type="button"
                      onClick={() => setCloseReasonJob(j)}
                      className="mt-1 block text-[10px] font-semibold text-purple-600 hover:underline"
                    >
                      View reason
                    </button>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-gray-500">{fmtDate(j.created_at)}</td>
                                <td className="px-4 py-3 text-xs">
                  {jobAgentName(j) ? (
                    <span className="font-semibold px-2 py-1 rounded-full bg-pink-50 text-pink-600">{jobAgentName(j)}</span>
                  ) : (
                    <span className="text-gray-400">Employer</span>
                  )}
                </td>
  <td className="px-4 py-3 sticky right-0 z-10 bg-white border-l border-gray-100 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.08)]">
  <RowActions
    phone={j.employer?.phone}
    onView={() => setViewJobModal(j)}
    onEdit={() => setEditJobModal({ ...j })}
    deleting={deletingJobId === j.id}
    onDelete={() => deleteJob(j)}
  />
</td>
              </tr>
            ))}
            {filteredInternalJobs.length === 0 && (
              <tr><td colSpan={22} className="px-4 py-6 text-center text-gray-400">{jobs.length === 0 ? 'No employer-posted jobs.' : 'No jobs match your filters.'}</td></tr>
            )}
          </tbody>
        </table>
      </div>
<PaginationBar
  className="mt-3"
  total={filteredInternalJobs.length}
  page={jobPage}
  pageSize={jobPageSize}
  onPage={setJobPage}
  onPageSize={setJobPageSize}
/>
     
    </>
    ) : (
      <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 overflow-hidden">
        <table className="w-full text-sm">
        <thead className="bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 text-[11px] tracking-wider uppercase border-b border-gray-100">
  <tr>
    {[
      { key: 'title', label: 'Title' },
      { key: 'company', label: 'Company' },
      { key: 'location', label: 'Location' },
      { key: 'type', label: 'Type' },
      { key: 'salary', label: 'Salary' },
      { key: 'source', label: 'Source' },
    ].map((col) => (
      <SortTh key={col.key} label={col.label} sortKey={col.key} sort={aggSort} onSort={toggleAggSort} />
    ))}
    <th className="text-left px-4 py-3 font-semibold sticky right-0 bg-purple-50">Actions</th>
  </tr>
</thead>
                   <tbody>
            {aggJobsLoading ? (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-400">Loading...</td></tr>
            ) : aggJobsError ? (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-red-600">{aggJobsError}</td></tr>
            ) : filteredAggJobs.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-400">{aggJobs.length === 0 ? 'No aggregated jobs.' : 'No jobs match your filters.'}</td></tr>
            ) : (
              sortedAggJobs.map((j) => (
                <tr key={j.id} className="border-t border-gray-50 hover:bg-purple-50/70 transition-colors">
                  <td className="px-4 py-3">{j.title}</td>
                  <td className="px-4 py-3">{j.company_name || '—'}</td>
                  <td className="px-4 py-3">{j.location || '—'}</td>
                  <td className="px-4 py-3 capitalize">{(j.job_type || '').replace(/_/g, ' ') || '—'}</td>
                  <td className="px-4 py-3">{formatAggSalary(j.salary_min, j.salary_max, j.salary_currency)}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-medium px-2 py-1 rounded-full bg-gray-100 text-gray-500">
                      {j.source_name || 'Partner source'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
  <RowActions
    onView={() => setViewJobModal({ ...j, _agg: true })}
    deleting={deletingAggJobId === j.id}
    onDelete={() => deleteAggJob(j)}
  />
</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    )}
  </div>
)}

                {activeTab === 'job-aggregator' && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 p-6">
              <h3 className="font-bold text-[#0D1B3D] text-lg mb-3">
                {editingSourceId ? 'Edit Source' : 'Add New Source'}
              </h3>
              {sourceFormError && <p className="text-sm text-red-600 mb-2">{sourceFormError}</p>}
              <form onSubmit={submitSourceForm} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  type="text" name="name" placeholder="Source Name"
                  value={sourceForm.name} onChange={handleSourceFormChange} required
                  className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                />
                <select
                  name="source_type" value={sourceForm.source_type} onChange={handleSourceFormChange}
                  className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                >
                  {Object.entries(SOURCE_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
                <input
                  type="url" name="website_url" placeholder="Website URL"
                  value={sourceForm.website_url} onChange={handleSourceFormChange}
                  className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                />
                <input
                  type="url" name="feed_url" placeholder="Feed/API URL"
                  value={sourceForm.feed_url} onChange={handleSourceFormChange}
                  className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                />
                <select
                  name="status" value={sourceForm.status} onChange={handleSourceFormChange}
                  className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                <select
                  name="scrape_frequency" value={sourceForm.scrape_frequency} onChange={handleSourceFormChange}
                  className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                >
                  <option value="hourly">Every Hour</option>
                  <option value="every_6_hours">Every 6 Hours</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                </select>
                <div className="sm:col-span-2 flex gap-2">
                  <button
                    type="submit" disabled={sourceSaving}
                    className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-6 py-2 rounded-lg font-semibold text-sm hover:opacity-90 disabled:opacity-50"
                  >
                    {sourceSaving ? 'Saving...' : editingSourceId ? 'Update Source' : 'Add Source'}
                  </button>
                  {editingSourceId && (
                    <button
                      type="button" onClick={resetSourceForm}
                      className="px-6 py-2 rounded-lg font-semibold text-sm text-gray-500 hover:bg-gray-100"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>

            <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 text-[11px] tracking-wider uppercase border-b border-gray-100">
  <tr>
    {[
      { key: 'name', label: 'Source Name' },
      { key: 'type', label: 'Type' },
      { key: 'status', label: 'Status' },
      { key: 'frequency', label: 'Frequency' },
      { key: 'jobs', label: 'Jobs' },
      { key: 'last_run', label: 'Last Run' },
    ].map((col) => (
      <SortTh key={col.key} label={col.label} sortKey={col.key} sort={sourceSort} onSort={toggleSourceSort} />
    ))}
    <th className="text-left px-4 py-3 font-semibold sticky right-0 bg-purple-50">Actions</th>
  </tr>
</thead>
                               <tbody>
                  {jobSourcesLoading ? (
                    <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-400">Loading...</td></tr>
                  ) : jobSourcesError ? (
                    <tr><td colSpan={7} className="px-4 py-6 text-center text-red-600">{jobSourcesError}</td></tr>
                  ) : filteredJobSources.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-400">{jobSources.length === 0 ? 'No sources added yet.' : 'No sources match your search.'}</td></tr>
                  ) : (
                    sortedJobSources.map((s) => (
                      <tr key={s.id} className="border-t border-gray-50 hover:bg-purple-50/70 transition-colors">
                        <td className="px-4 py-3 font-medium">{s.name}</td>
                        <td className="px-4 py-3">{SOURCE_TYPE_LABELS[s.source_type] || s.source_type}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                            s.status === 'active' ? 'bg-[#ECFDF5] text-[#10B981]' : 'bg-gray-100 text-gray-500'
                          }`}>
                            {s.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 capitalize">{s.scrape_frequency?.replace(/_/g, ' ')}</td>
                        <td className="px-4 py-3">{s.total_jobs ?? 0}</td>
                        <td className="px-4 py-3 text-xs text-gray-500">
                          {s.last_run_at ? new Date(s.last_run_at).toLocaleString() : '—'}
                        </td>
<td className="px-4 py-3">
  <RowActions
    onEdit={() => editSource(s)}
    onDelete={() => deleteSource(s.id)}
    extra={<button onClick={() => viewSourceLogs(s)} className={ACT_GRAY}>📜 Logs</button>}
  />
</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'aggregator-dashboard' && (
  <div className="space-y-4">
    {aggDashboardLoading ? (
      <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 p-10 text-center text-gray-400">
        Loading dashboard...
      </div>
    ) : aggDashboardError ? (
      <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 p-6 text-center text-red-600 text-sm">
        {aggDashboardError}
      </div>
    ) : aggDashboard && (
      <>
        {/* Overview cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {[
            { label: 'Total Aggregated Jobs', value: aggDashboard.total_jobs, color: 'text-[#7C3AED]' },
            { label: 'Active', value: aggDashboard.active, color: 'text-[#10B981]' },
            { label: 'New Today', value: aggDashboard.new_today, color: 'text-[#7C3AED]' },
            { label: 'Pending Review', value: aggDashboard.pending_review, color: 'text-[#F59E0B]' },
            { label: 'Duplicates', value: aggDashboard.duplicates, color: 'text-gray-500' },
            { label: 'Expired', value: aggDashboard.expired, color: 'text-red-500' },
          ].map((card) => (
            <div
              key={card.label}
              className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 hover:shadow-md transition-shadow duration-200 p-6 relative overflow-hidden"
            >
              <span className={`absolute top-0 left-0 w-full h-1 ${card.color.replace('text-', 'bg-')}`}></span>
              <p className={`text-3xl font-bold ${card.color}`}>{card.value}</p>
              <p className="text-sm font-medium text-gray-500 mt-1">{card.label}</p>
            </div>
          ))}
        </div>
 
               {/* Source performance — one row per source, latest run status */}
        <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 overflow-hidden">
          <div className="p-4 border-b">
            <h3 className="font-bold text-[#0D1B3D] mb-3">Source Performance</h3>
            <input
              type="text"
              value={dashboardSourceSearch}
              onChange={(e) => setDashboardSourceSearch(e.target.value)}
              placeholder="Search sources by name..."
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-purple-400"
            />
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 text-[11px] tracking-wider uppercase border-b border-gray-100">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Source</th>
                <th className="text-left px-4 py-3 font-semibold">Jobs</th>
                <th className="text-left px-4 py-3 font-semibold">Last Run</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredSourcePerformance.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-gray-400">
                    {aggDashboard.source_performance.length === 0 ? 'No sources added yet.' : 'No sources match your search.'}
                  </td>
                </tr>
              ) : (
                filteredSourcePerformance.map((s) => (
                  <tr key={s.id} className="border-t border-gray-50 hover:bg-purple-50/70 transition-colors">
                    <td className="px-4 py-3 font-medium">{s.name}</td>
                    <td className="px-4 py-3">{s.total_jobs}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {s.last_run_at ? new Date(s.last_run_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {s.status === 'successful' ? (
                        <span className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold bg-[#ECFDF5] text-[#10B981]">
                          ✓
                        </span>
                      ) : s.status === 'failed' ? (
                        <span className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold bg-[#FEF2F2] text-[#EF4444]">
                          ✗
                        </span>
                      ) : (
                        <span className="text-gray-300 text-xs">— never run —</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </>
    )}
  </div>
)}


{activeTab === 'job-review' && (
  <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 overflow-hidden">
       <div className="p-4 border-b flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <div>
        <h3 className="font-bold text-[#0D1B3D]">Jobs Pending Review</h3>
        <p className="text-xs text-gray-500 mt-1">Click a row to view full details, or select rows to act in bulk.</p>
        <input
          type="text"
          value={reviewSearch}
          onChange={(e) => setReviewSearch(e.target.value)}
          placeholder="Search by title, company, or location..."
          className="mt-2 w-full sm:w-80 border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-purple-400"
        />
      </div>

      {selectedReviewIds.size > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-gray-500">
            {selectedReviewIds.size} selected
          </span>
          <button
            onClick={() => handleBulkReviewAction('publish')}
            disabled={bulkActionLoading}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#10B981] text-white hover:opacity-90 disabled:opacity-50"
          >
            Publish
          </button>
          <button
            onClick={() => handleBulkReviewAction('reject')}
            disabled={bulkActionLoading}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-red-600 text-white hover:opacity-90 disabled:opacity-50"
          >
            Reject
          </button>
          <button
            onClick={() => handleBulkReviewAction('mark_duplicate')}
            disabled={bulkActionLoading}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-gray-600 text-white hover:opacity-90 disabled:opacity-50"
          >
            Mark Duplicate
          </button>
          <button
            onClick={() => setSelectedReviewIds(new Set())}
            disabled={bulkActionLoading}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-50"
          >
            Clear
          </button>
        </div>
      )}
    </div>

    <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 text-[11px] tracking-wider uppercase border-b border-gray-100">
        <tr>
                    <th className="text-left px-4 py-3 font-semibold w-10">
            {filteredReviewJobs.length > 0 && (
              <input
                type="checkbox"
                checked={selectedReviewIds.size === filteredReviewJobs.length}
                onChange={toggleReviewSelectAll}
                onClick={(e) => e.stopPropagation()}
              />
            )}
          </th>
         {[
  { key: 'title', label: 'Title' },
  { key: 'company', label: 'Company' },
  { key: 'location', label: 'Location' },
  { key: 'salary', label: 'Salary' },
  { key: 'source', label: 'Source' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'score', label: 'Score' },
].map((col) => (
  <SortTh key={col.key} label={col.label} sortKey={col.key} sort={reviewSort} onSort={toggleReviewSort} className="!px-2" />
))}
        </tr>
      </thead>
      <tbody>
               {reviewJobsLoading ? (
          <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-400">Loading...</td></tr>
        ) : reviewJobsError ? (
          <tr><td colSpan={7} className="px-4 py-6 text-center text-red-600">{reviewJobsError}</td></tr>
        ) : filteredReviewJobs.length === 0 ? (
          <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-400">{reviewJobs.length === 0 ? 'No jobs pending review.' : 'No jobs match your search.'}</td></tr>
        ) : (
          sortedReviewJobs.map((job) => (
            <tr
              key={job.id}
              onClick={() => openReviewModal(job)}
              className={`border-t border-gray-50 hover:bg-purple-50/70 transition-colors cursor-pointer ${
                selectedReviewIds.has(job.id) ? 'bg-purple-50' : ''
              }`}
            >
              <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selectedReviewIds.has(job.id)}
                  onChange={() => toggleReviewSelect(job.id)}
                />
              </td>
             <td className="px-2 py-3 font-medium max-w-[160px] truncate">{job.title}</td>
<td className="px-2 py-3 max-w-[120px] truncate">{job.company_name}</td>
<td className="px-2 py-3 max-w-[110px] truncate">{job.location || '—'}</td>
<td className="px-2 py-3 text-xs">

  {job.salary_min || job.salary_max
    ? `${job.salary_currency === 'INR' ? '₹' : ''}${
        job.salary_min ? Number(job.salary_min).toLocaleString() : ''
      }${job.salary_min && job.salary_max ? ' - ' : ''}${
        job.salary_max ? Number(job.salary_max).toLocaleString() : ''
      }`
    : '—'}
</td>
<td className="px-2 py-3 text-xs text-gray-500 max-w-[90px] truncate">{job.source_name || '—'}</td>
              <td className="px-2 py-3 text-xs">
                {job.company_mobile || <span className="text-red-400">missing</span>}
              </td>
             <td className="px-2 py-3 whitespace-nowrap">
  <span className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold bg-[#FFFBEB] text-[#F59E0B]">
    {job.quality_score}%
  </span>
</td>
            </tr>
          ))
        )}
          </tbody>
    </table>
    </div>
  </div>
)}
 
{activeTab === 'applications' && (
  <div className="space-y-4">
    <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 p-4 space-y-3">
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          value={applicationSearch}
          onChange={(e) => setApplicationSearch(e.target.value)}
          placeholder="Search by candidate, job, or company..."
          className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-purple-400"
        />
        <select
          value={applicationStatusFilter}
          onChange={(e) => setApplicationStatusFilter(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
        >
          <option value="">All Statuses</option>
          <option value="applied">Applied</option>
          <option value="screening">Screening</option>
          <option value="shortlisted">Shortlisted</option>
          <option value="interview_round_1">Interview (Round 1)</option>
          <option value="interview_round_2">Interview (Round 2)</option>
          <option value="hr_round">HR Round</option>
          <option value="offer">Offer</option>
          <option value="joined">Joined</option>
          <option value="rejected">Rejected</option>
        </select>
        <select
          value={applicationViewedFilter}
          onChange={(e) => setApplicationViewedFilter(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
        >
          <option value="">Employer Viewed: All</option>
          <option value="yes">Viewed</option>
          <option value="no">Not viewed</option>
        </select>
        <select
          value={applicationResponseFilter}
          onChange={(e) => setApplicationResponseFilter(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
        >
          <option value="">Response Status: All</option>
          <option value="not_yet">Not yet</option>
          <option value="screening">Screening</option>
          <option value="shortlisted">Shortlist</option>
          <option value="interview">Interview</option>
          <option value="offer">Offer</option>
          <option value="joined">Joined</option>
          <option value="rejected">Reject</option>
          <option value="withdrawn">Withdrawn</option>
        </select>
      </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <MultiSelectDropdown
          values={appCandidateFilter}
          onChange={setAppCandidateFilter}
          options={appCandidateOptions}
          allLabel="All Candidates"
        />
        <MultiSelectDropdown
          values={appCompanyFilter}
          onChange={setAppCompanyFilter}
          options={appCompanyOptions}
          allLabel="All Companies"
        />
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-semibold text-gray-400 uppercase">Applied</span>
        <input
          type="date"
          value={applicationDateFrom}
          onChange={(e) => setApplicationDateFrom(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
        />
        <span className="text-gray-400 text-sm">to</span>
        <input
          type="date"
          value={applicationDateTo}
          onChange={(e) => setApplicationDateTo(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
        />
        {(applicationSearch || applicationStatusFilter || applicationViewedFilter || applicationResponseFilter || applicationDateFrom || applicationDateTo || appCandidateFilter.length > 0 || appCompanyFilter.length > 0) && (
          <button
            type="button"
            onClick={() => {
              setAppCandidateFilter([]);
              setAppCompanyFilter([]);
              setApplicationSearch('');
              setApplicationStatusFilter('');
              setApplicationViewedFilter('');
              setApplicationResponseFilter('');
              setApplicationDateFrom('');
              setApplicationDateTo('');
            }}
            className="text-xs font-semibold text-[#7C3AED] hover:underline"
          >
            Clear filters
          </button>
        )}
      </div>
    </div>

    <PaginationBar
      total={filteredApplications.length}
      page={appPage}
      pageSize={appPageSize}
      onPage={setAppPage}
      onPageSize={(n) => { setAppPageSize(n); setAppPage(1); }}
    />

    <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 overflow-x-auto">
      <table className="w-full text-sm min-w-[1300px]">
       <thead className="bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 text-[11px] tracking-wider uppercase border-b border-gray-100">
  <tr>
    {[
      { key: 'candidate', label: 'Candidate' },
      { key: 'job', label: 'Job' },
      { key: 'company', label: 'Company' },
      { key: 'applied', label: 'Applied On' },
      { key: 'viewed', label: 'Impressions of Employer' },
      { key: 'status', label: 'Response Status' },
    ].map((col) => (
      <SortTh key={col.key} label={col.label} sortKey={col.key} sort={appSort} onSort={toggleAppSort} />
    ))}
    <th className="text-left px-4 py-3 font-semibold sticky right-0 z-10 bg-gray-50">Actions</th>
  </tr>
</thead>
        <tbody>
          {applicationsLoading ? (
            <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-400">Loading...</td></tr>
          ) : applicationsError ? (
            <tr><td colSpan={7} className="px-4 py-6 text-center text-red-600">{applicationsError}</td></tr>
          ) : filteredApplications.length === 0 ? (
            <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-400">No applications found.</td></tr>
          ) : (
            sortedApplications.slice((appPage - 1) * appPageSize, appPage * appPageSize).map((app) => {
              const viewed = employerViewedLabel(app);
              return (
                <tr key={app.id} className="border-t border-gray-50 hover:bg-purple-50/70 transition-colors">
                  <td className="px-4 py-3">
                    {app.user?.id ? (
                      <button
                        type="button"
                        onClick={() => viewUserDetails(app.user.id)}
                        className="font-medium text-left text-[#7C3AED] hover:underline"
                      >
                        {app.user.name || '—'}
                      </button>
                    ) : (
                      <p className="font-medium">—</p>
                    )}
                    <p className="text-xs text-gray-500">{app.user?.email}</p>
                  </td>
                  <td className="px-4 py-3">{app.job_posting?.title || '—'}</td>
                  <td className="px-4 py-3">
                    {app.job_posting?.employer_id ? (
                      <button
                        type="button"
                        onClick={() => viewUserDetails(app.job_posting.employer_id)}
                        className="text-left text-[#7C3AED] hover:underline"
                      >
                        {app.job_posting.company_name || '—'}
                      </button>
                    ) : (
                      app.job_posting?.company_name || '—'
                    )}
                  </td>
                
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {app.created_at ? new Date(app.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs font-medium">
                    {viewed ? <span className="text-green-600">{viewed}</span> : <span className="text-gray-400">No</span>}
                  </td>
                  <td className="px-4 py-3 text-xs font-semibold text-gray-700">
                    {({ applied: '—', screening: 'Screening', shortlisted: 'Shortlist', interview_round_1: 'Interview', interview_round_2: 'Interview', hr_round: 'Interview', offer: 'Offer', joined: 'Joined', rejected: 'Reject', withdrawn: 'Withdrawn' })[app.status] || app.status || '—'}
                    {app.status === 'withdrawn' && app.withdraw_reason && (
                      <button
                        type="button"
                        onClick={() => setReasonApplication(app)}
                        className="mt-1 block text-[10px] font-semibold text-purple-600 hover:underline"
                      >
                        View reason
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 sticky right-0 z-10 bg-white">
  <RowActions
    phone={app.user?.phone}
    onView={app.user?.id ? () => viewUserDetails(app.user.id) : undefined}
    extra={
      <select
        value={app.status || ''}
        onChange={(e) => handleUpdateApplicationStatus(app.id, e.target.value)}
        className="h-8 px-3 rounded-full text-xs font-semibold border border-purple-200 bg-purple-50 text-[#7C3AED] cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-400"
      >
        <option value="applied">Applied</option>
        <option value="screening">Screening</option>
        <option value="shortlisted">Shortlisted</option>
        <option value="interview_round_1">Interview (Round 1)</option>
        <option value="interview_round_2">Interview (Round 2)</option>
        <option value="hr_round">HR Round</option>
        <option value="offer">Offer</option>
        <option value="joined">Joined</option>
        <option value="rejected">Rejected</option>
        <option value="withdrawn">Withdrawn</option>
      </select>
    }
  />
</td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
    <PaginationBar
      className="mt-3"
      total={filteredApplications.length}
      page={appPage}
      pageSize={appPageSize}
      onPage={setAppPage}
      onPageSize={(n) => { setAppPageSize(n); setAppPage(1); }}
    />
  </div>
)}


{reasonApplication && (
  <div
    className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
    onClick={() => setReasonApplication(null)}
  >
    <div
      className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start justify-between mb-4">
        <h3 className="text-lg font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">
          Withdraw Reason
        </h3>
        <button
          onClick={() => setReasonApplication(null)}
          className="text-gray-400 hover:text-purple-600 text-xl leading-none"
        >
          ×
        </button>
      </div>
      <div className="space-y-2 text-sm">
        <p><span className="text-gray-400">Candidate:</span> {reasonApplication.user?.name || '—'}</p>
        <p><span className="text-gray-400">Job:</span> {reasonApplication.job_posting?.title || '—'}</p>
        <p><span className="text-gray-400">Company:</span> {reasonApplication.job_posting?.company_name || '—'}</p>
        <p className="whitespace-pre-wrap break-words">
          <span className="text-gray-400">Reason:</span> {reasonApplication.withdraw_reason}
        </p>
        <p>
          <span className="text-gray-400">Withdrawn on:</span>{' '}
          {reasonApplication.withdrawn_at
            ? new Date(reasonApplication.withdrawn_at).toLocaleString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })
            : '—'}
        </p>
      </div>
    </div>
  </div>
)}


{reviewModalJob && (
  <div
    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    onClick={closeReviewModal}
  >
    <div
      className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-8 relative"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        onClick={closeReviewModal}
        className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-xl font-bold"
      >
        ×
      </button>
 
      <h3 className="text-xl font-bold text-[#7C3AED]">{reviewModalJob.title}</h3>
      <p className="text-sm text-gray-500 mb-4">
        {reviewModalJob.company_name}
        {reviewModalJob.location && <span> · {reviewModalJob.location}</span>}
      </p>
 
      <div className="grid grid-cols-2 gap-3 text-sm text-gray-700 mb-4">
        {(reviewModalJob.experience_min != null || reviewModalJob.experience_max != null) && (
          <p><span className="font-medium">Experience:</span> {reviewModalJob.experience_min}-{reviewModalJob.experience_max} yrs</p>
        )}
       {(reviewModalJob.salary_min || reviewModalJob.salary_max) && (
  <p>
    <span className="font-medium">Salary:</span>{' '}
    {reviewModalJob.salary_currency === 'INR' ? '₹' : ''}
    {reviewModalJob.salary_min ? Number(reviewModalJob.salary_min).toLocaleString() : ''}
    {reviewModalJob.salary_min && reviewModalJob.salary_max ? ' - ' : ''}
    {reviewModalJob.salary_max ? Number(reviewModalJob.salary_max).toLocaleString() : ''}
  </p>
)}
        {reviewModalJob.category && (
          <p><span className="font-medium">Category:</span> {reviewModalJob.category}</p>
        )}
        <p><span className="font-medium">Source:</span> {reviewModalJob.source_name || '—'}</p>
      </div>
 
      {reviewModalJob.skills && (
        <div className="mb-4">
          <p className="text-xs font-semibold text-gray-400 uppercase mb-1.5">Skills</p>
          <div className="flex flex-wrap gap-1.5">
            {reviewModalJob.skills.split(',').map((s) => s.trim()).filter(Boolean).map((skill, i) => (
              <span key={i} className="text-xs font-medium px-2.5 py-1 rounded-full bg-[#EFF6FF] text-[#7C3AED]">
                {skill}
              </span>
            ))}
          </div>
        </div>
      )}
 
    <div className="mb-4">
  <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Description</p>
  <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">
    {reviewModalJob.description ? cleanDescription(reviewModalJob.description) : 'No description provided.'}
  </p>
</div>

      <div className="mb-4">
  <label className="text-xs font-semibold text-gray-400 uppercase mb-1 block">
    Company Mobile Number
  </label>
  <input
    type="text"
    value={reviewMobile}
    onChange={(e) => setReviewMobile(e.target.value)}
    placeholder="e.g. 9876543210 — needed for the agent to confirm this job"
    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
  />
</div>


      <div className="mb-6">
        <span className="inline-block px-3 py-1.5 rounded-full text-sm font-semibold bg-[#FFFBEB] text-[#F59E0B]">
          Quality Score: {reviewModalJob.quality_score}%
        </span>
      </div>
 
      <div className="flex flex-col sm:flex-row gap-2">
        <button
          onClick={() => handleReviewAction('publish')}
          disabled={reviewActionLoading}
          className="flex-1 text-center text-sm font-semibold px-4 py-2.5 rounded-lg bg-[#10B981] text-white hover:opacity-90 disabled:opacity-50"
        >
          Publish
        </button>
        <button
          onClick={() => handleReviewAction('reject')}
          disabled={reviewActionLoading}
          className="flex-1 text-center text-sm font-semibold px-4 py-2.5 rounded-lg bg-red-600 text-white hover:opacity-90 disabled:opacity-50"
        >
          Reject
        </button>
        <button
          onClick={() => handleReviewAction('mark_duplicate')}
          disabled={reviewActionLoading}
          className="flex-1 text-center text-sm font-semibold px-4 py-2.5 rounded-lg bg-gray-600 text-white hover:opacity-90 disabled:opacity-50"
        >
          Mark Duplicate
        </button>
      </div>
    </div>
  </div>
)}

{metricModal && (
  <div
    className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
    onClick={() => setMetricModal(null)}
  >
    <div
      className="bg-white rounded-2xl shadow-xl max-w-3xl w-full p-6 max-h-[85vh] overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-[#0D1B3D]">{metricModal.label}</h3>
          <p className="text-xs text-gray-500">{metricModal.job.title} · {metricModal.job.company_name}</p>
        </div>
        <button onClick={() => setMetricModal(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
      </div>

      {metricLoading ? (
        <p className="text-center text-gray-400 py-8">Loading...</p>
      ) : metricError ? (
        <p className="text-center text-red-600 py-6 text-sm">{metricError}</p>
      ) : metricUsers.length === 0 ? (
        <p className="text-center text-gray-400 py-8 text-sm">No users found.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 text-[11px] tracking-wider uppercase border-b border-gray-100">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Name</th>
              <th className="text-left px-3 py-2 font-semibold">Email</th>
              <th className="text-left px-3 py-2 font-semibold">Phone</th>
              <th className="text-left px-3 py-2 font-semibold">Location</th>
              <th className="text-left px-3 py-2 font-semibold">Date</th>
              <th className="text-left px-3 py-2 font-semibold">Action</th>
            </tr>
          </thead>
          <tbody>
            {metricUsers.map((u, i) => (
              <tr key={`${u.id}-${i}`} className="border-t border-gray-50 hover:bg-purple-50/70">
                <td className="px-3 py-2 font-medium">{u.name}</td>
                <td className="px-3 py-2">{u.email}</td>
                <td className="px-3 py-2">{u.phone || '—'}</td>
                <td className="px-3 py-2">{u.location || '—'}</td>
                <td className="px-3 py-2 text-xs text-gray-500">{fmtDate(u.at)}</td>
                <td className="px-3 py-2">
                  <button onClick={() => viewUserDetails(u.id)} className="text-xs font-semibold text-[#7C3AED] hover:underline">
                    View profile
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  </div>
)}

{closeReasonJob && (
  <div
    className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
    onClick={() => setCloseReasonJob(null)}
  >
    <div
      className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start justify-between mb-4">
        <h3 className="text-lg font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">
          Close Reason
        </h3>
        <button
          onClick={() => setCloseReasonJob(null)}
          className="text-gray-400 hover:text-purple-600 text-xl leading-none"
        >
          ×
        </button>
      </div>
      <div className="space-y-2 text-sm">
        <p><span className="text-gray-400">Job:</span> {closeReasonJob.title}</p>
        <p><span className="text-gray-400">Company:</span> {closeReasonJob.company_name || '—'}</p>
        <p><span className="text-gray-400">Employer:</span> {closeReasonJob.employer?.name || '—'}</p>
        <p className="whitespace-pre-wrap break-words">
          <span className="text-gray-400">Reason:</span> {closeReasonJob.close_reason}
        </p>
        <p>
          <span className="text-gray-400">Closed on:</span>{' '}
          {closeReasonJob.closed_at
            ? new Date(closeReasonJob.closed_at).toLocaleString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })
            : '—'}
        </p>
      </div>
    </div>
  </div>
)}

{/* Jobs tab: View modal (internal + aggregated) */}
{viewJobModal && (
  <div
    className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
    onClick={() => setViewJobModal(null)}
  >
    <div
      className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 max-h-[85vh] overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start justify-between mb-4">
        <h3 className="text-lg font-bold text-[#0D1B3D]">{viewJobModal.title}</h3>
        <button onClick={() => setViewJobModal(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
      </div>
      <div className="space-y-2 text-sm">
        {viewJobModal.employer?.name && <p><span className="text-gray-400">Employer:</span> {viewJobModal.employer.name}</p>}
        {viewJobModal.company_name && <p><span className="text-gray-400">Company:</span> {viewJobModal.company_name}</p>}
        <p><span className="text-gray-400">Location:</span> {viewJobModal.location || '—'}</p>
        <p><span className="text-gray-400">Type:</span> {(viewJobModal.job_type || '').replace(/_/g, ' ') || '—'}</p>
        {viewJobModal._agg ? (
          <p><span className="text-gray-400">Salary:</span> {formatAggSalary(viewJobModal.salary_min, viewJobModal.salary_max, viewJobModal.salary_currency)}</p>
        ) : (
          <p><span className="text-gray-400">Salary:</span> {fmtINR(viewJobModal.salary_min)} – {fmtINR(viewJobModal.salary_max)}</p>
        )}
        {viewJobModal.openings !== undefined && <p><span className="text-gray-400">Openings:</span> {viewJobModal.openings ?? '—'}</p>}
        {viewJobModal.status && <p><span className="text-gray-400">Status:</span> <span className="capitalize">{viewJobModal.status}</span></p>}
        {viewJobModal.created_at && <p><span className="text-gray-400">Posted:</span> {fmtDate(viewJobModal.created_at)}</p>}
        {viewJobModal._agg && viewJobModal.source_name && <p><span className="text-gray-400">Source:</span> {viewJobModal.source_name}</p>}
        {viewJobModal.description && (
          <div>
            <p className="text-gray-400 mb-1">Description:</p>
            <p className="whitespace-pre-wrap text-gray-700">
              {viewJobModal._agg ? cleanDescription(viewJobModal.description) : viewJobModal.description}
            </p>
          </div>
        )}
      </div>
    </div>
  </div>
)}

{/* Jobs tab: Edit modal (internal jobs only) */}
{editJobModal && (
  <div
    className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
    onClick={() => setEditJobModal(null)}
  >
    <form
      onSubmit={handleSaveJobEdit}
      className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 max-h-[85vh] overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start justify-between mb-4">
        <h3 className="text-lg font-bold text-[#0D1B3D]">Edit Job</h3>
        <button type="button" onClick={() => setEditJobModal(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
      </div>

      <div className="space-y-3">
        <div>
          <label className="block text-xs text-gray-400 mb-1">Title</label>
          <input
            type="text"
            value={editJobModal.title || ''}
            onChange={(e) => setEditJobModal((j) => ({ ...j, title: e.target.value }))}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Location</label>
          <input
            type="text"
            value={editJobModal.location || ''}
            onChange={(e) => setEditJobModal((j) => ({ ...j, location: e.target.value }))}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Job Type</label>
            <select
              value={editJobModal.job_type || ''}
              onChange={(e) => setEditJobModal((j) => ({ ...j, job_type: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            >
              {ADMIN_JOB_TYPE_OPTIONS.filter((o) => o.value).map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Status</label>
            <select
              value={editJobModal.status || ''}
              onChange={(e) => setEditJobModal((j) => ({ ...j, status: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            >
              {ADMIN_JOB_STATUS_OPTIONS.filter((o) => o.value).map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Salary Min</label>
            <input
              type="number"
              value={editJobModal.salary_min ?? ''}
              onChange={(e) => setEditJobModal((j) => ({ ...j, salary_min: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Salary Max</label>
            <input
              type="number"
              value={editJobModal.salary_max ?? ''}
              onChange={(e) => setEditJobModal((j) => ({ ...j, salary_max: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Openings</label>
            <input
              type="number"
              value={editJobModal.openings ?? ''}
              onChange={(e) => setEditJobModal((j) => ({ ...j, openings: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 mt-5">
        <button type="button" onClick={() => setEditJobModal(null)} className="text-sm text-gray-500 hover:text-gray-700">
          Cancel
        </button>
        <button
          type="submit"
          disabled={savingJobEdit}
          className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:opacity-90 disabled:opacity-50"
        >
          {savingJobEdit ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </form>
  </div>
)}


{activeTab === 'notification-report' && <NotificationReportPanel />}
{activeTab === 'deletion-requests' && <DeletionRequestsPanel />}
{activeTab === 'contact-enquiries' && <ContactEnquiriesPanel />}
{activeTab === 'notifications' && <NotificationCampaignsPanel />}
{activeTab === 'enterprise' && <EnterprisePanel />}
{activeTab === 'enterprise-enquiries' && <AdminEnterpriseInquiries />}
{activeTab === 'billing-reports' && <AdminBillingReports />}
{activeTab === 'subscriptions' && (
  <div className="space-y-4">
    <div className="flex gap-1 border-b border-gray-100">
      {[
        { key: 'all', label: 'All Subscriptions' },
        { key: 'renewals', label: 'Renewals' },
      ].map((t) => (
        <button
          key={t.key}
          onClick={() => setSubsSubTab(t.key)}
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
            subsSubTab === t.key
              ? 'border-purple-600 text-purple-600'
              : 'border-transparent text-gray-400 hover:text-gray-600'
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>

    {subsSubTab === 'all' ? (
      <AdminSubscriptionsPanel key="subs" mode="all" />
    ) : (
      <AdminSubscriptionsPanel key="renewals" mode="expiring" />
    )}
  </div>
)}
{activeTab === 'billing-analytics' && <BillingAnalyticsPanel />}
{activeTab === 'support-tickets' && (
  <SupportTicketsPanel />
)}

{activeTab === 'businesses' && (
  <BusinessesPanel />
)}

{activeTab === 'business-import' && (
  <BusinessImportPanel />
)}

{activeTab === 'locations' && (
  <LocationCategoryPanel />
)}

      {activeTab === 'aadhaar' && (
  <div className="space-y-4">
    <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
      <input
        type="text"
        value={aadhaarSearch}
        onChange={(e) => setAadhaarSearch(e.target.value)}
        placeholder="Search by name, email, or Aadhaar number..."
        className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-purple-400"
      />
      {selectedAadhaarIds.size > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-gray-500">
            {selectedAadhaarIds.size} selected
          </span>
          <button
            onClick={() => handleBulkAadhaarStatus('verified')}
            disabled={bulkAadhaarLoading}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#10B981] text-white hover:opacity-90 disabled:opacity-50"
          >
            Approve
          </button>
          <button
            onClick={() => handleBulkAadhaarStatus('rejected')}
            disabled={bulkAadhaarLoading}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-red-600 text-white hover:opacity-90 disabled:opacity-50"
          >
            Reject
          </button>
          <button
            onClick={() => setSelectedAadhaarIds(new Set())}
            disabled={bulkAadhaarLoading}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-50"
          >
            Clear
          </button>
        </div>
      )}
    </div>

    <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 text-[11px] tracking-wider uppercase border-b border-gray-100">
          <tr>
            <th className="text-left px-4 py-3 font-semibold w-10">
              {filteredAadhaarRequests.length > 0 && (
                <input
                  type="checkbox"
                  checked={selectedAadhaarIds.size === filteredAadhaarRequests.length}
                  onChange={toggleAadhaarSelectAll}
                />
              )}
            </th>
            <th className="text-left px-4 py-3 font-semibold">Name</th>
            <th className="text-left px-4 py-3 font-semibold">Email</th>
            <th className="text-left px-4 py-3 font-semibold">Aadhaar Number</th>
           <th className="text-left px-4 py-3 font-semibold sticky right-0 bg-purple-50">Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredAadhaarRequests.map((u) => (
            <tr key={u.id} className="border-t border-gray-50 hover:bg-purple-50/70 transition-colors">
              <td className="px-4 py-3">
                <input
                  type="checkbox"
                  checked={selectedAadhaarIds.has(u.id)}
                  onChange={() => toggleAadhaarSelect(u.id)}
                />
              </td>
  
              <td className="px-4 py-3">{u.name}</td>
              <td className="px-4 py-3">{u.email}</td>
              <td className="px-4 py-3">{u.aadhaar_number}</td>
              <td className="px-4 py-3">
<RowActions
  hideContact
  extra={
    <>
      <button onClick={() => handleAadhaarStatus(u.id, 'verified')} className={ACT_CALL}>✓ Approve</button>
      <button onClick={() => handleAadhaarStatus(u.id, 'rejected')} className={ACT_DELETE}>✗ Reject</button>
    </>
  }
/>
              </td>
            </tr>
          ))}
          {filteredAadhaarRequests.length === 0 && (
            <tr>
              <td colSpan={5} className="px-4 py-6 text-center text-gray-400">
                {aadhaarRequests.length === 0 ? 'No pending Aadhaar requests.' : 'No requests match your search.'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
)}

        {activeTab === 'master' && (
          <div className="space-y-4">
    
                {masterView === '' && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { key: 'locations', icon: '📍', title: 'Locations', desc: 'States, districts, cities and areas' },
                  { key: 'plans', icon: '💳', title: 'Plans & Pricing', desc: 'Hiring plans, packs, credits, boosts' },
                  { key: 'data', icon: '🗂️', title: 'Master Data', desc: 'Skills, education, job titles, companies' },
                ].map((b) => (
                  <button
                    key={b.key}
                    type="button"
                    onClick={() => setMasterView(b.key)}
                    className="text-left bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 p-6 hover:-translate-y-0.5 hover:shadow-lg transition"
                  >
                    <span className="text-3xl">{b.icon}</span>
                    <p className="font-bold text-[#0D1B3D] mt-3">{b.title}</p>
                    <p className="text-sm text-gray-500 mt-1">{b.desc}</p>
                  </button>
                ))}
              </div>
            )}

            {masterView !== '' && (
              <button type="button" onClick={() => setMasterView('')} className="text-sm font-semibold text-[#7C3AED] hover:underline">
                ← Back to Master
              </button>
            )}

            {masterView === 'locations' && <LocationMasterPanel />}

            {masterView === 'plans' && (
            <>
            

            {/* Credit settings */}
            {/* <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 p-6">
              <h3 className="font-bold text-[#0D1B3D] text-lg mb-1">Candidate Credits</h3>
              <p className="text-sm text-gray-500 mb-3">Credits charged per contact unlock, and rupee price of one credit.</p>
              {crErr && <p className="text-sm text-red-600 mb-2">{crErr}</p>}
              {crMsg && <p className="text-sm text-green-600 mb-2">{crMsg}</p>}
              <form onSubmit={saveCreditSettings} className="space-y-4">
                <div className="border rounded-lg p-4 flex flex-wrap gap-4 items-end">
                  <label className="text-sm text-gray-600">Unlock bundle: credits
                    <input type="number" min="1" step="1" value={crSettings.unlock_bundle_credits}
                      onChange={(e) => setCrSettings((s) => ({ ...s, unlock_bundle_credits: e.target.value }))}
                      className="block w-40 border rounded-lg px-3 py-2 mt-1" />
                  </label>
                  <label className="text-sm text-gray-600">...for how many unlocks
                    <input type="number" min="1" step="1" value={crSettings.unlock_bundle_unlocks}
                      onChange={(e) => setCrSettings((s) => ({ ...s, unlock_bundle_unlocks: e.target.value }))}
                      className="block w-40 border rounded-lg px-3 py-2 mt-1" />
                  </label>
                  {crSettings.unlock_bundle_credits && crSettings.unlock_bundle_unlocks && Number(crSettings.unlock_bundle_unlocks) > 0 && (
                    <p className="text-xs text-gray-400 self-center">
                      = {Math.ceil(Number(crSettings.unlock_bundle_credits) / Number(crSettings.unlock_bundle_unlocks))} credits per single unlock
                    </p>
                  )}
                </div>

                <div className="border rounded-lg p-4 flex flex-wrap gap-4 items-end">
                  <label className="text-sm text-gray-600">Job post bundle: credits
                    <input type="number" min="1" step="1" value={crSettings.job_post_bundle_credits}
                      onChange={(e) => setCrSettings((s) => ({ ...s, job_post_bundle_credits: e.target.value }))}
                      className="block w-40 border rounded-lg px-3 py-2 mt-1" />
                  </label>
                  <label className="text-sm text-gray-600">...for how many job posts
                    <input type="number" min="1" step="1" value={crSettings.job_post_bundle_posts}
                      onChange={(e) => setCrSettings((s) => ({ ...s, job_post_bundle_posts: e.target.value }))}
                      className="block w-40 border rounded-lg px-3 py-2 mt-1" />
                  </label>
                  {crSettings.job_post_bundle_credits && crSettings.job_post_bundle_posts && Number(crSettings.job_post_bundle_posts) > 0 && (
                    <p className="text-xs text-gray-400 self-center">
                      = {Math.ceil(Number(crSettings.job_post_bundle_credits) / Number(crSettings.job_post_bundle_posts))} credits per single job post
                    </p>
                  )}
                </div>

                <div className="border rounded-lg p-4 flex flex-wrap gap-4 items-end">
                  <label className="text-sm text-gray-600">Price per credit (&#8377;)
                    <input type="number" min="0.01" step="0.01" value={crSettings.price_per_credit}
                      onChange={(e) => setCrSettings((s) => ({ ...s, price_per_credit: e.target.value }))}
                      className="block w-40 border rounded-lg px-3 py-2 mt-1" />
                  </label>
                  <p className="text-xs text-gray-400 self-center">Rate used to convert a &#8377; recharge into wallet credits.</p>
                </div>

                <button type="submit" disabled={crSaving} className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-6 py-2 rounded-lg font-semibold disabled:opacity-50">
                  {crSaving ? 'Saving...' : 'Save'}
                </button>
              </form>
            </div> */}

            {/* Plans */}
           
   <PlansAdminPanel />

      <EnterpriseCardPanel />
            {/* Hiring Packs */}
            {/* <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 p-6">
              <h3 className="font-bold text-[#0D1B3D] text-lg mb-1">Hiring Packs</h3>
              <p className="text-sm text-gray-500 mb-3">One-time prepaid packs. Employers only see a pack while it is Active.</p>
              {planErr && <p className="text-sm text-red-600 mb-2">{planErr}</p>}
              {planMsg && <p className="text-sm text-green-600 mb-2">{planMsg}</p>}
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="py-2 pr-4">Pack</th>
                      <th className="py-2 pr-4">Price (&#8377;)</th>
                      <th className="py-2 pr-4">Job Posts</th>
                      <th className="py-2 pr-4">Candidate Credits</th>
                      <th className="py-2 pr-4">Featured Jobs</th>
                      <th className="py-2 pr-4">Contact Unlocks/Job</th>
                      <th className="py-2 pr-4">Job Duration (days)</th>
                      <th className="py-2 pr-4">Pack Validity (days)</th>
                      <th className="py-2 pr-4">Featured</th>
                      <th className="py-2 pr-4">Active</th>
                      <th className="py-2 pr-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {plans.filter((p) => p.type === 'hiring').map((plan) => {
                      const edit = planEdits[plan.id] || {};
                      return (
                        <tr key={plan.id} className="border-b last:border-0">
                          <td className="py-2 pr-4 font-medium text-[#0D1B3D]">{plan.name}</td>
                          {[
                            ['price_monthly', '0.01', 'w-28'],
                            ['job_post_limit', '1', 'w-24'],
                            ['candidate_credits', '1', 'w-24'],
                            ['featured_jobs', '1', 'w-24'],
                            ['contact_unlocks_per_job', '1', 'w-24'],
                            ['per_job_duration_days', '1', 'w-24'],
                            ['pack_validity_days', '1', 'w-24'],
                          ].map(([f, step, w]) => (
                            <td key={f} className="py-2 pr-4">
                              <input type="number" min="0" step={step} value={edit[f] ?? ''}
                                onChange={(e) => updatePlanField(plan.id, f, e.target.value)}
                                className={`${w} border rounded-lg px-2 py-1`} />
                            </td>
                          ))}
                          <td className="py-2 pr-4">
                            <input type="checkbox" checked={!!edit.is_featured_pack}
                              onChange={(e) => updatePlanField(plan.id, 'is_featured_pack', e.target.checked)} />
                          </td>
                          <td className="py-2 pr-4">
                            <input type="checkbox" checked={!!edit.is_active}
                              onChange={(e) => updatePlanField(plan.id, 'is_active', e.target.checked)} />
                          </td>
                          <td className="py-2 pr-4">
                            <button type="button" onClick={() => savePlan(plan.id)} disabled={planSavingId === plan.id}
                              className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-4 py-1.5 rounded-lg font-semibold disabled:opacity-50">
                              {planSavingId === plan.id ? 'Saving...' : 'Save'}
                            </button>
                            <button type="button" onClick={() => deletePlan(plan.id)} disabled={deletingPlanId === plan.id}
                              className="ml-2 bg-red-600 text-white px-3 py-1.5 rounded-lg font-semibold disabled:opacity-50">
                              {deletingPlanId === plan.id ? 'Deleting...' : 'Delete'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {plans.filter((p) => p.type === 'hiring').length === 0 && (
                      <tr><td colSpan={12} className="py-3 text-gray-400">No hiring packs yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm font-semibold text-gray-600 mb-2">Add new hiring pack</p>
                <div className="flex flex-wrap gap-2 items-center">
                  <input type="text" placeholder="Name" value={newPack.name}
                    onChange={(e) => setNewPack((p) => ({ ...p, name: e.target.value }))}
                    className="w-32 border rounded-lg px-2 py-1 text-sm" />
                  {[
                    ['price_monthly', 'Price', 'w-24', '0.01'],
                    ['job_post_limit', 'Job posts', 'w-24', '1'],
                    ['candidate_credits', 'Candidate credits', 'w-32', '1'],
                    ['featured_jobs', 'Featured jobs', 'w-28', '1'],
                    ['contact_unlocks_per_job', 'Contact unlocks/job', 'w-32', '1'],
                    ['per_job_duration_days', 'Job duration (days)', 'w-32', '1'],
                    ['pack_validity_days', 'Pack validity (days)', 'w-32', '1'],
                  ].map(([f, ph, w, step]) => (
                    <input key={f} type="number" min="0" step={step} placeholder={ph} value={newPack[f]}
                      onChange={(e) => setNewPack((p) => ({ ...p, [f]: e.target.value }))}
                      className={`${w} border rounded-lg px-2 py-1 text-sm`} />
                  ))}
                  <label className="flex items-center gap-1 text-xs text-gray-600">
                    <input type="checkbox" checked={!!newPack.is_featured_pack}
                      onChange={(e) => setNewPack((p) => ({ ...p, is_featured_pack: e.target.checked }))} />
                    Featured
                  </label>
                  <button type="button" onClick={addPack} disabled={addingPack || !newPack.name || !newPack.price_monthly}
                    className="bg-green-600 text-white px-4 py-1.5 rounded-lg font-semibold disabled:opacity-50">
                    {addingPack ? 'Adding...' : 'Add Pack'}
                  </button>
                </div>
              </div>
            </div> */}

            {/* Pay-Per-Job Options */}
            {/* <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 p-6">
              <h3 className="font-bold text-[#0D1B3D] text-lg mb-1">Pay-Per-Job Options</h3>
              <p className="text-sm text-gray-500 mb-3">Employers who don&apos;t want a subscription buy individual job posts. Set the price, number of jobs and validity. Employers only see an option while it is Active.</p>
              {productErr && <p className="text-sm text-red-600 mb-2">{productErr}</p>}
              {productMsg && <p className="text-sm text-green-600 mb-2">{productMsg}</p>}
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="py-2 pr-4">Option</th>
                      <th className="py-2 pr-4">Price (&#8377;)</th>
                      <th className="py-2 pr-4">Job Posts</th>
                      <th className="py-2 pr-4">Validity (days)</th>
                      <th className="py-2 pr-4">Active</th>
                      <th className="py-2 pr-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobProducts.map((d) => {
                      const e = productEdits[d.id] || {};
                      return (
                        <tr key={d.id} className="border-b last:border-0">
                          <td className="py-2 pr-4">
                            <input type="text" value={e.name ?? ''}
                              onChange={(ev) => updateProductField(d.id, 'name', ev.target.value)}
                              className="w-32 border rounded-lg px-2 py-1" />
                          </td>
                          {[
                            ['price', '0.01', 'w-28'],
                            ['job_posts', '1', 'w-24'],
                            ['validity_days', '1', 'w-24'],
                          ].map(([f, step, w]) => (
                            <td key={f} className="py-2 pr-4">
                              <input type="number" min="0" step={step} value={e[f] ?? ''}
                                onChange={(ev) => updateProductField(d.id, f, ev.target.value)}
                                className={`${w} border rounded-lg px-2 py-1`} />
                            </td>
                          ))}
                          <td className="py-2 pr-4">
                            <input type="checkbox" checked={!!e.is_active}
                              onChange={(ev) => updateProductField(d.id, 'is_active', ev.target.checked)} />
                          </td>
                          <td className="py-2 pr-4">
                            <button type="button" onClick={() => saveProduct(d.id)} disabled={productBusy === d.id}
                              className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-4 py-1.5 rounded-lg font-semibold disabled:opacity-50">
                              {productBusy === d.id ? 'Saving...' : 'Save'}
                            </button>
                            <button type="button" onClick={() => deleteProduct(d.id)} disabled={productBusy === d.id}
                              className="ml-2 bg-red-600 text-white px-3 py-1.5 rounded-lg font-semibold disabled:opacity-50">
                              Delete
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {jobProducts.length === 0 && (
                      <tr><td colSpan={6} className="py-3 text-gray-400">No pay-per-job options yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm font-semibold text-gray-600 mb-2">Add new option</p>
                <div className="flex flex-wrap gap-2 items-center">
                  <input type="text" placeholder="Name (e.g. 3 Jobs)" value={newProduct.name}
                    onChange={(ev) => setNewProduct((p) => ({ ...p, name: ev.target.value }))}
                    className="w-40 border rounded-lg px-2 py-1 text-sm" />
                  <input type="number" min="0" step="0.01" placeholder="Price" value={newProduct.price}
                    onChange={(ev) => setNewProduct((p) => ({ ...p, price: ev.target.value }))}
                    className="w-24 border rounded-lg px-2 py-1 text-sm" />
                  <input type="number" min="1" step="1" placeholder="Job posts" value={newProduct.job_posts}
                    onChange={(ev) => setNewProduct((p) => ({ ...p, job_posts: ev.target.value }))}
                    className="w-24 border rounded-lg px-2 py-1 text-sm" />
                  <input type="number" min="1" step="1" placeholder="Validity (days)" value={newProduct.validity_days}
                    onChange={(ev) => setNewProduct((p) => ({ ...p, validity_days: ev.target.value }))}
                    className="w-32 border rounded-lg px-2 py-1 text-sm" />
                  <button type="button" onClick={addProduct}
                    disabled={productBusy === 'add' || !newProduct.name || !newProduct.price || !newProduct.job_posts || !newProduct.validity_days}
                    className="bg-green-600 text-white px-4 py-1.5 rounded-lg font-semibold disabled:opacity-50">
                    {productBusy === 'add' ? 'Adding...' : 'Add Option'}
                  </button>
                </div>
              </div>
            </div> */}

            {/* Candidate Credit Top-Ups */}
            <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 p-6">
              <h3 className="font-bold text-[#0D1B3D] text-lg mb-1">Candidate Credit Top-Ups</h3>
              <p className="text-sm text-gray-500 mb-3">Extra candidate-contact unlocks an employer can buy when they run out. Top-ups never expire. Set how many unlocks each pack gives — usually the same as the credit count, but you can set a bonus for promotions.</p>
              {topupErr && <p className="text-sm text-red-600 mb-2">{topupErr}</p>}
              {topupMsg && <p className="text-sm text-green-600 mb-2">{topupMsg}</p>}
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="py-2 pr-4">Credits</th>
                      <th className="py-2 pr-4">Price (&#8377;)</th>
                      <th className="py-2 pr-4">Unlocks</th>
                      <th className="py-2 pr-4">Meaning</th>
                      <th className="py-2 pr-4">Active</th>
                      <th className="py-2 pr-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {topupPacks.map((d) => {
                      const e = topupEdits[d.id] || {};
                      return (
                        <tr key={d.id} className="border-b last:border-0">
                          {[
                            ['credits', '1', 'w-24', ''],
                            ['price', '0.01', 'w-28', ''],
                            ['unlocks', '1', 'w-28', ''],
                          ].map(([f, step, w, ph]) => (
                            <td key={f} className="py-2 pr-4">
                              <input type="number" min="0" step={step} placeholder={ph} value={e[f] ?? ''}
                                onChange={(ev) => updateTopupField(d.id, f, ev.target.value)}
                                className={`${w} border rounded-lg px-2 py-1`} />
                            </td>
                          ))}
                                  
                                   <td className="py-2 pr-4 text-gray-500 whitespace-nowrap">
                           {e.unlocks || '—'} candidate contact unlocks
               </td>

                          <td className="py-2 pr-4">
                            <input type="checkbox" checked={!!e.is_active}
                              onChange={(ev) => updateTopupField(d.id, 'is_active', ev.target.checked)} />
                          </td>
                          <td className="py-2 pr-4">
                            <button type="button" onClick={() => saveTopup(d.id)} disabled={topupBusy === d.id}
                              className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-4 py-1.5 rounded-lg font-semibold disabled:opacity-50">
                              {topupBusy === d.id ? 'Saving...' : 'Save'}
                            </button>
                            <button type="button" onClick={() => deleteTopup(d.id)} disabled={topupBusy === d.id}
                              className="ml-2 bg-red-600 text-white px-3 py-1.5 rounded-lg font-semibold disabled:opacity-50">
                              Delete
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {topupPacks.length === 0 && (
                      <tr><td colSpan={6} className="py-3 text-gray-400">No top-up packs yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm font-semibold text-gray-600 mb-2">Add new top-up pack</p>
                <div className="flex flex-wrap gap-2 items-center">
                  <input type="number" min="1" step="1" placeholder="Credits" value={newTopup.credits}
                    onChange={(ev) => setNewTopup((p) => ({ ...p, credits: ev.target.value }))}
                    className="w-24 border rounded-lg px-2 py-1 text-sm" />
                  <input type="number" min="0" step="0.01" placeholder="Price" value={newTopup.price}
                    onChange={(ev) => setNewTopup((p) => ({ ...p, price: ev.target.value }))}
                    className="w-24 border rounded-lg px-2 py-1 text-sm" />
                  <input type="number" min="1" step="1" placeholder="Unlocks" value={newTopup.unlocks}
                    onChange={(ev) => setNewTopup((p) => ({ ...p, unlocks: ev.target.value }))}
                    className="w-28 border rounded-lg px-2 py-1 text-sm" />
                  <button type="button" onClick={addTopup}
                    disabled={topupBusy === 'add' || !newTopup.credits || !newTopup.price || !newTopup.unlocks}
                    className="bg-green-600 text-white px-4 py-1.5 rounded-lg font-semibold disabled:opacity-50">
                    {topupBusy === 'add' ? 'Adding...' : 'Add Top-Up'}
                  </button>
                </div>
              </div>
            </div>

            {/* Job Boost */}
            <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 p-6">
              <h3 className="font-bold text-[#0D1B3D] text-lg mb-1">Job Boost</h3>
              <p className="text-sm text-gray-500 mb-3">Separate optional product. A boosted job gets a featured badge, higher visibility, priority placement and more recommendations (display logic not wired up yet — this only manages the tiers).</p>
              {boostErr && <p className="text-sm text-red-600 mb-2">{boostErr}</p>}
              {boostMsg && <p className="text-sm text-green-600 mb-2">{boostMsg}</p>}
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="py-2 pr-4">Duration (days)</th>
                      <th className="py-2 pr-4">Price (&#8377;)</th>
                      <th className="py-2 pr-4">Active</th>
                      <th className="py-2 pr-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {boostTiers.map((d) => {
                      const e = boostEdits[d.id] || {};
                      return (
                        <tr key={d.id} className="border-b last:border-0">
                          <td className="py-2 pr-4">
                            <input type="number" min="1" step="1" value={e.duration_days ?? ''}
                              onChange={(ev) => updateBoostField(d.id, 'duration_days', ev.target.value)}
                              className="w-24 border rounded-lg px-2 py-1" />
                          </td>
                          <td className="py-2 pr-4">
                            <input type="number" min="0" step="0.01" value={e.price ?? ''}
                              onChange={(ev) => updateBoostField(d.id, 'price', ev.target.value)}
                              className="w-28 border rounded-lg px-2 py-1" />
                          </td>
                          <td className="py-2 pr-4">
                            <input type="checkbox" checked={!!e.is_active}
                              onChange={(ev) => updateBoostField(d.id, 'is_active', ev.target.checked)} />
                          </td>
                          <td className="py-2 pr-4">
                            <button type="button" onClick={() => saveBoost(d.id)} disabled={boostBusy === d.id}
                              className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-4 py-1.5 rounded-lg font-semibold disabled:opacity-50">
                              {boostBusy === d.id ? 'Saving...' : 'Save'}
                            </button>
                            <button type="button" onClick={() => deleteBoost(d.id)} disabled={boostBusy === d.id}
                              className="ml-2 bg-red-600 text-white px-3 py-1.5 rounded-lg font-semibold disabled:opacity-50">
                              Delete
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {boostTiers.length === 0 && (
                      <tr><td colSpan={4} className="py-3 text-gray-400">No boost tiers yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm font-semibold text-gray-600 mb-2">Add new boost tier</p>
                <div className="flex flex-wrap gap-2 items-center">
                  <input type="number" min="1" step="1" placeholder="Duration (days)" value={newBoost.duration_days}
                    onChange={(ev) => setNewBoost((p) => ({ ...p, duration_days: ev.target.value }))}
                    className="w-36 border rounded-lg px-2 py-1 text-sm" />
                  <input type="number" min="0" step="0.01" placeholder="Price" value={newBoost.price}
                    onChange={(ev) => setNewBoost((p) => ({ ...p, price: ev.target.value }))}
                    className="w-24 border rounded-lg px-2 py-1 text-sm" />
                  <button type="button" onClick={addBoost}
                    disabled={boostBusy === 'add' || !newBoost.duration_days || !newBoost.price}
                    className="bg-green-600 text-white px-4 py-1.5 rounded-lg font-semibold disabled:opacity-50">
                    {boostBusy === 'add' ? 'Adding...' : 'Add Boost Tier'}
                  </button>
                </div>
              </div>
            </div>

            {/* Featured Job */}
            <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 p-6">
              <h3 className="font-bold text-[#0D1B3D] text-lg mb-1">Featured Job</h3>
              <p className="text-sm text-gray-500 mb-3">Low-cost add-on. The employer pays, then picks a job to feature for the number of days set here. Employers only see an option while it is Active.</p>
              {fjErr && <p className="text-sm text-red-600 mb-2">{fjErr}</p>}
              {fjMsg && <p className="text-sm text-green-600 mb-2">{fjMsg}</p>}
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="py-2 pr-4">Name</th>
                      <th className="py-2 pr-4">Price (&#8377;)</th>
                      <th className="py-2 pr-4">Featured for (days)</th>
                      <th className="py-2 pr-4">Active</th>
                      <th className="py-2 pr-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {fjOptions.map((d) => {
                      const e = fjEdits[d.id] || {};
                      return (
                        <tr key={d.id} className="border-b last:border-0">
                          <td className="py-2 pr-4">
                            <input type="text" value={e.name ?? ''}
                              onChange={(ev) => updateFjField(d.id, 'name', ev.target.value)}
                              className="w-40 border rounded-lg px-2 py-1" />
                          </td>
                          <td className="py-2 pr-4">
                            <input type="number" min="0" step="0.01" value={e.price ?? ''}
                              onChange={(ev) => updateFjField(d.id, 'price', ev.target.value)}
                              className="w-28 border rounded-lg px-2 py-1" />
                          </td>
                          <td className="py-2 pr-4">
                            <input type="number" min="1" step="1" value={e.duration_days ?? ''}
                              onChange={(ev) => updateFjField(d.id, 'duration_days', ev.target.value)}
                              className="w-24 border rounded-lg px-2 py-1" />
                          </td>
                          <td className="py-2 pr-4">
                            <input type="checkbox" checked={!!e.is_active}
                              onChange={(ev) => updateFjField(d.id, 'is_active', ev.target.checked)} />
                          </td>
                          <td className="py-2 pr-4">
                            <button type="button" onClick={() => saveFj(d.id)} disabled={fjBusy === d.id}
                              className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-4 py-1.5 rounded-lg font-semibold disabled:opacity-50">
                              {fjBusy === d.id ? 'Saving...' : 'Save'}
                            </button>
                            <button type="button" onClick={() => deleteFj(d.id)} disabled={fjBusy === d.id}
                              className="ml-2 bg-red-600 text-white px-3 py-1.5 rounded-lg font-semibold disabled:opacity-50">
                              Delete
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {fjOptions.length === 0 && (
                      <tr><td colSpan={5} className="py-3 text-gray-400">No Featured Job options yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm font-semibold text-gray-600 mb-2">Add new option</p>
                <div className="flex flex-wrap gap-2 items-center">
                  <input type="text" placeholder="Name" value={newFj.name}
                    onChange={(ev) => setNewFj((p) => ({ ...p, name: ev.target.value }))}
                    className="w-40 border rounded-lg px-2 py-1 text-sm" />
                  <input type="number" min="0" step="0.01" placeholder="Price" value={newFj.price}
                    onChange={(ev) => setNewFj((p) => ({ ...p, price: ev.target.value }))}
                    className="w-24 border rounded-lg px-2 py-1 text-sm" />
                  <input type="number" min="1" step="1" placeholder="Days" value={newFj.duration_days}
                    onChange={(ev) => setNewFj((p) => ({ ...p, duration_days: ev.target.value }))}
                    className="w-24 border rounded-lg px-2 py-1 text-sm" />
                  <button type="button" onClick={addFj}
                    disabled={fjBusy === 'add' || !newFj.price || !newFj.duration_days}
                    className="bg-green-600 text-white px-4 py-1.5 rounded-lg font-semibold disabled:opacity-50">
                    {fjBusy === 'add' ? 'Adding...' : 'Add Option'}
                  </button>
                </div>
              </div>
            </div>

                        </>
            )}

            {masterView === 'data' && (
            <>

            {/* Category selector */}
            <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 p-4 flex gap-2 flex-wrap">
              {MASTER_TYPES.map((t) => (
                <button
                  key={t.value}
                  onClick={() => switchMasterType(t.value)}
                  className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition ${
                    masterType === t.value
                      ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Add form */}
            <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 p-6">
              <h3 className="font-bold text-[#0D1B3D] text-lg mb-3">
                Add {MASTER_TYPES.find((t) => t.value === masterType)?.label}
              </h3>
              {masterError && (
                <p className="text-sm text-red-600 mb-2">{masterError}</p>
              )}
              <form onSubmit={addMasterItem} className="flex gap-2">
                <input
                  type="text"
                  value={newMasterValue}
                  onChange={(e) => setNewMasterValue(e.target.value)}
                  placeholder={`New ${MASTER_TYPES.find((t) => t.value === masterType)?.label.toLowerCase()} value`}
                  className="flex-1 border rounded-lg px-3 py-2"
                />
                <button
                  type="submit"
                  disabled={masterSaving || !newMasterValue.trim()}
                  className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-6 py-2 rounded-lg font-semibold hover:opacity-90 disabled:opacity-50"
                >
                  {masterSaving ? 'Adding...' : 'Add'}
                </button>
              </form>
            </div>

            {/* List */}
            <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 text-[11px] tracking-wider uppercase border-b border-gray-100">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Value</th>
                    <th className="text-left px-4 py-3 font-semibold w-24">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {masterLoading ? (
                    <tr>
                      <td colSpan={2} className="px-4 py-6 text-center text-gray-400">
                        Loading...
                      </td>
                    </tr>
                  ) : masterItems.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="px-4 py-6 text-center text-gray-400">
                        No entries yet for this category.
                      </td>
                    </tr>
                  ) : (
                    masterItems.map((item) => (
                      <tr key={item.id} className="border-t border-gray-50 hover:bg-purple-50/70 transition-colors">
                        <td className="px-4 py-3">{item.value}</td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => deleteMasterItem(item.id)}
                            className="text-red-600 hover:underline text-xs font-semibold"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                           </table>
            </div>
            </>
            )}
          </div>
        )}
      </div>


      {jobsModalUser && (
  <div
    className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
    onClick={closeUserJobsModal}
  >
    <div
      className="bg-white rounded-2xl shadow-xl max-w-4xl w-full p-6 max-h-[85vh] overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-[#0D1B3D]">Job Posts</h3>
          <p className="text-xs text-gray-500">{jobsModalUser.name}</p>
        </div>
        <button onClick={closeUserJobsModal} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
      </div>

      {userJobsLoading ? (
        <p className="text-center text-gray-400 py-8">Loading...</p>
      ) : userJobsError ? (
        <p className="text-center text-red-600 py-6 text-sm">{userJobsError}</p>
      ) : userJobs.length === 0 ? (
        <p className="text-center text-gray-400 py-8 text-sm">No jobs posted.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 text-[11px] tracking-wider uppercase border-b border-gray-100">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Title</th>
                <th className="text-left px-3 py-2 font-semibold">Location</th>
                <th className="text-left px-3 py-2 font-semibold">Type</th>
                <th className="text-left px-3 py-2 font-semibold">Salary</th>
                <th className="text-left px-3 py-2 font-semibold">Openings</th>
                <th className="text-left px-3 py-2 font-semibold">Applicants</th>
                <th className="text-left px-3 py-2 font-semibold">Status</th>
                <th className="text-left px-3 py-2 font-semibold">Posted</th>
              </tr>
            </thead>
            <tbody>
              {userJobs.map((jp) => (
                <tr key={jp.id} className="border-t border-gray-50 hover:bg-purple-50/70">
                  <td className="px-3 py-2 font-medium">{jp.title}</td>
                  <td className="px-3 py-2">{jp.location || '—'}</td>
                  <td className="px-3 py-2 capitalize">{(jp.job_type || '').replace(/_/g, ' ') || '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{fmtINR(jp.salary_min)} – {fmtINR(jp.salary_max)}</td>
                  <td className="px-3 py-2">{jp.openings ?? '—'}</td>
                  <td className="px-3 py-2">{jp.applications?.length ?? jp.applications_count ?? 0}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${ADMIN_STATUS_STYLES[jp.status] || 'bg-gray-100 text-gray-500'}`}>
                      {jp.status || '—'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">{fmtDate(jp.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  </div>
)}

{appsModalUser && (
  <div
    className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
    onClick={closeUserAppsModal}
  >
    <div
      className="bg-white rounded-2xl shadow-xl max-w-4xl w-full p-6 max-h-[85vh] overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-[#0D1B3D]">Job Applications</h3>
          <p className="text-xs text-gray-500">{appsModalUser.name}</p>
        </div>
        <button onClick={closeUserAppsModal} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
      </div>

      {userAppsLoading ? (
        <p className="text-center text-gray-400 py-8">Loading...</p>
      ) : userAppsError ? (
        <p className="text-center text-red-600 py-6 text-sm">{userAppsError}</p>
      ) : userApps.length === 0 ? (
        <p className="text-center text-gray-400 py-8 text-sm">No applications found.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 text-[11px] tracking-wider uppercase border-b border-gray-100">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Job</th>
                <th className="text-left px-3 py-2 font-semibold">Company</th>
                <th className="text-left px-3 py-2 font-semibold">Location</th>
                <th className="text-left px-3 py-2 font-semibold">Salary</th>
                <th className="text-left px-3 py-2 font-semibold">Applied On</th>
                <th className="text-left px-3 py-2 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {userApps.map((app) => (
                <tr key={app.id} className="border-t border-gray-50 hover:bg-purple-50/70">
                  <td className="px-3 py-2 font-medium">{app.job_posting?.title || '—'}</td>
                  <td className="px-3 py-2">{app.job_posting?.company_name || '—'}</td>
                  <td className="px-3 py-2">{app.job_posting?.location || '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {app.job_posting ? `${fmtINR(app.job_posting.salary_min)} – ${fmtINR(app.job_posting.salary_max)}` : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">{fmtDate(app.created_at)}</td>
                  <td className="px-3 py-2">
                    <span className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold capitalize bg-purple-50 text-[#7C3AED]">
                      {(app.status || '—').replace(/_/g, ' ')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  </div>
)}

{empAppsModalUser && (
  <div
    className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
    onClick={closeEmployerAppsModal}
  >
    <div
      className="bg-white rounded-2xl shadow-xl max-w-5xl w-full p-6 max-h-[85vh] overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-[#0D1B3D]">Applications Received</h3>
          <p className="text-xs text-gray-500">{empAppsModalUser.name} · {empApps.length} application(s)</p>
        </div>
        <button onClick={closeEmployerAppsModal} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
      </div>

      {empAppsLoading ? (
        <p className="text-center text-gray-400 py-8">Loading...</p>
      ) : empAppsError ? (
        <p className="text-center text-red-600 py-6 text-sm">{empAppsError}</p>
      ) : empApps.length === 0 ? (
        <p className="text-center text-gray-400 py-8 text-sm">No applications received.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]">
            <thead className="bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 text-[11px] tracking-wider uppercase border-b border-gray-100">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Candidate</th>
                <th className="text-left px-3 py-2 font-semibold">Email</th>
                <th className="text-left px-3 py-2 font-semibold">Phone</th>
                <th className="text-left px-3 py-2 font-semibold">Job</th>
                <th className="text-left px-3 py-2 font-semibold">Applied On</th>
                <th className="text-left px-3 py-2 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {empApps.map((a) => (
                <tr key={a.id} className="border-t border-gray-50 hover:bg-purple-50/70">
                  <td className="px-3 py-2 font-medium">
                    {a.user?.id ? (
                      <button type="button" onClick={() => viewUserDetails(a.user.id)} className="text-left text-[#7C3AED] hover:underline">
                        {a.user.name || '—'}
                      </button>
                    ) : '—'}
                  </td>
                  <td className="px-3 py-2">{a.user?.email || '—'}</td>
                  <td className="px-3 py-2">{a.user?.phone || '—'}</td>
                  <td className="px-3 py-2">{a.job_title || '—'}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{fmtDate(a.created_at)}</td>
                  <td className="px-3 py-2">
                    <span className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold capitalize bg-purple-50 text-[#7C3AED]">
                      {(a.status || '—').replace(/_/g, ' ')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  </div>
)}

      {/* User Detail Modal */}
      {(selectedUser || detailLoading) && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={closeUserDetails}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeUserDetails}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-xl font-bold"
            >
              ×
            </button>

            {detailLoading && !selectedUser && (
              <p className="text-center text-gray-500 py-10">Loading details...</p>
            )}

            {selectedUser && (
              <div className="space-y-6">
                {/* Basic Info */}
                <div className="flex items-center gap-4">
                  {selectedUser.profile_photo_path && (
                    <img
                      src={`${BACKEND_URL}/storage/${selectedUser.profile_photo_path}`}
                      alt={selectedUser.name}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                  )}
                  <div>
                    <h3 className="text-2xl font-bold text-[#7C3AED]">{selectedUser.name}</h3>
                    <p className="text-gray-500">{selectedUser.email}</p>
                    <span className="inline-block mt-1 text-xs font-semibold bg-[#EFF6FF] text-[#7C3AED] px-2 py-1 rounded capitalize">
                      {selectedUser.role?.replace('_', ' ')}
                    </span>
                  </div>
                </div>

                {/* Personal details */}
                <div>
                  <h4 className="font-bold text-[#0D1B3D] mb-2">Personal Details</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
                    <p><span className="font-medium">Phone:</span> {selectedUser.phone || '—'}</p>
                    {selectedUser.alternate_email && <p><span className="font-medium">Alternate Email:</span> {selectedUser.alternate_email}</p>}
{selectedUser.alternate_phone && <p><span className="font-medium">Alternate Phone:</span> {selectedUser.alternate_phone}</p>}
{selectedUser.merged_at && <p><span className="font-medium">Merged On:</span> {fmtDate(selectedUser.merged_at)}</p>}
                    <p><span className="font-medium">DOB:</span> {selectedUser.date_of_birth || '—'}</p>
                    <p><span className="font-medium">Gender:</span> {selectedUser.gender || '—'}</p>
                    <p><span className="font-medium">City:</span> {selectedUser.city || '—'}</p>
                    <p><span className="font-medium">State:</span> {selectedUser.state || '—'}</p>
                    <p><span className="font-medium">Pincode:</span> {selectedUser.pincode || '—'}</p>
                    <p className="col-span-2"><span className="font-medium">Address:</span> {selectedUser.address || '—'}</p>
                    <p><span className="font-medium">Aadhaar:</span> {selectedUser.aadhaar_number || '—'} ({selectedUser.aadhaar_status || 'not submitted'})</p>
                  </div>
                  {selectedUser.career_objective && (
                    <p className="text-sm text-gray-700 mt-2">
                      <span className="font-medium">Career Objective:</span> {selectedUser.career_objective}
                    </p>
                  )}
                  {selectedUser.resume_path && (
                    <a
                      href={`${BACKEND_URL}/storage/${selectedUser.resume_path}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-block mt-2 text-[#7C3AED] hover:underline text-sm font-medium"
                    >
                      View Resume
                    </a>
                  )}
                </div>

                {/* Company details (employer only) */}
                {selectedUser.role === 'employer' && (
                  <div>
                    <h4 className="font-bold text-[#0D1B3D] mb-2">Company Details</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
                      <p><span className="font-medium">Website:</span> {selectedUser.company_website || '—'}</p>
                      <p><span className="font-medium">GST:</span> {selectedUser.company_gst || '—'}</p>
                      <p><span className="font-medium">Size:</span> {selectedUser.company_size || '—'}</p>
                      <p><span className="font-medium">Industry:</span> {selectedUser.company_industry || '—'}</p>
                      <p><span className="font-medium">Verified Status:</span> {selectedUser.company_verified_status || 'unverified'}</p>
                    </div>
                    {selectedUser.company_about && (
                      <p className="text-sm text-gray-700 mt-2">
                        <span className="font-medium">About:</span> {selectedUser.company_about}
                      </p>
                    )}
                  </div>
                )}

                {/* Skills / Education / Experience / Certifications / Languages */}
                {selectedUser.skills?.length > 0 && (
                  <div>
                    <h4 className="font-bold text-[#0D1B3D] mb-2">Skills</h4>
                    <div className="flex flex-wrap gap-2">
                    {selectedUser.skills.map((s) => {
  const skillName = s.name ?? s.skill_name ?? s.skill ?? s.title ?? '';
  const level = s.proficiency ?? s.level;
  return (
    <span key={s.id} className="bg-[#EFF6FF] text-[#7C3AED] text-xs font-medium px-2 py-1 rounded">
      {skillName}{level ? ` (${level})` : ''}
    </span>
  );
})}
                    </div>
                  </div>
                )}

                {selectedUser.educations?.length > 0 && (
                  <div>
                    <h4 className="font-bold text-[#0D1B3D] mb-2">Education</h4>
                    <ul className="text-sm text-gray-700 space-y-1">
                      {selectedUser.educations.map((e) => (
                        <li key={e.id}>
                          {e.degree} — {e.institution} {e.year_of_passing ? `(${e.year_of_passing})` : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {selectedUser.experiences?.length > 0 && (
                  <div>
                    <h4 className="font-bold text-[#0D1B3D] mb-2">Experience</h4>
                    <ul className="text-sm text-gray-700 space-y-1">
                      {selectedUser.experiences.map((exp) => (
                        <li key={exp.id}>
                          {exp.job_title} — {exp.company_name}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Applications (job seeker) */}
                {selectedUser.job_applications?.length > 0 && (
                  <div>
                    <h4 className="font-bold text-[#0D1B3D] mb-2">Job Applications</h4>
                    <ul className="text-sm text-gray-700 space-y-2">
                      {selectedUser.job_applications.map((app) => (
                        <li key={app.id} className="border-b pb-1">
                          <span className="font-medium">{app.job_posting?.title}</span> at{' '}
                          {app.job_posting?.company_name} — status:{' '}
                          <span className="capitalize">{app.status}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Job postings (employer) */}
                {selectedUser.job_postings?.length > 0 && (
                  <div>
                    <h4 className="font-bold text-[#0D1B3D] mb-2">Job Postings</h4>
                    <ul className="text-sm text-gray-700 space-y-3">
                      {selectedUser.job_postings.map((jp) => (
                        <li key={jp.id} className="border-b pb-2">
                          <p className="font-medium">
                            {jp.title} <span className="capitalize text-gray-500">({jp.status})</span>
                          </p>
                          <p className="text-xs text-gray-500">
                            {jp.applications?.length || 0} applicant(s)
                          </p>
                          {jp.applications?.length > 0 && (
                            <ul className="ml-4 mt-1 list-disc text-xs text-gray-600">
                              {jp.applications.map((a) => (
                                <li key={a.id}>
                                  {a.user?.name} ({a.user?.email}) — {a.status}
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

            {logsModalSource && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={closeLogsModal}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[85vh] overflow-y-auto p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeLogsModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-xl font-bold"
            >
              ×
            </button>

            <h3 className="text-xl font-bold text-[#7C3AED] mb-1">Run History</h3>
            <p className="text-sm text-gray-500 mb-4">{logsModalSource.name}</p>

            {logsLoading ? (
              <p className="text-center text-gray-400 py-10">Loading...</p>
            ) : logsError ? (
              <p className="text-center text-red-600 py-6">{logsError}</p>
            ) : sourceLogs.length === 0 ? (
              <p className="text-center text-gray-400 py-10">No runs recorded yet for this source.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 text-[11px] tracking-wider uppercase border-b border-gray-100">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">Started</th>
                      <th className="text-left px-3 py-2 font-semibold">Found</th>
                      <th className="text-left px-3 py-2 font-semibold">New</th>
                      <th className="text-left px-3 py-2 font-semibold">Updated</th>
                      <th className="text-left px-3 py-2 font-semibold">Duplicates</th>
                      <th className="text-left px-3 py-2 font-semibold">Errors</th>
                      <th className="text-left px-3 py-2 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sourceLogs.map((log) => (
                      <tr key={log.id} className="border-t border-gray-50 hover:bg-purple-50/70 transition-colors align-top">
                        <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                          {log.started_at ? new Date(log.started_at).toLocaleString() : '—'}
                        </td>
                        <td className="px-3 py-2">{log.jobs_found}</td>
                        <td className="px-3 py-2">{log.jobs_created}</td>
                        <td className="px-3 py-2">{log.jobs_updated}</td>
                        <td className="px-3 py-2">{log.duplicates_found}</td>
                        <td className="px-3 py-2">{log.errors_count}</td>
                        <td className="px-3 py-2">
                          {log.status === 'successful' ? (
                            <span className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold bg-[#ECFDF5] text-[#10B981]">
                              ✓ Successful
                            </span>
                          ) : (
                            <div>
                              <span className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold bg-[#FEF2F2] text-[#EF4444]">
                                ✗ Failed
                              </span>
                              {log.error_message && (
                                <p className="text-xs text-red-500 mt-1 max-w-xs">{log.error_message}</p>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

{passwordAgent && (
  <div
    className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
    onClick={closePasswordModal}
  >
    <form
      onSubmit={submitAgentPassword}
      className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-[#0D1B3D]">Change Password</h3>
          <p className="text-xs text-gray-500">{passwordAgent.name} · {passwordAgent.email}</p>
        </div>
        <button type="button" onClick={closePasswordModal} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
      </div>

            <div className="mb-4 bg-purple-50 border border-purple-100 rounded-xl p-3">
        <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Current password</p>
        {currentAgentPassword === null ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : currentAgentPassword ? (
          <div className="grid grid-rows-2 grid-flow-col auto-cols-fr gap-2 w-max">
            <code className="flex-1 bg-white border border-purple-100 rounded-lg px-3 py-2 text-sm font-mono break-all">
              {currentAgentPassword}
            </code>
            <button type="button" onClick={() => navigator.clipboard.writeText(currentAgentPassword)} className={PILL_GREEN}>
              📋 Copy
            </button>
          </div>
        ) : (
          <p className="text-sm text-gray-400">Not saved yet. Set a new password below to store it.</p>
        )}
      </div>

      {passwordSavedValue && (
        <div className="mb-4 bg-green-50 border border-green-100 rounded-xl p-3">
          <p className="text-sm font-semibold text-green-700 mb-2">Password updated. Share this with the agent:</p>
          <div className="grid grid-rows-2 grid-flow-col auto-cols-fr gap-2 w-max">
            <code className="flex-1 bg-white border border-green-100 rounded-lg px-3 py-2 text-sm font-mono break-all">{passwordSavedValue}</code>
            <button type="button" onClick={() => navigator.clipboard.writeText(passwordSavedValue)} className={PILL_GREEN}>📋 Copy</button>
          </div>
        </div>
      )}

      {passwordError && <p className="text-sm text-red-600 mb-3">{passwordError}</p>}

      <div className="space-y-3">
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder="New password (min 8 chars)"
            value={passwordForm.password}
            onChange={(e) => setPasswordForm((f) => ({ ...f, password: e.target.value }))}
            required
            minLength={8}
            autoFocus
            className="w-full border border-gray-200 rounded-xl pl-3 pr-10 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
          />
          <button
            type="button"
            onClick={() => setShowPassword((s) => !s)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            title={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? '🙈' : '👁'}
          </button>
        </div>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder="Confirm new password"
            value={passwordForm.password_confirmation}
            onChange={(e) => setPasswordForm((f) => ({ ...f, password_confirmation: e.target.value }))}
            required
            minLength={8}
            className="w-full border border-gray-200 rounded-xl pl-3 pr-10 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
          />
          <button
            type="button"
            onClick={() => setShowPassword((s) => !s)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            title={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? '🙈' : '👁'}
          </button>
        </div>

        
      </div>

      <p className="text-xs text-gray-400 mt-3">The agent will be logged out everywhere and must sign in with the new password.</p>

      <div className="flex justify-end gap-3 mt-5">
        <button type="button" onClick={closePasswordModal} className="text-sm text-gray-500 hover:text-gray-700">
          Cancel
        </button>
        <button
          type="submit"
          disabled={passwordSaving}
          className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:opacity-90 disabled:opacity-50"
        >
          {passwordSaving ? 'Saving…' : 'Update password'}
        </button>
      </div>
    </form>
  </div>
)}

      {mergeOpen && canMerge && (
        <MergeEmployersModal
          employers={selectedUsersArr}
          loading={mergeLoading}
          onClose={() => setMergeOpen(false)}
          onConfirm={handleMerge}
        />
      )}

      {/* <Footer /> */}
    </div>
  );
}

export default AdminPanel;
